import {
    AlertCircle, ArrowRight, Box, Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ClipboardCheck,
    Loader, MapPin, Package, Printer, RefreshCcw,
    Search, Send, Trash2, Truck, Warehouse, X,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PDFDocument } from "pdf-lib";
import { jsPDF } from "jspdf";
import JsBarcode from "jsbarcode";
import {
    getEligibleWfsItems, getPackProfile, savePackProfile, getReturnAddress, saveReturnAddress,
    createShipmentDraft, updateShipmentReturnAddress, fetchShipmentPreview, selectShipmentPreview, createShipmentOnWalmart,
    addDraftItems,
    listWfsShipments, getWfsShipment, refreshWfsShipmentStatus, getWfsShipmentErrors, cancelWfsShipment,
    fetchReceivingLabelBlob, updateSplitTracking,
    createCarrierQuote, confirmCarrierQuote, fetchCarrierLabelBlob,
    getPrepReport, getWalmartInventoryBasic, removeShipmentItem, updateWizardStep,
    updateItemPackDetails, removeShipmentFromList,
    enrichShipmentItemImage,
    getBoxConfig, saveBoxConfig,
    createCustomSplitShipment, previewCustomSplit,
    getSelfMirroringDestinations,
    resolveWalmartItemId,
} from "../services/wfsShipmentService";
import { listBatches, getItemsFeesBatch, mergeItemFeeKey, updateCatalogItemMeta, getItemsMetadata, getItemDetails } from "../services/catalogService";
import ReplensModal from "../components/common/ReplensModal";
import FeeUpdateConfirmModal from "../components/common/FeeUpdateConfirmModal";
import useLiveShipmentSync from "../hooks/useLiveShipmentSync";
import { estimateWalmartProfit } from "../utils/walmartFees";

const CUSTOM_SPLIT_UNLOCKED_KEY = "wfs_custom_split_unlocked";

// Number inputs let the mouse scroll wheel silently increment/decrement
// the value when the cursor happens to be hovering over them while the
// page scrolls — very easy to trigger by accident and easy to not notice,
// especially on quantity/count fields. Blurring the input on wheel stops
// the browser from applying the scroll to it at all; the page underneath
// still scrolls normally. Applied to every quantity/count/dimension input
// across the shipment flow (Pack Details, Build Your Own Boxes, Box
// Configuration, packing box dims, label count).
const preventWheelChange = (e) => e.target.blur();

// Walks a list of catalog items missing an image_url and tries to enrich
// each one via getItemDetails (same lookup Catalog's own image backfill
// uses — gtin/upc catalog search, falling back to scraping the item's own
// Walmart page by wpid). Updates `setItems` progressively, one item at a
// time as each result lands, rather than waiting for the whole batch —
// and is entirely best-effort: a failed/empty lookup just leaves that row
// as-is, never blocks or errors the picker itself.
// Same GTIN/UPC can appear under more than one catalog_item_id when a SKU
// changes but the item ID stays the same. Keep only ONE row per product
// code. Priority: (1) a PUBLISHED entry always wins over an unpublished one,
// regardless of order — a stale/retired SKU shouldn't shadow the live one;
// (2) among ties (both published, or both unpublished), keep whichever
// occurs LAST in the list, since the catalog returns items in an order
// where later = newer/current SKU. Items with no code at all (shouldn't
// normally happen for WFS-eligible items) fall back to being keyed by their
// own catalog_item_id, so they're never accidentally merged with each other.
const dedupeByProductId = (items) => {
    const map = new Map();
    for (const it of items || []) {
        const key = it.gtin || it.upc || `_id_${it.catalog_item_id}`;
        const existing = map.get(key);
        if (!existing) {
            map.set(key, it);
            continue;
        }
        const existingPublished = existing.published_status === "PUBLISHED";
        const currentPublished = it.published_status === "PUBLISHED";
        if (currentPublished || !existingPublished) {
            // current wins if it's published (regardless of existing), OR
            // if existing isn't published either (tie — take the later one)
            map.set(key, it);
        }
        // else: existing is published and current isn't — keep existing
    }
    return [...map.values()];
};

const enrichMissingImages = async (items, setItems) => {
    const missing = (items || []).filter((it) => it.catalog_item_id && !it.image_url);
    for (const it of missing) {
        try {
            const result = await getItemDetails(it.catalog_item_id);
            if (result?.image_url) {
                setItems((prev) => (prev || []).map((row) =>
                    row.catalog_item_id === it.catalog_item_id
                        ? { ...row, image_url: result.image_url, brand: result.brand || row.brand }
                        : row
                ));
            }
        } catch { /* best-effort — skip and keep going */ }
        await new Promise((r) => setTimeout(r, 250)); // same gentle stagger used elsewhere in this file
    }
};

const openBlobInNewTab = (blob) => {
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 30000);
};

// Same date reformat LabelPrinter.jsx uses — YYYY-MM-DD -> MM-DD-YYYY for
// display on the label; anything else is passed through unchanged.
const formatExpiry = (val) => {
    if (!val) return "";
    const match = val.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) return `${match[2]}-${match[3]}-${match[1]}`;
    return val;
};

const generateBarcodeDataUrl = (value) => {
    try {
        const canvas = document.createElement("canvas");
        const cleaned = String(value).replace(/\D/g, "");
        let format = "CODE128";
        if (cleaned.length === 12) format = "UPC";
        else if (cleaned.length === 13) format = "EAN13";
        JsBarcode(canvas, cleaned, {
            format, displayValue: false, margin: 2, width: 4, height: 100,
            background: "#ffffff", lineColor: "#000000", flat: true,
        });
        return canvas.toDataURL("image/png");
    } catch {
        return null;
    }
};

const LABEL_LW = 57.15, LABEL_LH = 31.75; // mm, standard 2.25"x1.25" thermal label

// Draws `qty` copies of one label into an EXISTING jsPDF doc. `isFirstEver`
// (true only for the very first label of the very first row) prevents an
// extra blank leading page — every other copy, across every row, starts a
// new page first.
const drawLabelPages = (doc, row, qty, expirationDate, isFirstEver) => {
    const gtin = row.product_id;
    if (!gtin) return false;
    const printQty = Math.max(1, parseInt(qty, 10) || 1);
    const barcodeDataUrl = generateBarcodeDataUrl(gtin);
    const pad = 2, inner = LABEL_LW - pad * 2;

    for (let i = 0; i < printQty; i++) {
        if (!(isFirstEver && i === 0)) doc.addPage([LABEL_LH, LABEL_LW], "landscape");
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 0, LABEL_LW, LABEL_LH, "F");
        const barcodeH = LABEL_LH * 0.45, barcodeY = pad;
        if (barcodeDataUrl) doc.addImage(barcodeDataUrl, "PNG", 3, barcodeY, LABEL_LW - 6, barcodeH);
        const gtinY = barcodeY + barcodeH + 2.5;
        doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(0, 0, 0);
        doc.text(gtin, LABEL_LW / 2, gtinY, { align: "center" });
        const titleY = gtinY + 3.5;
        doc.setFont("helvetica", "normal"); doc.setFontSize(6); doc.setTextColor(30, 30, 30);
        const titleLines = doc.splitTextToSize(row.title || "", inner);
        doc.text(titleLines.slice(0, 2), LABEL_LW / 2, titleY, { align: "center" });
        if (expirationDate) {
            doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(0, 0, 0);
            doc.text(`EXPIRY DATE ${formatExpiry(expirationDate)}`, LABEL_LW / 2, LABEL_LH - pad - 1, { align: "center" });
        }
    }
    return true;
};

// Same thermal label size/layout as BatchesPage's quickPrintLabel, adapted
// to this step's row shape (product_id instead of a payload-nested GTIN)
// and printed `qty` times — qty comes from whatever the seller just set in
// the Packing column for that row, not a fixed default.
// Matches LabelPrinter.jsx's drawThermalLabel exactly (same insets, font
// sizes, and bottom-anchored expiration line) so a label printed from here
// looks identical to one printed from the standalone Label Printer or from
// Batches' quickPrintLabel.
const printPackRowLabel = (row, qty, expirationDate) => {
    if (!row.product_id) {
        alert("No barcode available for this item.");
        return;
    }
    const doc = new jsPDF({ unit: "mm", format: [LABEL_LH, LABEL_LW], orientation: "landscape" });
    drawLabelPages(doc, row, qty, expirationDate, true);
    doc.save(`label-${row.product_id}.pdf`);
};

// Bulk print — merges every row's labels into ONE PDF. `rows` is an array
// of { row, qty, expirationDate }. Rows with no product_id are silently
// skipped (same "no barcode" case as the single-print button, just without
// interrupting the whole batch with an alert per missing item).
const printAllLabels = (rows) => {
    const withBarcode = rows.filter((r) => r.row.product_id);
    if (withBarcode.length === 0) {
        alert("No items with a barcode to print.");
        return;
    }
    const doc = new jsPDF({ unit: "mm", format: [LABEL_LH, LABEL_LW], orientation: "landscape" });
    withBarcode.forEach(({ row, qty, expirationDate }, idx) => {
        drawLabelPages(doc, row, qty, expirationDate, idx === 0);
    });
    doc.save(`labels-all-${Date.now()}.pdf`);
};

// Merges several already-fetched PDF blobs into ONE PDF, in the order
// given — used so bulk label printing opens a single tab/file instead of
// one browser tab per label (which popup blockers kill anyway).
const mergePdfBlobs = async (blobs) => {
    const validBlobs = blobs.filter(Boolean);
    if (validBlobs.length === 0) return null;
    const merged = await PDFDocument.create();
    for (const blob of validBlobs) {
        const bytes = await blob.arrayBuffer();
        const doc = await PDFDocument.load(bytes);
        const pages = await merged.copyPages(doc, doc.getPageIndices());
        pages.forEach((p) => merged.addPage(p));
    }
    const mergedBytes = await merged.save();
    return new Blob([mergedBytes], { type: "application/pdf" });
};

// Builds "123 Main St, Springfield, PA 19064" from whatever fields Walmart's
// ship_to_address actually includes, skipping any that are missing —
// avoids the copy-pasted "just city/state" truncation this file kept
// repeating across every card that shows a destination address.
const formatShipToAddress = (addr) => {
    if (!addr) return "";
    const parts = [addr.addressLine1, addr.city, addr.stateCode, addr.postalCode].filter(Boolean);
    return parts.join(", ").replace(", " + addr.postalCode, " " + addr.postalCode); // "PA, 19064" -> "PA 19064"
};

// Generic tooltip wrapper — unlike HoverTitle (which renders its own text),
// this wraps ANY existing element (a link, a paragraph, etc.) and shows the
// black tooltip on hover without changing what's rendered inside.
const TitleHoverWrapper = ({ text, children }) => {
    const [pos, setPos] = useState(null);
    const anchorRef = useRef(null);
    if (!text) return children;

    const handleEnter = () => {
        const rect = anchorRef.current?.getBoundingClientRect();
        if (!rect) return;
        setPos({ top: rect.top - 6, left: Math.min(rect.left + 50, window.innerWidth - 320) });
    };

    return (
        <span ref={anchorRef} className="block" onMouseEnter={handleEnter} onMouseLeave={() => setPos(null)}>
            {children}
            {pos && (
                <span
                    className="fixed z-50 bg-gray-900 text-white text-xs rounded-lg shadow-2xl px-2.5 py-1.5 pointer-events-none"
                    style={{ top: pos.top, left: pos.left, width: "max-content", maxWidth: 300, whiteSpace: "normal", wordBreak: "break-word", transform: "translateY(-100%)" }}
                >
                    {text}
                </span>
            )}
        </span>
    );
};

// SKU shown truncated in the Item column — hover shows the full value in a
// tooltip (same as title), click copies it to the clipboard (same feedback
// pattern as CopyableCode, just also truncated + tooltipped).
const CopyableSku = ({ value }) => {
    const [copied, setCopied] = useState(false);
    const [pos, setPos] = useState(null);
    const anchorRef = useRef(null);
    if (!value) return null;

    const handleEnter = () => {
        const rect = anchorRef.current?.getBoundingClientRect();
        if (!rect) return;
        setPos({ top: rect.top - 6, left: Math.min(rect.left + 50, window.innerWidth - 320) });
    };

    return (
        <span
            ref={anchorRef}
            onMouseEnter={handleEnter}
            onMouseLeave={() => setPos(null)}
            onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(value);
                setCopied(true);
                setTimeout(() => setCopied(false), 1200);
            }}
            className="text-gray-400 hover:text-teal-600 truncate max-w-[152px] block cursor-pointer"
        >
            {copied ? "Copied!" : value}
            {pos && (
                <span
                    className="fixed z-50 bg-gray-900 text-white text-xs rounded-lg shadow-2xl px-2.5 py-1.5 pointer-events-none"
                    style={{ top: pos.top, left: pos.left, width: "max-content", maxWidth: 300, whiteSpace: "normal", wordBreak: "break-word", transform: "translateY(-100%)" }}
                >
                    {value}
                </span>
            )}
        </span>
    );
};

const CopyableCode = ({ value }) => {
    const [copied, setCopied] = useState(false);
    if (!value) return null;
    return (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(value);
                setCopied(true);
                setTimeout(() => setCopied(false), 1200);
            }}
            className="text-gray-400 hover:text-teal-600 font-mono inline-flex items-center gap-1"
            title="Copy"
        >
            {value}{copied ? " Copied!" : ""}
        </button>
    );
};

// Small inline editor next to the print-label button — shows the current
// expiration date (or "+ Add" if none), click to reveal a date input,
// saves via updateCatalogItemMeta (same endpoint the Catalog page uses for
// this field) and reports the new value back up so the print button uses
// it immediately without needing a full row reload.
const EXPIRATION_DATES_KEY = "wfs_expiration_dates_by_sku";

const loadExpirationDatesMap = () => {
    try {
        const raw = localStorage.getItem(EXPIRATION_DATES_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

const saveExpirationDateForSku = (sku, date) => {
    try {
        const map = loadExpirationDatesMap();
        if (date) map[sku] = date; else delete map[sku];
        localStorage.setItem(EXPIRATION_DATES_KEY, JSON.stringify(map));
    } catch { /* non-fatal */ }
};

const getSavedExpirationDate = (sku) => loadExpirationDatesMap()[sku] || null;

// Qty typed at pick-time (SourceStep or Add More Items) — sessionStorage so
// it survives a refresh mid-session but doesn't leak into future sessions.
// Shared key across both pickers so the same number shows up in either.
const NEW_SHIPMENT_QTY_KEY = "wfs_new_shipment_selected_qty";

const loadSelectedQtyMap = () => {
    try {
        const raw = sessionStorage.getItem(NEW_SHIPMENT_QTY_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

const saveSelectedQtyMap = (map) => {
    try {
        sessionStorage.setItem(NEW_SHIPMENT_QTY_KEY, JSON.stringify(map));
    } catch { /* non-fatal */ }
};

const ExpirationDateEditor = ({ catalogItemId, sku, value, onSaved }) => {
    const [editing, setEditing] = useState(false);
    // Falls back to localStorage directly for DISPLAY too, not just when
    // rows are first built — so even a stale `value` prop still shows the
    // real saved date.
    const displayValue = value || getSavedExpirationDate(sku) || "";

    // Fires the instant a date is picked — a native <input type="date">'s
    // onChange only fires once a full date is actually selected/committed,
    // so this needs no separate "Save" click and can't be skipped by
    // accident before printing or navigating away.
    const handleChange = (e) => {
        const newDate = e.target.value;
        saveExpirationDateForSku(sku, newDate || null);
        onSaved(newDate || null);
    };

    if (!sku) return null; // nothing to key the saved date by

    if (editing) {
        return (
            <div className="flex items-center gap-1">
                <input
                    type="date" autoFocus value={displayValue}
                    onChange={handleChange}
                    onBlur={() => setEditing(false)}
                    className="border border-gray-200 rounded px-1 py-0.5 text-[10px] focus:outline-none focus:border-teal-500"
                />
                <button onClick={() => setEditing(false)} className="text-[10px] text-gray-400">✕</button>
            </div>
        );
    }

    return (
        <button
            type="button"
            onClick={() => setEditing(true)}
            className={displayValue
                ? "text-[10px] font-bold text-red-500 hover:text-red-600 underline decoration-dotted whitespace-nowrap"
                : "text-[10px] text-gray-400 hover:text-teal-600 underline decoration-dotted whitespace-nowrap"}
        >
            {displayValue ? `EXP: ${displayValue}` : "+ Add expiration"}
        </button>
    );
};

const ProductImage = ({ src, fallback = "📦" }) => {
    const [errored, setErrored] = useState(false);
    const [hoverPos, setHoverPos] = useState(null); // {top, left} in viewport coords, or null when not hovering
    const imgRef = useRef(null);

    if (!src || errored) return <span className="text-2xl">{fallback}</span>;

    const handleEnter = () => {
        const rect = imgRef.current?.getBoundingClientRect();
        if (!rect) return;
        const previewSize = 200;
        const spaceOnRight = window.innerWidth - rect.right;
        const left = spaceOnRight > previewSize + 16 ? rect.right + 8 : rect.left - previewSize - 8;
        const top = Math.min(Math.max(rect.top + rect.height / 2 - previewSize / 2, 8), window.innerHeight - previewSize - 8);
        setHoverPos({ top, left });
    };

    return (
        <>
            <img ref={imgRef} src={src} alt="" onError={() => setErrored(true)}
                onMouseEnter={handleEnter} onMouseLeave={() => setHoverPos(null)}
                className="w-10 h-10 object-contain rounded cursor-zoom-in" />
            {hoverPos && (
                // position: fixed escapes any table's overflow-y-auto/hidden
                // clipping, same pattern used in BatchesPage.
                <div className="fixed z-50 bg-white border border-gray-200 rounded-xl shadow-2xl p-2 pointer-events-none"
                    style={{ top: hoverPos.top, left: hoverPos.left }}>
                    <img src={src} alt="" className="rounded-lg object-contain" style={{ width: 200, height: 200 }} />
                </div>
            )}
        </>
    );
};

// Hover popover showing the per-unit fee breakdown behind a row's Profit/ROI
// figure — same fixed-position-via-getBoundingClientRect technique as
// ProductImage's zoom preview above, so it isn't clipped by the table's
// own overflow-x-auto scrolling container.
// Small black tooltip showing the FULL title on hover — used wherever a
// title is truncated in the row. Same fixed-position technique as
// ProductImage's zoom preview so it isn't clipped by a scrolling container.
const HoverTitle = ({ title, className }) => {
    const [pos, setPos] = useState(null);
    const anchorRef = useRef(null);

    if (!title) return null;

    const handleEnter = () => {
        const rect = anchorRef.current?.getBoundingClientRect();
        if (!rect) return;
        setPos({ top: rect.top - 6, left: Math.min(rect.left + 110, window.innerWidth - 320) });
    };

    return (
        <span ref={anchorRef} className={className} onMouseEnter={handleEnter} onMouseLeave={() => setPos(null)}>
            {title}
            {pos && (
                <span
                    className="fixed z-50 bg-gray-900 text-white text-xs rounded-lg shadow-2xl px-2.5 py-1.5 pointer-events-none"
                    style={{ top: pos.top, left: pos.left, width: "max-content", maxWidth: 300, whiteSpace: "normal", wordBreak: "break-word", transform: "translateY(-100%)" }}
                >
                    {title}
                </span>
            )}
        </span>
    );
};

const ProfitFeeHover = ({ est }) => {
    const [pos, setPos] = useState(null);
    const anchorRef = useRef(null);
    const popoverWidth = 208;

    if (!est) return <span className="text-gray-300">—</span>;

    const handleEnter = () => {
        const rect = anchorRef.current?.getBoundingClientRect();
        if (!rect) return;
        const left = Math.min(Math.max(rect.left, 8), window.innerWidth - popoverWidth - 8);
        const top = Math.max(rect.top - 8, 8);
        setPos({ top, left });
    };

    const roi = est.totalCostBasis > 0 ? (est.profit / est.totalCostBasis) * 100 : null;

    return (
        <span
            ref={anchorRef}
            className="cursor-help whitespace-nowrap"
            onMouseEnter={handleEnter}
            onMouseLeave={() => setPos(null)}
        >
            <span className={`font-bold ${est.profit >= 0 ? "text-green-600" : "text-red-600"}`}>
                ${est.profit.toFixed(2)}
            </span>
            {roi != null && (
                <span className={`ml-1 ${est.profit >= 0 ? "text-purple-600" : "text-red-500"}`}>
                    ({roi.toFixed(1)}%)
                </span>
            )}
            {pos && (
                <div
                    className="fixed z-50 bg-white border border-gray-200 rounded-xl shadow-2xl p-3 text-left normal-case pointer-events-none"
                    style={{ top: pos.top, left: pos.left, width: popoverWidth, transform: "translateY(-100%)" }}
                >
                    <p className="text-[10px] font-bold text-gray-500 uppercase mb-1.5">Fee Breakdown (per unit)</p>
                    <div className="space-y-1 text-xs">
                        <div className="flex justify-between"><span className="text-gray-500">Referral Fee</span><span className="font-semibold text-gray-700">${(est.referralFee || 0).toFixed(2)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">WFS Fee</span><span className="font-semibold text-gray-700">${(est.wfsFee || 0).toFixed(2)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Inbound Shipping</span><span className="font-semibold text-gray-700">${(est.inboundShipping || 0).toFixed(2)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Sales Tax (on cost)</span><span className="font-semibold text-gray-700">${(est.salesTax || 0).toFixed(2)}</span></div>
                        <div className="flex justify-between pt-1 border-t border-gray-100"><span className="text-gray-600 font-bold">Total Fees</span><span className="font-bold text-red-600">${(est.totalFees || 0).toFixed(2)}</span></div>
                        <div className="flex justify-between pt-1 border-t border-gray-100"><span className="text-gray-600 font-bold">Profit</span><span className={`font-bold ${est.profit >= 0 ? "text-green-600" : "text-red-600"}`}>${est.profit.toFixed(2)}</span></div>
                    </div>
                </div>
            )}
        </span>
    );
};

// Walmart's raw `status` field is coarse (AWAITING_DELIVERY / CLOSED) —
// their own Seller Central UI derives a friendlier, more specific label
// using actual_delivery_date and received-vs-requested quantity as extra
// signals. Reproduced here so our badges match what sellers already see
// on Walmart's own site.
const deriveDisplayStatus = (split, splitItems) => {
    if (split.status === "CANCELLED") return { label: "Cancelled", key: "CANCELLED" };
    if (split.status === "CLOSED") {
        const totalQty = splitItems.reduce((s, it) => s + (it.item_qty || 0), 0);
        const totalReceived = splitItems.reduce((s, it) => s + (it.received_qty || 0), 0);
        return totalReceived >= totalQty && totalQty > 0
            ? { label: "Completed", key: "COMPLETED" }
            : { label: "Closed", key: "CLOSED" };
    }
    if (split.actual_delivery_date) return { label: "Arrived", key: "ARRIVED" };
    return { label: "Awaiting Transit", key: "AWAITING_TRANSIT" };
};

const STATUS_STYLES = {
    draft: "bg-gray-100 text-gray-500",
    previewed: "bg-blue-50 text-blue-600",
    created: "bg-teal-50 text-teal-700",
    cancelled: "bg-red-50 text-red-600",
    AWAITING_DELIVERY: "bg-orange-50 text-orange-600",
    AWAITING_TRANSIT: "bg-orange-50 text-orange-600",
    ARRIVED: "bg-blue-50 text-blue-600",
    RECEIVING_IN_PROGRESS: "bg-blue-50 text-blue-600",
    CLOSED: "bg-gray-100 text-gray-500",
    COMPLETED: "bg-green-50 text-green-600",
    CANCELLED: "bg-red-50 text-red-600",
};
const StatusBadge = ({ status, label }) => (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_STYLES[status] || "bg-gray-100 text-gray-500"}`}>
        {label || (status || "unknown").replace(/_/g, " ")}
    </span>
);

const PublishedStatusBadge = ({ status }) => (
    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap ${status === "PUBLISHED" ? "bg-green-50 text-green-600" : "bg-orange-50 text-orange-600"}`}>
        {status || "UNKNOWN"}
    </span>
);

const CatalogStockBadge = ({ stock }) => (
    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap ${(stock || 0) > 0 ? "bg-blue-50 text-blue-600" : "bg-red-50 text-red-600"}`}>
        WFS Stock: {stock ?? "—"}
    </span>
);

/* ═══════════════════════════════════════════════════════════
   STEP 1 — Source: pick a WFS batch or hand-pick catalog items
   ═══════════════════════════════════════════════════════════ */
const SOURCE_ITEMS_PAGE_SIZE = 30;

const SOURCE_SEARCH_TYPES = [
    { id: "all", label: "All" },
    { id: "title", label: "Name" },
    { id: "sku", label: "SKU" },
    { id: "upc", label: "UPC" },
    { id: "gtin", label: "GTIN" },
    { id: "wpid", label: "Item ID" },
];

// Used by the auto-select-on-scan effect below — a partial/substring match
// (e.g. missing the last 2 digits) must NEVER auto-select. Only a fully
// exact code match is allowed to.
const isExactCodeMatch = (it, code, searchType) => {
    if (!code) return false;
    if (searchType === "all") {
        return [it.sku, it.upc, it.gtin, it.wpid].some((v) => v != null && String(v) === code);
    }
    const val = it[searchType];
    return val != null && String(val) === code;
};

// "123456789" or "https://www.walmart.com/ip/some-name/123456789?..." -> "123456789"
const parseWalmartItemId = (raw) => {
    const s = (raw || "").trim();
    const m = s.match(/walmart\.com\/ip\/(?:[^/?#]+\/)?(\d+)/i);
    if (m) return m[1];
    return /^\d{5,}$/.test(s) ? s : null;
};

// Looks up a numeric walmart.com item ID -> { wpid, sku } (debounced + cached).
const useWalmartItemIdLookup = (search, searchType) => {
    const [resolved, setResolved] = useState(null);
    const [looking, setLooking] = useState(false);
    const cacheRef = useRef({});
    const itemId = (searchType === "all" || searchType === "wpid") ? parseWalmartItemId(search) : null;

    useEffect(() => {
        if (!itemId) return;
        if (cacheRef.current[itemId] !== undefined) { setResolved(cacheRef.current[itemId]); return; }
        let cancelled = false;
        const t = setTimeout(async () => {
            setLooking(true);
            let result = null;
            try {
                const d = await resolveWalmartItemId(itemId);
                if (d?.match) result = { itemId, wpid: d.match.wpid, sku: d.match.sku };
            } catch { /* not found */ }
            cacheRef.current[itemId] = result;
            if (!cancelled) { setResolved(result); setLooking(false); }
        }, 400);
        return () => { cancelled = true; clearTimeout(t); setLooking(false); };
    }, [itemId]);

    return { resolved: resolved?.itemId === itemId ? resolved : null, looking };
};

const matchesResolvedItemId = (it, resolved) =>
    !!resolved && ((resolved.wpid && it.wpid === resolved.wpid) || (resolved.sku && it.sku === resolved.sku));

const SourceStep = ({ onNext, onGoToAllShipments }) => {
    const [mode, setMode] = useState(null); // 'batch' | 'catalog'
    // Tracks whether the current search value arrived via an actual paste
    // (vs typed character-by-character) — set on the input's onPaste, read
    // and cleared by the auto-check effect below.
    const searchPastedRef = useRef(false);
    const searchInputRef = useRef(null);
    const [batches, setBatches] = useState(null);
    const [catalogItems, setCatalogItems] = useState(null);
    const [selectedBatchId, setSelectedBatchId] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [qtyById, setQtyById] = useState(() => loadSelectedQtyMap());
    const [loading, setLoading] = useState(false);
    const [reloadingCatalog, setReloadingCatalog] = useState(false);
    const [search, setSearch] = useState("");
    const [searchType, setSearchType] = useState("all");
    const [publishedFilter, setPublishedFilter] = useState("published"); // 'all' | 'published' | 'unpublished'
    const [stockFilter, setStockFilter] = useState("all"); // 'all' | 'in_stock' | 'out_of_stock'
    const [brandFilter, setBrandFilter] = useState("all");
    const [visibleCount, setVisibleCount] = useState(SOURCE_ITEMS_PAGE_SIZE);
    const [creatingDraft, setCreatingDraft] = useState(false);
    const [draftError, setDraftError] = useState("");
    const [savedShipmentId, setSavedShipmentId] = useState(null); // draft created by "Save" (or Continue)
    const [savingOnly, setSavingOnly] = useState(false);
    const [savedMsg, setSavedMsg] = useState("");
    const { resolved: resolvedItemId, looking: lookingUpItemId } = useWalmartItemIdLookup(search, searchType);

    const brandOptions = useMemo(() => {
        const set = new Set((catalogItems || []).map((it) => it.brand).filter(Boolean));
        return [...set].sort((a, b) => a.localeCompare(b));
    }, [catalogItems]);

    const filteredItems = useMemo(() => {
        const list = catalogItems || [];
        const q = search.trim().toLowerCase();
        return list.filter((it) => {
            if (q) {
                const matches = searchType === "all"
                    ? [it.title, it.sku, it.upc, it.gtin, it.wpid].some((v) => v && String(v).toLowerCase().includes(q))
                    : (it[searchType] && String(it[searchType]).toLowerCase().includes(q));
                if (!matches && !matchesResolvedItemId(it, resolvedItemId)) return false;
            }
            if (publishedFilter === "published" && it.published_status !== "PUBLISHED") return false;
            if (publishedFilter === "unpublished" && it.published_status === "PUBLISHED") return false;
            if (stockFilter === "in_stock" && !((it.wfs_stock || 0) > 0)) return false;
            if (stockFilter === "out_of_stock" && (it.wfs_stock || 0) > 0) return false;
            if (brandFilter !== "all" && it.brand !== brandFilter) return false;
            return true;
        });
    }, [catalogItems, search, searchType, publishedFilter, stockFilter, brandFilter, resolvedItemId]);

    // The moment search narrows to exactly one match, fetch its image right
    // away if missing — enrichMissingImages's staggered background loop
    // might not reach this item for a while, and it's very likely the
    // seller's only choice right now.
    useEffect(() => {
        if (filteredItems.length !== 1) return;
        const it = filteredItems[0];
        if (!it.catalog_item_id || it.image_url) return;
        getItemDetails(it.catalog_item_id).then((result) => {
            if (result?.image_url) {
                setCatalogItems((prev) => (prev || []).map((row) =>
                    row.catalog_item_id === it.catalog_item_id
                        ? { ...row, image_url: result.image_url, brand: result.brand || row.brand }
                        : row
                ));
            }
        }).catch(() => { /* best-effort */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredItems.length === 1 ? filteredItems[0]?.catalog_item_id : null]);

    // Any time the search/filter changes, start back at page 1 — otherwise
    // "visibleCount" from a broad list could hide matches in a narrow one.
    useEffect(() => {
        setVisibleCount(SOURCE_ITEMS_PAGE_SIZE);
    }, [search, searchType, publishedFilter, stockFilter, brandFilter]);

    // A purely numeric 10+ digit value (any search type) auto-checks a
    // single match — fires on paste AND on typing, but ONLY when it's an
    // EXACT match for that item's code, never a substring match.
    useEffect(() => {
        const trimmed = search.trim();
        if (/^\d{10,}$/.test(trimmed) && filteredItems.length === 1 && isExactCodeMatch(filteredItems[0], trimmed, searchType)) {
            const id = filteredItems[0].catalog_item_id;
            setSelectedIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
        }
        searchPastedRef.current = false;
    }, [search, filteredItems, searchType]);

    // isReload=true re-fetches even when catalogItems already has data (used
    // by the "Reload items" button when a search comes up empty — the item
    // may have just been added/published and isn't in what we loaded yet).
    const loadCatalogItems = (isReload) => {
        if (isReload) setReloadingCatalog(true); else setLoading(true);
        return getEligibleWfsItems()
            .then((d) => {
                const items = dedupeByProductId(d.items || []);
                setCatalogItems(items);
                enrichMissingImages(items, setCatalogItems);
            })
            .catch(() => { if (!isReload) setCatalogItems([]); })
            .finally(() => { if (isReload) setReloadingCatalog(false); else setLoading(false); });
    };

    useEffect(() => {
        if (mode === "batch" && batches === null) {
            listBatches("open").then((d) => setBatches((d.batches || []).filter((b) => b.batch_type === "walmart_wfs"))).catch(() => setBatches([]));
        }
        if (mode === "catalog" && catalogItems === null) {
            loadCatalogItems(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    const loadBatchItems = async (batchId) => {
        setLoading(true);
        setSelectedBatchId(batchId);
        try {
            const d = await getEligibleWfsItems(batchId);
            const items = dedupeByProductId(d.items || []);
            setCatalogItems(items);
            setSelectedIds(new Set(items.map((it) => it.catalog_item_id)));
            enrichMissingImages(items, setCatalogItems);
        } finally {
            setLoading(false);
        }
    };

    const toggle = (id) => setSelectedIds((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    const setQty = (id, qty) => setQtyById((prev) => {
        const next = { ...prev, [id]: qty };
        saveSelectedQtyMap(next);
        return next;
    });

    // For the input's value — shows exactly what's been typed (including
    // empty, mid-edit) once the seller has touched this row's qty at all.
    // Only falls back to the catalog default when the row hasn't been
    // touched yet, so clearing the field to retype doesn't snap back to 1.
    const getQtyDisplayValue = (it) => {
        const id = it.catalog_item_id;
        if (Object.prototype.hasOwnProperty.call(qtyById, id)) return qtyById[id];
        return String((it.ship_qty_default != null && it.ship_qty_default > 0) ? it.ship_qty_default : 1);
    };

    // For actually USING the qty (Continue button) — resolves to a real
    // positive integer, falling back to the catalog default (or 1) if the
    // field was left blank/invalid.
    const getQtyValue = (it) => {
        const saved = parseInt(qtyById[it.catalog_item_id], 10);
        if (saved > 0) return saved;
        return (it.ship_qty_default != null && it.ship_qty_default > 0) ? it.ship_qty_default : 1;
    };

    const selectedSummary = useMemo(() => {
        const itemById = new Map((catalogItems || []).map((it) => [it.catalog_item_id, it]));
        return [...selectedIds].map((id) => {
            const it = itemById.get(id);
            if (!it) return null;
            return {
                id, title: it.title, sku: it.sku, qty: getQtyValue(it),
                image_url: it.image_url, product_id: it.gtin || it.upc || "",
            };
        }).filter(Boolean);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIds, catalogItems, qtyById]);
    const selectedTotalQty = useMemo(() => selectedSummary.reduce((s, r) => s + r.qty, 0), [selectedSummary]);

    // Shared by "Save" and "Continue". First call creates the draft; later
    // calls update the same draft (and drop anything unchecked since).
    const persistSelection = async () => {
        const itemById = new Map((catalogItems || []).map((it) => [it.catalog_item_id, it]));
        const items = [...selectedIds].map((id) => {
            const base = itemById.get(id);
            if (!base) return null;
            return { ...base, ship_qty_default: getQtyValue(base) };
        }).filter(Boolean);
        const draftItems = items.map((it) => ({
            catalog_item_id: it.catalog_item_id, sku: it.sku, title: it.title, image_url: it.image_url,
            product_id: it.gtin || it.upc || "", product_id_type: it.gtin ? "GTIN" : "UPC",
            item_desc: it.title,
            item_qty: (it.ship_qty_default != null && it.ship_qty_default > 0) ? it.ship_qty_default : 1,
            vendor_pack_qty: 1, inner_pack_qty: 1,
        }));
        let id = savedShipmentId;
        if (!id) {
            const { shipment } = await createShipmentDraft({ source_batch_id: selectedBatchId, items: draftItems });
            id = shipment.id;
            setSavedShipmentId(id);
        } else {
            await addDraftItems(id, draftItems);
            const { shipment } = await getWfsShipment(id);
            const keep = new Set(items.map((it) => it.catalog_item_id));
            for (const row of shipment?.wfs_shipment_items || []) {
                if (!keep.has(row.catalog_item_id)) await removeShipmentItem(id, row.id).catch(() => { });
            }
        }
        return { items, id };
    };

    if (!mode) {
        return (
            <div className="max-w-2xl mx-auto py-10 px-6">
                <h1 className="text-2xl font-bold text-gray-800 mb-2">New WFS Shipment</h1>
                <p className="text-gray-500 text-sm mb-8">How do you want to choose what's going in this shipment?</p>
                <div className="grid grid-cols-3 gap-4">
                    <button onClick={() => setMode("batch")} className="bg-white rounded-2xl shadow-lg p-6 text-left hover:border-teal-500 border-2 border-transparent transition">
                        <Package size={28} className="text-teal-600 mb-3" />
                        <h3 className="font-bold text-gray-800 mb-1">From a Batch</h3>
                        <p className="text-xs text-gray-500">Ship the items you just converted to WFS in a batch.</p>
                    </button>
                    <button onClick={() => setMode("catalog")} className="bg-white rounded-2xl shadow-lg p-6 text-left hover:border-teal-500 border-2 border-transparent transition">
                        <Warehouse size={28} className="text-blue-600 mb-3" />
                        <h3 className="font-bold text-gray-800 mb-1">Pick from Catalog</h3>
                        <p className="text-xs text-gray-500">Hand-pick any WFS item that needs restocking.</p>
                    </button>
                    <button onClick={onGoToAllShipments} className="bg-white rounded-2xl shadow-lg p-6 text-left hover:border-teal-500 border-2 border-transparent transition">
                        <ClipboardCheck size={28} className="text-gray-600 mb-3" />
                        <h3 className="font-bold text-gray-800 mb-1">View Existing Shipments</h3>
                        <p className="text-xs text-gray-500">See or resume shipments you've already started.</p>
                    </button>
                </div>
            </div>
        );
    }

    if (mode === "batch" && !selectedBatchId) {
        return (
            <div className="max-w-2xl mx-auto py-10 px-6">
                <button onClick={() => setMode(null)} className="text-gray-400 hover:text-gray-600 mb-4 flex items-center gap-1 text-sm"><ChevronLeft size={16} /> Back</button>
                <h1 className="text-xl font-bold text-gray-800 mb-4">Choose a Batch</h1>
                {batches === null && <p className="text-gray-400 text-sm">Loading...</p>}
                {batches?.length === 0 && <p className="text-gray-400 text-sm">No open WFS batches found. Try picking from Catalog instead.</p>}
                <div className="space-y-2">
                    {(batches || []).map((b) => (
                        <button key={b.id} onClick={() => loadBatchItems(b.id)} className="w-full bg-white rounded-xl shadow p-4 text-left hover:bg-teal-50 transition flex items-center justify-between">
                            <div>
                                <p className="font-semibold text-gray-800 text-sm">{b.name}</p>
                                <p className="text-xs text-gray-400">{b.batch_items?.[0]?.count ?? 0} items</p>
                            </div>
                            <ChevronRight size={16} className="text-gray-300" />
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto py-10 px-6">
            <button onClick={() => { setMode(null); setCatalogItems(null); setSelectedIds(new Set()); setSelectedBatchId(null); setSearch(""); setSearchType("all"); }}
                className="text-gray-400 hover:text-gray-600 mb-4 flex items-center gap-1 text-sm"><ChevronLeft size={16} /> Back</button>
            <h1 className="text-xl font-bold text-gray-800 mb-1">Select Items to Ship</h1>
            <p className="text-gray-500 text-sm mb-5">{selectedIds.size} item{selectedIds.size === 1 ? "" : "s"} selected</p>

            {loading && (
                <div className="flex items-center gap-2 text-gray-400 text-sm py-6">
                    <Loader size={18} className="animate-spin" /> Loading your full catalog...
                </div>
            )}
            {!loading && catalogItems?.length === 0 && <p className="text-gray-400 text-sm">No eligible WFS items found.</p>}

            {!loading && catalogItems?.length > 0 && (
                <div className="mb-4">
                    <div className="relative mb-2">
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
                        <input
                            ref={searchInputRef}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onPaste={() => { searchPastedRef.current = true; }}
                            placeholder={`Search by ${SOURCE_SEARCH_TYPES.find((t) => t.id === searchType)?.label.toLowerCase()}...`}
                            className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-teal-500"
                        />
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                        {SOURCE_SEARCH_TYPES.map((t) => (
                            <button key={t.id} onClick={() => setSearchType(t.id)}
                                className={`px-3 py-1 text-xs font-bold rounded-full border-2 transition ${searchType === t.id ? "border-teal-500 bg-teal-50 text-teal-700" : "border-gray-200 text-gray-400 hover:border-gray-300"}`}>
                                {t.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-2 flex-wrap mt-2">
                        <select value={publishedFilter} onChange={(e) => setPublishedFilter(e.target.value)}
                            className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500 bg-white">
                            <option value="all">All Publish Statuses</option>
                            <option value="published">Published Only</option>
                            <option value="unpublished">Unpublished Only</option>
                        </select>
                        <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)}
                            className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500 bg-white">
                            <option value="all">All Stock Levels</option>
                            <option value="in_stock">In Stock</option>
                            <option value="out_of_stock">Out of Stock</option>
                        </select>
                        {brandOptions.length > 0 && (
                            <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}
                                className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500 bg-white">
                                <option value="all">All Brands</option>
                                {brandOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                            </select>
                        )}
                        {(publishedFilter !== "all" || stockFilter !== "all" || brandFilter !== "all") && (
                            <button type="button" onClick={() => { setPublishedFilter("all"); setStockFilter("all"); setBrandFilter("all"); }}
                                className="text-xs text-gray-400 hover:text-red-500 font-semibold">
                                Clear Filters
                            </button>
                        )}
                    </div>
                </div>
            )}

            <div className="bg-white rounded-2xl shadow-lg divide-y divide-gray-100 max-h-[50vh] overflow-y-auto mb-2">
                {filteredItems.length === 0 && search.trim() && (
                    <div className="p-4 text-center">
                        <p className="text-gray-400 text-sm mb-2">{lookingUpItemId ? "Looking up Walmart item ID..." : `No items match "${search}".`}</p>
                        <button type="button" onClick={() => loadCatalogItems(true)} disabled={reloadingCatalog}
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-teal-600 hover:text-teal-700 underline decoration-dotted disabled:opacity-50">
                            <RefreshCcw size={12} className={reloadingCatalog ? "animate-spin" : ""} />
                            {reloadingCatalog ? "Reloading..." : "Reload items (in case they were just added via MSP - if added through WM, please sync your catalog first)"}
                        </button>
                    </div>
                )}
                {filteredItems.slice(0, visibleCount).map((it) => {
                    const checked = selectedIds.has(it.catalog_item_id);
                    const unpublished = it.published_status !== "PUBLISHED";
                    return (
                        <label key={it.catalog_item_id} className={`flex items-center gap-3 p-3 cursor-pointer ${unpublished ? "bg-orange-50 hover:bg-orange-100" : "hover:bg-gray-50"}`}>
                            <input type="checkbox" checked={checked} onChange={() => toggle(it.catalog_item_id)} className="w-4 h-4 accent-teal-600" />
                            <ProductImage src={it.image_url} />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                    <HoverTitle title={it.title} className="text-sm font-semibold text-gray-800 truncate block max-w-[280px]" />
                                    <PublishedStatusBadge status={it.published_status} />
                                    <CatalogStockBadge stock={it.wfs_stock} />
                                </div>
                                <p className="text-xs text-gray-400">
                                    {it.sku}{it.upc && ` · UPC ${it.upc}`}{it.gtin && ` · GTIN ${it.gtin}`}{it.wpid && ` · ID ${it.wpid}`}
                                </p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                <span className="text-[9px] font-bold text-gray-400 uppercase">Qty</span>
                                <input
                                    type="number" min="1" value={getQtyDisplayValue(it)}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => setQty(it.catalog_item_id, e.target.value)}
                                    onWheel={preventWheelChange}
                                    onKeyDown={(e) => {
                                        if (e.key !== "Enter") return;
                                        e.preventDefault();
                                        setSearch("");
                                        searchInputRef.current?.focus();
                                    }}
                                    className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-teal-500"
                                />
                            </div>
                        </label>
                    );
                })}
            </div>

            {visibleCount < filteredItems.length && (
                <button onClick={() => setVisibleCount((c) => c + SOURCE_ITEMS_PAGE_SIZE)}
                    className="w-full mb-6 py-2.5 border-2 border-dashed border-gray-300 hover:border-teal-400 rounded-xl text-gray-500 hover:text-teal-600 text-sm font-semibold transition">
                    Load {SOURCE_ITEMS_PAGE_SIZE} More ({filteredItems.length - visibleCount} remaining)
                </button>
            )}
            {visibleCount >= filteredItems.length && filteredItems.length > 0 && (
                <div className="mb-6" />
            )}

            {draftError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3 text-xs text-red-700 flex items-center gap-2">
                    <AlertCircle size={14} /> {draftError}
                </div>
            )}

            <div className="flex gap-2">
                <button
                    disabled={selectedIds.size === 0 || creatingDraft || savingOnly}
                    onClick={async () => {
                        setSavingOnly(true);
                        setDraftError("");
                        try {
                            await persistSelection();
                            setSavedMsg("Saved ✓ — you can find it in All Shipments");
                            setTimeout(() => setSavedMsg(""), 3000);
                        } catch (e) {
                            setDraftError(e.message || "Failed to save");
                        } finally {
                            setSavingOnly(false);
                        }
                    }}
                    className="px-5 py-3 bg-white border-2 border-teal-500 text-teal-700 rounded-xl font-bold hover:bg-teal-50 transition disabled:opacity-40 flex items-center justify-center gap-2 whitespace-nowrap"
                >
                    {savingOnly ? <><Loader size={16} className="animate-spin" /> Saving...</> : "Save"}
                </button>
                <button
                    disabled={selectedIds.size === 0 || creatingDraft || savingOnly}
                    onClick={async () => {
                        setCreatingDraft(true);
                        setDraftError("");
                        try {
                            const { items, id } = await persistSelection();
                            onNext({ sourceBatchId: selectedBatchId, items, shipmentId: id });
                        } catch (e) {
                            setDraftError(e.message || "Failed to start this shipment");
                        } finally {
                            setCreatingDraft(false);
                        }
                    }}
                    className="flex-1 py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition disabled:opacity-40 flex items-center justify-center gap-2"
                >
                    {creatingDraft ? <><Loader size={16} className="animate-spin" /> Starting...</> : <>Continue with {selectedIds.size} Item{selectedIds.size === 1 ? "" : "s"} ({selectedTotalQty} units) <ArrowRight size={16} /></>}
                </button>
            </div>
            {savedMsg && <p className="text-xs text-green-600 font-semibold mt-2 text-center">{savedMsg}</p>}

            {selectedSummary.length > 0 && (
                <div className="mt-4 bg-gray-50 border border-gray-200 rounded-xl p-3">
                    <p className="text-xs font-bold text-gray-500 uppercase mb-2">
                        {selectedSummary.length} item{selectedSummary.length === 1 ? "" : "s"} · {selectedTotalQty} total units
                    </p>
                    <div className="max-h-40 overflow-y-auto">
                        <table className="w-full text-xs">
                            <tbody>
                                {selectedSummary.map((r) => (
                                    <tr key={r.id} className="border-b border-gray-100 last:border-0">
                                        <td className="py-1 pr-2 w-8"><ProductImage src={r.image_url} /></td>
                                        <td className="py-1 text-gray-600 truncate max-w-[200px]">
                                            <TitleHoverWrapper text={r.title}>
                                                <span className="truncate block">{r.title}</span>
                                            </TitleHoverWrapper>
                                        </td>
                                        <td className="py-1 text-gray-400 font-mono">{r.sku}</td>
                                        <td className="py-1 pl-1">
                                            <button
                                                type="button"
                                                onClick={() => printPackRowLabel(
                                                    { product_id: r.product_id, title: r.title },
                                                    getQtyValue({ catalog_item_id: r.id, ship_qty_default: r.qty }),
                                                    getSavedExpirationDate(r.sku)
                                                )}
                                                disabled={!r.product_id}
                                                title={r.product_id ? "Print label(s) for this item" : "No barcode available for this item"}
                                                className="p-1.5 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg hover:bg-teal-100 transition disabled:opacity-30 disabled:hover:bg-teal-50"
                                            >
                                                🖨️
                                            </button>
                                        </td>
                                        <td className="py-1 pl-1">
                                            <input
                                                type="number" min="1" value={getQtyDisplayValue({ catalog_item_id: r.id, ship_qty_default: r.qty })}
                                                onChange={(e) => setQty(r.id, e.target.value)}
                                                onWheel={preventWheelChange}
                                                className="w-14 border border-gray-200 rounded px-1.5 py-0.5 text-xs text-right font-bold text-teal-700 focus:outline-none focus:border-teal-500"
                                            />
                                        </td>
                                        <td className="py-1 pl-1 pr-1">
                                            <button type="button" onClick={() => toggle(r.id)}
                                                title="Remove item"
                                                className="text-gray-300 hover:text-red-500">
                                                <X size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════
   STEP 2 — Pack details per item (qty, pack config, dims, weight)
   ═══════════════════════════════════════════════════════════ */
// Pulls cost basis (item_costs) and platform fees for a batch of catalog
// items, then runs the SAME estimateWalmartProfit calc Batches uses, so
// shipment rows show the identical profit/ROI numbers a seller already
// sees elsewhere in the app. Missing sale_price/cost data just yields a
// null profit_est for that row rather than blocking the whole batch.
const enrichRowsWithProfit = async (builtRows) => {
    const ids = builtRows.map((r) => r.catalog_item_id).filter(Boolean);
    if (ids.length === 0) return builtRows;
    let metaByItem = {}, feesByItem = {};
    try {
        const [metaResp, feesResp] = await Promise.all([
            getItemsMetadata(ids),
            getItemsFeesBatch(ids),
        ]);
        metaByItem = metaResp.items || {};
        feesByItem = feesResp.fees || {};
    } catch { /* non-fatal — rows just won't show profit/ROI */ }

    return builtRows.map((r) => {
        const meta = metaByItem[r.catalog_item_id] || {};
        const costsObj = meta.costs || {};
        const costPriceVal = parseFloat(costsObj["Cost Price"]) || 0;
        const extraCostsVal = Object.entries(costsObj)
            .filter(([name]) => name.toLowerCase() !== "cost price")
            .reduce((s, [, v]) => s + (parseFloat(v) || 0), 0);
        const est = estimateWalmartProfit({
            salePrice: r.sale_price || 0, costPrice: costPriceVal, extraCosts: extraCostsVal,
            weight: parseFloat(r.weight) || null,
            dimensions: { length: parseFloat(r.length) || null, width: parseFloat(r.width) || null, height: parseFloat(r.height) || null },
            category: meta.category || null,
            subCategory: meta.sub_category || null,
            isWFS: true,
        });
        return { ...r, cost_price: costPriceVal, extra_costs: extraCostsVal, profit_est: est };
    });
};

const PackDetailsStep = ({ shipmentId, sourceItems, onNext, onBack, initialRows }) => {
    const [rows, setRows] = useState(initialRows || null);
    const [setAllOpen, setSetAllOpen] = useState(false);
    const [setAllValue, setSetAllValue] = useState("");
    const [showAddMore, setShowAddMore] = useState(false);
    const [replensModalRow, setReplensModalRow] = useState(null); // row whose Replens modal is open, or null

    // Builds one fully-enriched row (profile -> past batch payload -> live
    // Walmart lookup, in that priority) for a single catalog item. Shared
    // by the initial-load effect below AND by "Add More Items", so newly
    // added items get the exact same treatment as the original selection.
    const buildRow = async (it) => {
        let profile = null;
        try { profile = (await getPackProfile(it.catalog_item_id)).profile; } catch { /* none yet */ }

        let weight = profile?.weight || it.weight || "";
        let length = profile?.dimensions?.length || it.length || "";
        let width = profile?.dimensions?.width || it.width || "";
        let height = profile?.dimensions?.height || it.height || "";

        if ((!weight || !length || !width || !height) && it.wpid) {
            try {
                const live = await getWalmartInventoryBasic(it.wpid);
                weight = weight || (live.weight != null ? String(live.weight) : "");
                length = length || (live.dimensions?.length != null ? String(live.dimensions.length) : "");
                width = width || (live.dimensions?.width != null ? String(live.dimensions.width) : "");
                height = height || (live.dimensions?.height != null ? String(live.dimensions.height) : "");
            } catch { /* live lookup failed — leave blank for manual entry */ }
        }

        const shipQtyDefault = (it.ship_qty_default != null && it.ship_qty_default > 0) ? it.ship_qty_default : 1;
        const savedVendorPackQty = profile?.vendor_pack_qty || 1;
        const savedInnerPackQty = profile?.inner_pack_qty || 1;
        const packMode = profile?.pack_mode || ((savedVendorPackQty > 1 || savedInnerPackQty > 1) ? "case" : "individual");

        return {
            id: it.id || null, wpid: it.wpid || null,
            catalog_item_id: it.catalog_item_id, sku: it.sku, title: it.title, image_url: it.image_url,
            product_id: it.gtin || it.upc || it.product_id || "", product_id_type: it.gtin ? "GTIN" : "UPC",
            expiration_date: it.expiration_date || getSavedExpirationDate(it.sku) || null,
            pack_mode: packMode,
            units_individual: String(shipQtyDefault),
            cases: String(packMode === "case" ? savedVendorPackQty : shipQtyDefault),
            units_per_case: String(packMode === "case" ? savedInnerPackQty : 1),
            weight, length, width, height,
            non_sort_item: false,
            sale_price: it.price ?? null,
            cost_price: 0, extra_costs: 0, profit_est: null,
        };
    };

    useEffect(() => {
        if (initialRows) return; // already have rows from a previous visit — don't rebuild and lose manual edits

        // Resuming a draft (no sourceItems from the catalog picker in this
        // session) — build rows straight from what's already saved on the
        // shipment. wpid isn't persisted on wfs_shipment_items, so we
        // re-fetch it from the catalog (same source SourceStep already
        // uses) and cross-reference by catalog_item_id — otherwise the live
        // Walmart-lookup fallback inside buildRow can never fire for a
        // resumed item still missing weight/dimensions.
        if (!sourceItems && shipmentId) {
            (async () => {
                const { shipment } = await getWfsShipment(shipmentId);
                const shipmentItems = shipment.wfs_shipment_items || [];

                let wpidByCatalogId = {};
                let priceByCatalogId = {};
                try {
                    const { items: catalogItems } = await getEligibleWfsItems();
                    wpidByCatalogId = Object.fromEntries(
                        (catalogItems || []).filter((c) => c.wpid).map((c) => [c.catalog_item_id, c.wpid])
                    );
                    priceByCatalogId = Object.fromEntries(
                        (catalogItems || []).map((c) => [c.catalog_item_id, c.price])
                    );
                } catch { /* catalog fetch failed — proceed without wpid/price, live-lookup fallback just won't fire */ }

                const built = await Promise.all(shipmentItems.map((it) => buildRow({
                    id: it.id,
                    catalog_item_id: it.catalog_item_id, sku: it.sku, title: it.title, image_url: it.image_url,
                    product_id: it.product_id, gtin: it.product_id_type === "GTIN" ? it.product_id : null,
                    upc: it.product_id_type === "UPC" ? it.product_id : null,
                    expiration_date: it.expiration_date,
                    weight: it.weight, length: it.dimensions?.length, width: it.dimensions?.width, height: it.dimensions?.height,
                    ship_qty_default: it.item_qty,
                    wpid: wpidByCatalogId[it.catalog_item_id],
                    price: priceByCatalogId[it.catalog_item_id],
                })));
                setRows(await enrichRowsWithProfit(built));
            })();
            return;
        }

        (async () => {
            const built = await Promise.all(sourceItems.map(async (it) => {
                let profile = null;
                try { profile = (await getPackProfile(it.catalog_item_id)).profile; } catch { /* none yet */ }

                // Priority: your own saved pack profile (deliberate manual
                // correction) > whatever we found on a past batch submission
                // for this item > a live scrape of Walmart's product page as
                // a last resort.
                let weight = profile?.weight || it.weight || "";
                let length = profile?.dimensions?.length || it.length || "";
                let width = profile?.dimensions?.width || it.width || "";
                let height = profile?.dimensions?.height || it.height || "";

                if ((!weight || !length || !width || !height) && it.wpid) {
                    try {
                        const live = await getWalmartInventoryBasic(it.wpid);
                        weight = weight || (live.weight != null ? String(live.weight) : "");
                        length = length || (live.dimensions?.length != null ? String(live.dimensions.length) : "");
                        width = width || (live.dimensions?.width != null ? String(live.dimensions.width) : "");
                        height = height || (live.dimensions?.height != null ? String(live.dimensions.height) : "");
                    } catch { /* live lookup failed — leave blank for manual entry */ }
                }

                const shipQtyDefault = (it.ship_qty_default != null && it.ship_qty_default > 0)
                    ? it.ship_qty_default
                    : 1;
                const savedVendorPackQty = profile?.vendor_pack_qty || 1;
                const savedInnerPackQty = profile?.inner_pack_qty || 1;
                const packMode = profile?.pack_mode || ((savedVendorPackQty > 1 || savedInnerPackQty > 1) ? "case" : "individual");

                return {
                    id: it.id || null, wpid: it.wpid || null,
                    catalog_item_id: it.catalog_item_id, sku: it.sku, title: it.title, image_url: it.image_url,
                    product_id: it.gtin || it.upc || "", product_id_type: it.gtin ? "GTIN" : "UPC",
                    expiration_date: it.expiration_date || getSavedExpirationDate(it.sku) || null,
                    pack_mode: packMode,
                    units_individual: String(shipQtyDefault),
                    cases: String(packMode === "case" ? savedVendorPackQty : shipQtyDefault),
                    units_per_case: String(packMode === "case" ? savedInnerPackQty : 1),
                    weight, length, width, height,
                    non_sort_item: false,
                    sale_price: it.price ?? null,
                    cost_price: 0, extra_costs: 0, profit_est: null,
                };
            }));
            setRows(await enrichRowsWithProfit(built));
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [addingMoreError, setAddingMoreError] = useState("");

    const handleAddMoreItems = async (newCatalogItems) => {
        const newRows = await enrichRowsWithProfit(await Promise.all(newCatalogItems.map(buildRow)));

        // Persist immediately — don't wait for the final "Continue" button.
        // Without this, closing the tab or navigating away right after
        // adding items (before clicking Continue) would silently lose them,
        // defeating the whole point of resumable drafts.
        setAddingMoreError("");
        try {
            const newItemsPayload = newRows.map((r) => {
                const { item_qty, vendor_pack_qty, inner_pack_qty } = getPackNumbers(r);
                return {
                    catalog_item_id: r.catalog_item_id, sku: r.sku, title: r.title, image_url: r.image_url,
                    product_id: r.product_id, product_id_type: r.product_id_type,
                    item_desc: r.title, item_qty, vendor_pack_qty, inner_pack_qty,
                    weight: parseFloat(r.weight) || null,
                    dimensions: { length: parseFloat(r.length) || null, width: parseFloat(r.width) || null, height: parseFloat(r.height) || null },
                    non_sort_item: r.non_sort_item,
                    expiration_date: r.expiration_date,
                };
            });
            await addDraftItems(shipmentId, newItemsPayload);
        } catch (e) {
            setAddingMoreError(e.message || "Failed to save the new items — they were added here but may not survive a refresh. Try Continue to retry saving.");
        }

        // Merge into local rows regardless of save outcome — even if the
        // save above failed, the seller can still see/edit them locally and
        // the next handleContinue call will retry saving everything.
        setRows((prev) => [
            ...prev.filter((r) => !newCatalogItems.some((it) => it.catalog_item_id === r.catalog_item_id)),
            ...newRows,
        ]);
        setShowAddMore(false);
    };

    const update = (idx, field, val) => setRows((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx], [field]: val };
        return next;
    });

    // Removes a row from this draft entirely. Best-effort backend delete —
    // if we don't have a backend item id yet (row was just picked in
    // SourceStep and never round-tripped), it just disappears locally and
    // the next autosave/Continue will persist the remaining rows.
    const handleRemoveRow = async (idx, row) => {
        if (!window.confirm(`Remove ${row.title || row.sku} from this shipment?`)) return;
        setRows((prev) => prev.filter((_, i) => i !== idx));
        if (row.id) {
            try { await removeShipmentItem(shipmentId, row.id); } catch { /* best-effort */ }
        }
    };

    // Debounced autosave — any edit (qty, weight, dims, pack mode, etc.)
    // persists to the draft a moment after the seller stops typing, so
    // leaving the page mid-edit (without ever clicking Continue) doesn't
    // silently lose changes to items that were already saved before.
    const [autosaving, setAutosaving] = useState(false);
    const autosaveTimerRef = useRef(null);
    const isFirstRowsRenderRef = useRef(true);

    useEffect(() => {
        if (!rows || !shipmentId) return;
        // Skip the very first time rows gets populated (initial load/build)
        // — nothing has actually changed yet, so there's nothing new to save.
        if (isFirstRowsRenderRef.current) {
            isFirstRowsRenderRef.current = false;
            return;
        }

        if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = setTimeout(async () => {
            setAutosaving(true);
            try {
                const payload = rows.map((r) => {
                    const { item_qty, vendor_pack_qty, inner_pack_qty } = getPackNumbers(r);
                    return {
                        catalog_item_id: r.catalog_item_id, sku: r.sku, title: r.title, image_url: r.image_url,
                        product_id: r.product_id, product_id_type: r.product_id_type,
                        item_desc: r.title, item_qty, vendor_pack_qty, inner_pack_qty,
                        weight: parseFloat(r.weight) || null,
                        dimensions: { length: parseFloat(r.length) || null, width: parseFloat(r.width) || null, height: parseFloat(r.height) || null },
                        non_sort_item: r.non_sort_item,
                        expiration_date: r.expiration_date,
                    };
                });
                await addDraftItems(shipmentId, payload);
                setAddingMoreError("");
            } catch (e) {
                setAddingMoreError(e.message || "Autosave failed — your changes may not be saved. Try again or use Continue.");
            } finally {
                setAutosaving(false);
            }
        }, 1200); // wait for a pause in typing before saving, not every keystroke

        return () => { if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, shipmentId]);

    const getPackNumbers = (r) => {
        if (r.pack_mode === "individual") {
            const units = parseInt(r.units_individual, 10) || 0;
            return { item_qty: units, vendor_pack_qty: units, inner_pack_qty: 1 };
        }
        const cases = parseInt(r.cases, 10) || 0;
        const unitsPerCase = parseInt(r.units_per_case, 10) || 0;
        return { item_qty: cases * unitsPerCase, vendor_pack_qty: cases, inner_pack_qty: unitsPerCase };
    };

    const updatePackMode = (idx, mode) => setRows((prev) => {
        const next = [...prev];
        const r = next[idx];
        if (mode === r.pack_mode) return prev;
        if (mode === "case") {
            const total = parseInt(r.units_individual, 10) || 1;
            next[idx] = { ...r, pack_mode: "case", cases: String(total), units_per_case: "1" };
        } else {
            const cases = parseInt(r.cases, 10) || 1;
            const perCase = parseInt(r.units_per_case, 10) || 1;
            next[idx] = { ...r, pack_mode: "individual", units_individual: String(cases * perCase) };
        }
        return next;
    });

    const applySetAllQty = () => {
        const qty = parseInt(setAllValue, 10);
        if (!qty || qty <= 0) return;
        setRows((prev) => prev.map((r) => ({ ...r, pack_mode: "individual", units_individual: String(qty) })));
        setSetAllValue("");
        setSetAllOpen(false);
    };

    const missingCount = useMemo(() => {
        if (!rows) return 0;
        return rows.filter((r) => {
            const packInvalid = r.pack_mode === "individual"
                ? (!r.units_individual || parseInt(r.units_individual, 10) <= 0)
                : (!r.cases || parseInt(r.cases, 10) <= 0 || !r.units_per_case || parseInt(r.units_per_case, 10) <= 0);
            return !r.product_id || packInvalid || !r.weight || !r.length || !r.width || !r.height;
        }).length;
    }, [rows]);

    // Rolling total across every row's CURRENT quantity — recomputes live
    // as any individual-units or case-pack field changes, regardless of
    // which pack_mode each row is using.
    const totalUnits = useMemo(() => {
        if (!rows) return 0;
        return rows.reduce((sum, r) => sum + getPackNumbers(r).item_qty, 0);
    }, [rows]);

    // Same shape as BatchSummarySidebar's batchTotals — total cost basis,
    // estimated fees, estimated profit, and blended ROI across every row's
    // CURRENT quantity, so this shipment shows the same kind of profit
    // summary a seller already sees in Batches.
    const shipmentTotals = useMemo(() => {
        const empty = {
            totalCost: 0, totalFees: 0, totalProfit: 0,
            feeBreakdown: { referral_fee: 0, wfs_fee: 0, inbound_shipping: 0, sales_tax: 0 },
        };
        if (!rows) return empty;
        return rows.reduce((acc, r) => {
            if (!r.profit_est) return acc;
            const qty = getPackNumbers(r).item_qty;
            acc.totalCost += r.profit_est.totalCostBasis * qty;
            acc.totalFees += r.profit_est.totalFees * qty;
            acc.totalProfit += r.profit_est.profit * qty;
            acc.feeBreakdown.referral_fee += (r.profit_est.referralFee || 0) * qty;
            acc.feeBreakdown.wfs_fee += (r.profit_est.wfsFee || 0) * qty;
            acc.feeBreakdown.inbound_shipping += (r.profit_est.inboundShipping || 0) * qty;
            acc.feeBreakdown.sales_tax += (r.profit_est.salesTax || 0) * qty;
            return acc;
        }, empty);
    }, [rows]);
    const shipmentRoi = shipmentTotals.totalCost > 0 ? (shipmentTotals.totalProfit / shipmentTotals.totalCost) * 100 : 0;

    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState("");

    const handleContinue = async () => {
        rows.forEach((r) => {
            const { vendor_pack_qty, inner_pack_qty } = getPackNumbers(r);
            savePackProfile(r.catalog_item_id, {
                vendor_pack_qty, inner_pack_qty,
                pack_mode: r.pack_mode,
                weight: parseFloat(r.weight) || null,
                dimensions: { length: parseFloat(r.length) || null, width: parseFloat(r.width) || null, height: parseFloat(r.height) || null },
                add_on_services: [],
            }).catch(() => { });
        });

        const items = rows.map((r) => {
            const { item_qty, vendor_pack_qty, inner_pack_qty } = getPackNumbers(r);
            return {
                catalog_item_id: r.catalog_item_id, sku: r.sku, title: r.title, image_url: r.image_url,
                product_id: r.product_id, product_id_type: r.product_id_type,
                item_desc: r.title, item_qty, vendor_pack_qty, inner_pack_qty,
                weight: parseFloat(r.weight) || null,
                dimensions: { length: parseFloat(r.length) || null, width: parseFloat(r.width) || null, height: parseFloat(r.height) || null },
                non_sort_item: r.non_sort_item,
                expiration_date: r.expiration_date,
            };
        });

        setSaving(true);
        setSaveError("");
        try {
            // The shipment already exists (created back in SourceStep) —
            // addDraftItems upserts by catalog_item_id, so this both saves
            // pack details for items chosen originally AND persists anything
            // added later via "Add More Items."
            await addDraftItems(shipmentId, items);
            onNext(items, rows);
        } catch (e) {
            setSaveError(e.message || "Failed to save pack details");
        } finally {
            setSaving(false);
        }
    };

    if (rows === null) {
        return <div className="max-w-4xl mx-auto py-10 px-6 flex items-center gap-2 text-gray-400"><Loader size={18} className="animate-spin" /> Loading pack details...</div>;
    }

    const inputCls = (missing) => `w-full border rounded-lg px-2 py-1.5 text-xs focus:outline-none ${missing ? "border-red-300 bg-red-50" : "border-gray-200 focus:border-teal-500"}`;

    return (
        <div className="max-w-6xl mx-auto py-10 px-6">
            <button onClick={() => onBack(rows)} className="text-gray-400 hover:text-gray-600 mb-4 flex items-center gap-1 text-sm"><ChevronLeft size={16} /> Back</button>
            <h1 className="text-xl font-bold text-gray-800 mb-1 flex items-center gap-2">
                Pack Details
                {autosaving && <span className="text-[10px] font-normal text-gray-400 flex items-center gap-1"><Loader size={10} className="animate-spin" /> Saving...</span>}
            </h1>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <p className="text-gray-500 text-sm">Confirm quantities, case packs, dimensions & weight for each item. We'll remember these for next time.</p>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <button type="button" onClick={() => setShowAddMore(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-xs font-bold hover:bg-blue-100 transition">
                        + Add More Items
                    </button>
                    <button
                        type="button"
                        onClick={() => printAllLabels(rows.map((r) => {
                            const { item_qty } = getPackNumbers(r);
                            return { row: { product_id: r.product_id, title: r.title }, qty: item_qty, expirationDate: r.expiration_date || getSavedExpirationDate(r.sku) };
                        }))}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg text-xs font-bold hover:bg-teal-100 transition"
                    >
                        🖨️ Print All Labels
                    </button>
                </div>
            </div>
            <p className="text-xs text-gray-400 mb-5">This shipment is already saved as a draft — you can leave anytime and resume from All Shipments.</p>

            {addingMoreError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-xs text-red-700 flex items-center gap-2">
                    <AlertCircle size={14} /> {addingMoreError}
                </div>
            )}

            {missingCount > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 text-xs text-blue-700 flex items-center gap-2">
                    <AlertCircle size={14} /> {missingCount} item{missingCount === 1 ? "" : "s"} still need required details.
                </div>
            )}

            <div className="bg-white rounded-2xl shadow-lg overflow-x-auto mb-6">
                <table className="w-full text-xs table-fixed" style={{ minWidth: 1000 }}>
                    <thead>
                        <tr className="text-left text-gray-400 border-b border-gray-100">
                            <th className="p-3 font-semibold w-48">Item</th>
                            <th className="p-3 font-semibold w-28">GTIN/UPC</th>
                            <th className="p-3 font-semibold w-24">Label</th>
                            <th className="p-3 font-semibold w-64">
                                <div className="flex flex-col gap-1">
                                    <span>Packing</span>
                                    <div className="relative">
                                        <button type="button" onClick={() => setSetAllOpen((o) => !o)}
                                            className="text-[10px] font-bold text-teal-600 hover:text-teal-700 underline decoration-dotted normal-case">
                                            Set All (Individual)
                                        </button>
                                        {setAllOpen && (
                                            <div className="absolute z-20 top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-2.5 w-32">
                                                <input
                                                    type="number" min="1" autoFocus value={setAllValue}
                                                    onChange={(e) => setSetAllValue(e.target.value)}
                                                    onKeyDown={(e) => e.key === "Enter" && applySetAllQty()}
                                                    onWheel={preventWheelChange}
                                                    placeholder="Qty"
                                                    className="w-full border border-gray-200 rounded-lg px-2 py-1 text-xs font-normal focus:outline-none focus:border-teal-500 mb-1.5"
                                                />
                                                <div className="flex gap-1">
                                                    <button type="button" onClick={applySetAllQty}
                                                        className="flex-1 py-1 bg-teal-600 text-white rounded-lg text-[10px] font-bold hover:bg-teal-700 transition">
                                                        Apply
                                                    </button>
                                                    <button type="button" onClick={() => { setSetAllOpen(false); setSetAllValue(""); }}
                                                        className="px-2 py-1 text-gray-400 hover:text-gray-600 text-[10px] font-bold">
                                                        ✕
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </th>
                            <th className="p-3 font-semibold w-20">Weight (lb)</th>
                            <th className="p-3 font-semibold w-20">Length</th>
                            <th className="p-3 font-semibold w-20">Width</th>
                            <th className="p-3 font-semibold w-20">Height</th>
                            <th className="p-3 font-semibold w-24">Profit/ROI</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, idx) => {
                            const { item_qty } = getPackNumbers(r);
                            const individualInvalid = !r.units_individual || parseInt(r.units_individual, 10) <= 0;
                            const casesInvalid = !r.cases || parseInt(r.cases, 10) <= 0;
                            const perCaseInvalid = !r.units_per_case || parseInt(r.units_per_case, 10) <= 0;
                            return (
                                <tr key={r.catalog_item_id} className="border-b border-gray-50">
                                    <td className="p-3">
                                        <div className="flex items-start gap-2">
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveRow(idx, r)}
                                                title="Remove item from this shipment"
                                                className="p-1.5 -ml-1 mt-0.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition flex-shrink-0"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                            <ProductImage src={r.image_url} />
                                            <div className="min-w-0">
                                                <TitleHoverWrapper text={r.title}>
                                                    {r.wpid ? (
                                                        <a
                                                            href={`https://www.walmart.com/ip/${r.wpid}`}
                                                            target="_blank" rel="noopener noreferrer"
                                                            className="font-semibold text-gray-800 hover:text-teal-600 hover:underline truncate max-w-[152px] block"
                                                        >
                                                            {r.title}
                                                        </a>
                                                    ) : (
                                                        <p className="font-semibold text-gray-800 truncate max-w-[152px]">{r.title}</p>
                                                    )}
                                                </TitleHoverWrapper>
                                                <CopyableSku value={r.sku} />
                                                <CopyableCode value={r.product_id} />
                                                <button
                                                    type="button"
                                                    onClick={() => setReplensModalRow(r)}
                                                    className="mt-1 text-[10px] font-bold text-teal-600 hover:text-teal-700 border border-teal-200 rounded-full px-2 py-0.5 whitespace-nowrap"
                                                >
                                                    Configure Replens
                                                </button>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-3">
                                        <input value={r.product_id} onChange={(e) => update(idx, "product_id", e.target.value)} className={inputCls(!r.product_id)} />
                                    </td>
                                    <td className="p-3">
                                        <div className="flex flex-col items-start gap-1">
                                            <button
                                                type="button"
                                                onClick={() => printPackRowLabel(r, item_qty, r.expiration_date || getSavedExpirationDate(r.sku))}
                                                title="Print label(s) for this item"
                                                className="p-2 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg hover:bg-teal-100 transition"
                                            >
                                                🖨️
                                            </button>
                                            <ExpirationDateEditor
                                                catalogItemId={r.catalog_item_id}
                                                sku={r.sku}
                                                value={r.expiration_date}
                                                onSaved={(newDate) => update(idx, "expiration_date", newDate)}
                                            />
                                        </div>
                                    </td>
                                    <td className="p-3">
                                        <div className="flex items-center gap-1.5 mb-1.5">
                                            <button type="button" onClick={() => updatePackMode(idx, "individual")}
                                                className={`px-2 py-1 rounded-lg text-[10px] font-bold border transition ${r.pack_mode === "individual" ? "border-teal-500 bg-teal-50 text-teal-700" : "border-gray-200 text-gray-400 hover:border-gray-300"}`}>
                                                Individual Units
                                            </button>
                                            <button type="button" onClick={() => updatePackMode(idx, "case")}
                                                className={`px-2 py-1 rounded-lg text-[10px] font-bold border transition ${r.pack_mode === "case" ? "border-teal-500 bg-teal-50 text-teal-700" : "border-gray-200 text-gray-400 hover:border-gray-300"}`}>
                                                Case Pack
                                            </button>
                                        </div>
                                        {r.pack_mode === "individual" ? (
                                            <div>
                                                <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Total Units to Ship</p>
                                                <input type="number" min="1" value={r.units_individual}
                                                    onChange={(e) => update(idx, "units_individual", e.target.value)}
                                                    onWheel={preventWheelChange}
                                                    placeholder="e.g. 24"
                                                    className={inputCls(individualInvalid)} />
                                            </div>
                                        ) : (
                                            <div className="flex items-start gap-1.5">
                                                <div>
                                                    <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Cases</p>
                                                    <input type="number" min="1" value={r.cases}
                                                        onChange={(e) => update(idx, "cases", e.target.value)}
                                                        onWheel={preventWheelChange}
                                                        placeholder="e.g. 4"
                                                        className={inputCls(casesInvalid)} style={{ width: 60 }} />
                                                </div>
                                                <span className="text-gray-300 mt-4">×</span>
                                                <div>
                                                    <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Units/Case</p>
                                                    <input type="number" min="1" value={r.units_per_case}
                                                        onChange={(e) => update(idx, "units_per_case", e.target.value)}
                                                        onWheel={preventWheelChange}
                                                        placeholder="e.g. 6"
                                                        className={inputCls(perCaseInvalid)} style={{ width: 60 }} />
                                                </div>
                                                <span className="text-[18px] text-gray-400 whitespace-nowrap mt-4">= {item_qty} units</span>
                                            </div>
                                        )}
                                    </td>
                                    <td className="p-3"><input type="number" step="0.1" value={r.weight} onChange={(e) => update(idx, "weight", e.target.value)} onWheel={preventWheelChange} className={inputCls(!r.weight)} /></td>
                                    <td className="p-3"><input type="number" step="0.1" value={r.length} onChange={(e) => update(idx, "length", e.target.value)} onWheel={preventWheelChange} className={inputCls(!r.length)} /></td>
                                    <td className="p-3"><input type="number" step="0.1" value={r.width} onChange={(e) => update(idx, "width", e.target.value)} onWheel={preventWheelChange} className={inputCls(!r.width)} /></td>
                                    <td className="p-3"><input type="number" step="0.1" value={r.height} onChange={(e) => update(idx, "height", e.target.value)} onWheel={preventWheelChange} className={inputCls(!r.height)} /></td>
                                    <td className="p-3">
                                        <ProfitFeeHover est={r.profit_est} />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
                <p className="text-xs font-bold text-gray-600 uppercase mb-2">Shipment Summary</p>
                <div className="grid grid-cols-5 gap-2 text-center">
                    <div>
                        <p className="text-[10px] text-gray-400">Total Units</p>
                        <p className="font-bold text-sm text-teal-700">{totalUnits.toLocaleString()}</p>
                    </div>
                    <div>
                        <p className="text-[10px] text-gray-400">Total Cost(s)</p>
                        <p className="font-bold text-sm text-gray-700">${shipmentTotals.totalCost.toFixed(2)}</p>
                    </div>
                    <div className="relative group">
                        <p className="text-[10px] text-gray-400 flex items-center justify-center gap-1 cursor-help">
                            Est. Total Fees <span className="underline decoration-dotted">(hover)</span>
                        </p>
                        <p className="font-bold text-sm text-red-600">${shipmentTotals.totalFees.toFixed(2)}</p>
                        <div className="hidden group-hover:block absolute z-20 top-full left-1/2 -translate-x-1/2 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-3 w-56 text-left">
                            <p className="text-[10px] font-bold text-gray-500 uppercase mb-1.5">Fee Breakdown (whole shipment)</p>
                            <div className="space-y-1 text-xs">
                                <div className="flex justify-between"><span className="text-gray-500">Referral Fee</span><span className="font-semibold text-gray-700">${shipmentTotals.feeBreakdown.referral_fee.toFixed(2)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">WFS Fee</span><span className="font-semibold text-gray-700">${shipmentTotals.feeBreakdown.wfs_fee.toFixed(2)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">Inbound Shipping</span><span className="font-semibold text-gray-700">${shipmentTotals.feeBreakdown.inbound_shipping.toFixed(2)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">Sales Tax (on cost)</span><span className="font-semibold text-gray-700">${shipmentTotals.feeBreakdown.sales_tax.toFixed(2)}</span></div>
                                <div className="flex justify-between pt-1 border-t border-gray-100"><span className="text-gray-600 font-bold">Total</span><span className="font-bold text-red-600">${shipmentTotals.totalFees.toFixed(2)}</span></div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <p className="text-[10px] text-gray-400">Est. Profit</p>
                        <p className={`font-bold text-sm ${shipmentTotals.totalProfit >= 0 ? "text-green-600" : "text-red-600"}`}>
                            ${shipmentTotals.totalProfit.toFixed(2)}
                        </p>
                    </div>
                    <div>
                        <p className="text-[10px] text-gray-400">Est. ROI</p>
                        <p className={`font-bold text-sm ${shipmentRoi >= 0 ? "text-purple-600" : "text-red-600"}`}>
                            {shipmentRoi.toFixed(1)}%
                        </p>
                    </div>
                </div>
            </div>

            {saveError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3 text-xs text-red-700">{saveError}</div>
            )}
            <button onClick={handleContinue} disabled={missingCount > 0 || saving}
                className="w-full py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition disabled:opacity-40 flex items-center justify-center gap-2">
                {saving ? <><Loader size={16} className="animate-spin" /> Saving...</> : <>Continue <ArrowRight size={16} /></>}
            </button>

            {showAddMore && (
                <AddMoreItemsModal
                    excludeCatalogItemIds={rows.map((r) => r.catalog_item_id)}
                    onClose={() => setShowAddMore(false)}
                    onAdd={handleAddMoreItems}
                />
            )}

            {replensModalRow && (
                <ReplensModal
                    item={{ id: replensModalRow.catalog_item_id, title: replensModalRow.title, sku: replensModalRow.sku }}
                    onClose={() => setReplensModalRow(null)}
                />
            )}
        </div>
    );
};

const ADD_MORE_ITEMS_PAGE_SIZE = 30;

// Same catalog picker as SourceStep's catalog mode, reused for appending
// items to an already-in-progress shipment — same search-type toggles and
// "Load More" pagination, minus the batch option (adding a whole separate
// batch mid-flow doesn't make sense here). Also lets the seller type/fix a
// UPC directly on the card, since an item without one can't be added
// (buildRow needs SOME product_id to generate a barcode later).
const AddMoreItemsModal = ({ excludeCatalogItemIds, onClose, onAdd }) => {
    const [items, setItems] = useState(null);
    const [batches, setBatches] = useState(null);
    const [showBatchPicker, setShowBatchPicker] = useState(false);
    const [loadingBatch, setLoadingBatch] = useState(false);
    const [activeBatchId, setActiveBatchId] = useState(null);
    const [switchingToCatalog, setSwitchingToCatalog] = useState(false);
    // Tracks whether the current search value arrived via an actual paste
    // (vs typed character-by-character) — set on the input's onPaste, read
    // and cleared by the auto-check effect below.
    const searchPastedRef = useRef(false);
    const searchInputRef = useRef(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [qtyById, setQtyById] = useState(() => loadSelectedQtyMap());
    const [search, setSearch] = useState("");
    const [searchType, setSearchType] = useState("all");
    const [publishedFilter, setPublishedFilter] = useState("published"); // 'all' | 'published' | 'unpublished'
    const [stockFilter, setStockFilter] = useState("all"); // 'all' | 'in_stock' | 'out_of_stock'
    const [brandFilter, setBrandFilter] = useState("all");
    const [visibleCount, setVisibleCount] = useState(ADD_MORE_ITEMS_PAGE_SIZE);
    const [adding, setAdding] = useState(false);
    const [reloading, setReloading] = useState(false);
    // Scoped fix for stale qty reuse: this map is shared across the whole
    // browser session (sessionStorage, by design, so a mid-build refresh
    // doesn't lose typed values) — but that means an item used in an EARLIER,
    // unrelated shipment this session can show its old qty here again. Rather
    // than wiping the shared map (which would also break legitimate
    // mid-session refresh recovery elsewhere), this toggle controls what a
    // FRESH checkbox-selection shows: blank (forces a deliberate type-in) or
    // the catalog's suggested qty — and either way, checking a box always
    // explicitly overwrites any stale leftover value for that item first.
    const [blankByDefault, setBlankByDefault] = useState(true);
    const { resolved: resolvedItemId, looking: lookingUpItemId } = useWalmartItemIdLookup(search, searchType);
    const qtyInputRefs = useRef({});
    const [justCheckedId, setJustCheckedId] = useState(null);

    useEffect(() => {
        if (!justCheckedId) return;
        const el = qtyInputRefs.current[justCheckedId];
        if (el) { el.focus(); el.select(); }
        setJustCheckedId(null);
    }, [justCheckedId]);

    const loadItems = (isReload) => {
        if (isReload) setReloading(true);
        setActiveBatchId(null);
        return getEligibleWfsItems().then((d) => {
            const list = dedupeByProductId(d.items || []);
            setItems(list);
            enrichMissingImages(list, setItems);
        }).catch(() => setItems((prev) => prev || []))
            .finally(() => { if (isReload) setReloading(false); setSwitchingToCatalog(false); });
    };

    const handleBackToFullCatalog = () => {
        setSwitchingToCatalog(true);
        loadItems(false);
    };

    useEffect(() => { loadItems(false); }, []);

    // "Load from Batch" — lists open WFS batches (same source SourceStep
    // uses); picking one narrows the item list to just that batch instead
    // of the full catalog, so there's nothing to search for.
    const loadBatchList = () => {
        setShowBatchPicker(true);
        if (batches === null) {
            listBatches("open").then((d) => setBatches((d.batches || []).filter((b) => b.batch_type === "walmart_wfs"))).catch(() => setBatches([]));
        }
    };

    const loadItemsFromBatch = (batchId) => {
        setLoadingBatch(true);
        setActiveBatchId(batchId);
        setShowBatchPicker(false);
        getEligibleWfsItems(batchId).then((d) => {
            const list = dedupeByProductId(d.items || []);
            setItems(list);
            enrichMissingImages(list, setItems);
        }).catch(() => setItems([]))
            .finally(() => setLoadingBatch(false));
    };

    const excludeSet = new Set(excludeCatalogItemIds);

    const brandOptions = useMemo(() => {
        const set = new Set((items || []).map((it) => it.brand).filter(Boolean));
        return [...set].sort((a, b) => a.localeCompare(b));
    }, [items]);

    const filtered = useMemo(() => {
        const list = (items || []).filter((it) => !excludeSet.has(it.catalog_item_id));
        const q = search.trim().toLowerCase();
        return list.filter((it) => {
            if (q) {
                const matches = searchType === "all"
                    ? [it.title, it.sku, it.upc, it.gtin, it.wpid].some((v) => v && String(v).toLowerCase().includes(q))
                    : (it[searchType] && String(it[searchType]).toLowerCase().includes(q));
                if (!matches && !matchesResolvedItemId(it, resolvedItemId)) return false;
            }
            if (publishedFilter === "published" && it.published_status !== "PUBLISHED") return false;
            if (publishedFilter === "unpublished" && it.published_status === "PUBLISHED") return false;
            if (stockFilter === "in_stock" && !((it.wfs_stock || 0) > 0)) return false;
            if (stockFilter === "out_of_stock" && (it.wfs_stock || 0) > 0) return false;
            if (brandFilter !== "all" && it.brand !== brandFilter) return false;
            return true;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, search, searchType, publishedFilter, stockFilter, brandFilter, resolvedItemId]);

    useEffect(() => {
        setVisibleCount(ADD_MORE_ITEMS_PAGE_SIZE);
    }, [search, searchType, publishedFilter, stockFilter, brandFilter]);

    // A purely numeric 10+ digit value (any search type) auto-checks a
    // single match — fires on paste AND on typing, but ONLY when it's an
    // EXACT match for that item's code, never a substring match. Same as
    // toggle(): always overwrites any stale qty for this item and focuses
    // the qty field so a barcode scan lands the seller straight into typing
    // a number, rather than silently carrying over an old value.
    useEffect(() => {
        const trimmed = search.trim();
        if (/^\d{10,}$/.test(trimmed) && filtered.length === 1 && isExactCodeMatch(filtered[0], trimmed, searchType)) {
            const match = filtered[0];
            const id = match.catalog_item_id;
            setSelectedIds((prev) => {
                if (prev.has(id)) return prev;
                const suggested = (match.ship_qty_default != null && match.ship_qty_default > 0)
                    ? String(match.ship_qty_default) : "1";
                setQty(id, blankByDefault ? "" : suggested);
                setJustCheckedId(id);
                return new Set(prev).add(id);
            });
        }
        searchPastedRef.current = false;
    }, [search, filtered, searchType, blankByDefault]);

    const toggle = (id, catalogItem) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            const wasChecked = next.has(id);
            wasChecked ? next.delete(id) : next.add(id);
            if (!wasChecked) {
                // Freshly checking this item — always overwrite whatever qty
                // is currently sitting in the shared map for it (which may be
                // stale from an earlier, unrelated shipment this session).
                // "Blank" mode clears it so the seller must type a number.
                // "Suggested" mode explicitly writes the catalog's own
                // recommended qty — still an overwrite, never the stale value.
                const suggested = (catalogItem?.ship_qty_default != null && catalogItem.ship_qty_default > 0)
                    ? String(catalogItem.ship_qty_default) : "1";
                setQty(id, blankByDefault ? "" : suggested);
                setJustCheckedId(id);
            }
            return next;
        });
    };

    const setQty = (id, qty) => setQtyById((prev) => {
        const next = { ...prev, [id]: qty };
        saveSelectedQtyMap(next);
        return next;
    });

    const getQtyDisplayValue = (it) => {
        const id = it.catalog_item_id;
        if (Object.prototype.hasOwnProperty.call(qtyById, id)) return qtyById[id];
        return String((it.ship_qty_default != null && it.ship_qty_default > 0) ? it.ship_qty_default : 1);
    };

    const getQtyValue = (it) => {
        const saved = parseInt(qtyById[it.catalog_item_id], 10);
        if (saved > 0) return saved;
        return (it.ship_qty_default != null && it.ship_qty_default > 0) ? it.ship_qty_default : 1;
    };

    const selectedSummary = useMemo(() => {
        const itemById = new Map((items || []).map((it) => [it.catalog_item_id, it]));
        return [...selectedIds].map((id) => {
            const it = itemById.get(id);
            if (!it) return null;
            return {
                id, title: it.title, sku: it.sku, qty: getQtyValue(it),
                image_url: it.image_url, product_id: it.gtin || it.upc || "",
            };
        }).filter(Boolean);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIds, items, qtyById]);
    const selectedTotalQty = useMemo(() => selectedSummary.reduce((s, r) => s + r.qty, 0), [selectedSummary]);

    const handleAdd = async () => {
        setAdding(true);
        try {
            // Iterate selectedIds directly (a Set, which preserves insertion
            // order) rather than filtering `items` — keeps items in the exact
            // order they were clicked/checked, same as SourceStep does.
            const itemById = new Map((items || []).map((it) => [it.catalog_item_id, it]));
            const picked = [...selectedIds].map((id) => {
                const it = itemById.get(id);
                if (!it) return null;
                return { ...it, ship_qty_default: getQtyValue(it) };
            }).filter(Boolean);
            await onAdd(picked);
        } finally {
            setAdding(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                    <h3 className="font-bold text-gray-800 text-sm">Add More Items</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                </div>
                <div className="p-4 flex-shrink-0 space-y-2">
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <input ref={searchInputRef} value={search} onChange={(e) => setSearch(e.target.value)}
                                onPaste={() => { searchPastedRef.current = true; }}
                                placeholder={`Search by ${SOURCE_SEARCH_TYPES.find((t) => t.id === searchType)?.label.toLowerCase()}...`}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" />
                        </div>
                        <div className="relative flex-shrink-0">
                            <button type="button" onClick={loadBatchList}
                                className="flex items-center gap-1.5 px-3 py-2 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg text-xs font-bold hover:bg-purple-100 transition whitespace-nowrap">
                                <Package size={12} /> Load from Batch
                            </button>
                            {showBatchPicker && (
                                <div className="absolute z-20 top-full right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-2 w-72 max-h-64 overflow-y-auto">
                                    <div className="flex items-center justify-between mb-1.5 px-1">
                                        <p className="text-[10px] font-bold text-gray-400 uppercase">Choose a Batch</p>
                                        <button type="button" onClick={() => setShowBatchPicker(false)} className="text-gray-300 hover:text-gray-500"><X size={12} /></button>
                                    </div>
                                    {batches === null && <p className="text-xs text-gray-400 px-1 py-2">Loading...</p>}
                                    {batches?.length === 0 && <p className="text-xs text-gray-400 px-1 py-2">No open WFS batches found.</p>}
                                    {(batches || []).map((b) => (
                                        <button key={b.id} type="button" onClick={() => loadItemsFromBatch(b.id)}
                                            className="w-full text-left px-2 py-2 rounded-lg hover:bg-purple-50 transition">
                                            <p className="text-xs font-semibold text-gray-800">{b.name}</p>
                                            <p className="text-[10px] text-gray-400">{b.batch_items?.[0]?.count ?? 0} items</p>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    {activeBatchId && (
                        <p className="text-xs text-purple-600 font-semibold flex items-center gap-1.5">
                            Showing items from this batch
                            <button type="button" onClick={handleBackToFullCatalog} disabled={switchingToCatalog}
                                className="text-gray-400 hover:text-red-500 underline decoration-dotted font-bold disabled:opacity-50">
                                {switchingToCatalog ? "Loading..." : "Back to Full Catalog"}
                            </button>
                        </p>
                    )}
                    {loadingBatch && (
                        <p className="text-xs text-gray-400 flex items-center gap-1.5"><Loader size={12} className="animate-spin" /> Loading batch items...</p>
                    )}
                    <div className="flex gap-1.5 flex-wrap">
                        {SOURCE_SEARCH_TYPES.map((t) => (
                            <button key={t.id} onClick={() => setSearchType(t.id)}
                                className={`px-3 py-1 text-xs font-bold rounded-full border-2 transition ${searchType === t.id ? "border-teal-500 bg-teal-50 text-teal-700" : "border-gray-200 text-gray-400 hover:border-gray-300"}`}>
                                {t.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <select value={publishedFilter} onChange={(e) => setPublishedFilter(e.target.value)}
                            className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500 bg-white">
                            <option value="all">All Publish Statuses</option>
                            <option value="published">Published Only</option>
                            <option value="unpublished">Unpublished Only</option>
                        </select>
                        <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)}
                            className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500 bg-white">
                            <option value="all">All Stock Levels</option>
                            <option value="in_stock">In Stock</option>
                            <option value="out_of_stock">Out of Stock</option>
                        </select>
                        {brandOptions.length > 0 && (
                            <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}
                                className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500 bg-white">
                                <option value="all">All Brands</option>
                                {brandOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                            </select>
                        )}
                        {(publishedFilter !== "all" || stockFilter !== "all" || brandFilter !== "all") && (
                            <button type="button" onClick={() => { setPublishedFilter("all"); setStockFilter("all"); setBrandFilter("all"); }}
                                className="text-xs text-gray-400 hover:text-red-500 font-semibold">
                                Clear Filters
                            </button>
                        )}
                        <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer ml-auto">
                            <input type="checkbox" checked={blankByDefault} onChange={(e) => setBlankByDefault(e.target.checked)} className="accent-teal-600" />
                            Blank qty by default
                        </label>
                    </div>
                </div>
                <div className="overflow-y-auto flex-1 px-4 pb-4 relative">
                    {switchingToCatalog && (
                        <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center">
                            <span className="flex items-center gap-2 text-sm text-gray-500 font-semibold">
                                <Loader size={16} className="animate-spin" /> Loading full catalog...
                            </span>
                        </div>
                    )}
                    {items === null && <p className="text-gray-400 text-sm py-6 text-center">Loading...</p>}
                    {items && filtered.length === 0 && (
                        <div className="text-center py-6">
                            <p className="text-gray-400 text-sm mb-2">{lookingUpItemId ? "Looking up Walmart item ID..." : "No matching items."}</p>
                            <button type="button" onClick={() => loadItems(true)} disabled={reloading}
                                className="inline-flex items-center gap-1.5 text-xs font-bold text-teal-600 hover:text-teal-700 underline decoration-dotted disabled:opacity-50">
                                <RefreshCcw size={12} className={reloading ? "animate-spin" : ""} />
                                {reloading ? "Reloading..." : "Reload items (in case they were just added via MSP - if added through WM, please sync your catalog first)"}
                            </button>
                        </div>
                    )}
                    {filtered.slice(0, visibleCount).map((it) => {
                        const checked = selectedIds.has(it.catalog_item_id);
                        const unpublished = it.published_status !== "PUBLISHED";
                        return (
                            <label key={it.catalog_item_id} className={`flex items-center gap-3 p-2 cursor-pointer rounded-lg ${unpublished ? "bg-orange-50 hover:bg-orange-100" : "hover:bg-gray-50"}`}>
                                <input type="checkbox" checked={checked} onChange={() => toggle(it.catalog_item_id, it)} className="w-4 h-4 accent-teal-600 flex-shrink-0" />
                                <ProductImage src={it.image_url} />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                        <HoverTitle title={it.title} className="text-sm font-semibold text-gray-800 truncate block max-w-[280px]" />
                                        <PublishedStatusBadge status={it.published_status} />
                                        <CatalogStockBadge stock={it.wfs_stock} />
                                    </div>
                                    <p className="text-xs text-gray-400">
                                        {it.sku}{it.upc && ` · UPC ${it.upc}`}{it.gtin && ` · GTIN ${it.gtin}`}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                    <span className="text-[9px] font-bold text-gray-400 uppercase">Qty</span>
                                    <input
                                        ref={(el) => { qtyInputRefs.current[it.catalog_item_id] = el; }}
                                        type="number" min="1"
                                        value={checked ? getQtyDisplayValue(it) : ""}
                                        placeholder="Qty"
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => setQty(it.catalog_item_id, e.target.value)}
                                        onWheel={preventWheelChange}
                                        onKeyDown={(e) => {
                                            if (e.key !== "Enter") return;
                                            e.preventDefault();
                                            setSearch("");
                                            searchInputRef.current?.focus();
                                        }}
                                        className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-teal-500"
                                    />
                                </div>
                            </label>
                        );
                    })}
                </div>

                {visibleCount < filtered.length && (
                    <div className="px-4 pb-3 flex-shrink-0">
                        <button onClick={() => setVisibleCount((c) => c + ADD_MORE_ITEMS_PAGE_SIZE)}
                            className="w-full py-2 border-2 border-dashed border-gray-300 hover:border-teal-400 rounded-xl text-gray-500 hover:text-teal-600 text-xs font-semibold transition">
                            Load {ADD_MORE_ITEMS_PAGE_SIZE} More ({filtered.length - visibleCount} remaining)
                        </button>
                    </div>
                )}

                <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0">
                    <button onClick={handleAdd} disabled={selectedIds.size === 0 || adding}
                        className="w-full py-2.5 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-lg text-sm font-bold hover:opacity-90 transition disabled:opacity-40">
                        {adding ? "Adding..." : `Add ${selectedIds.size} Item${selectedIds.size === 1 ? "" : "s"} (${selectedTotalQty} units)`}
                    </button>
                    {selectedSummary.length > 0 && (
                        <div className="mt-3 max-h-32 overflow-y-auto border border-gray-100 rounded-lg">
                            <table className="w-full text-xs">
                                <tbody>
                                    {selectedSummary.map((r) => (
                                        <tr key={r.id} className="border-b border-gray-50 last:border-0">
                                            <td className="py-1 pl-2 pr-1 w-8"><ProductImage src={r.image_url} /></td>
                                            <td className="py-1 px-1 text-gray-600 truncate max-w-[150px]">
                                                <TitleHoverWrapper text={r.title}>
                                                    <span className="truncate block">{r.title}</span>
                                                </TitleHoverWrapper>
                                            </td>
                                            <td className="py-1 px-1 text-gray-400 font-mono">{r.sku}</td>
                                            <td className="py-1 px-1 text-gray-400 font-mono">{r.product_id || "—"}</td>
                                            <td className="py-1 px-1">
                                                <button
                                                    type="button"
                                                    onClick={() => printPackRowLabel(
                                                        { product_id: r.product_id, title: r.title },
                                                        getQtyValue({ catalog_item_id: r.id, ship_qty_default: r.qty }),
                                                        getSavedExpirationDate(r.sku)
                                                    )}
                                                    disabled={!r.product_id}
                                                    title={r.product_id ? "Print label(s) for this item" : "No barcode available for this item"}
                                                    className="p-1.5 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg hover:bg-teal-100 transition disabled:opacity-30 disabled:hover:bg-teal-50"
                                                >
                                                    🖨️
                                                </button>
                                            </td>
                                            <td className="py-1 px-1">
                                                <span className="text-[9px] font-bold text-gray-400 uppercase">Qty</span>
                                                <input
                                                    type="number" min="1" value={getQtyDisplayValue({ catalog_item_id: r.id, ship_qty_default: r.qty })}
                                                    onChange={(e) => setQty(r.id, e.target.value)}
                                                    onWheel={preventWheelChange}
                                                    className="w-14 border border-gray-200 rounded ml-1 px-1.5 py-0.5 text-xs text-right font-bold text-teal-700 focus:outline-none focus:border-teal-500"
                                                />
                                            </td>
                                            <td className="py-1 pr-2">
                                                <button type="button" onClick={() => toggle(r.id)}
                                                    title="Remove item"
                                                    className="text-gray-300 hover:text-red-500">
                                                    <X size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════
   STEP 3 — Return address
   ═══════════════════════════════════════════════════════════ */
// USPS 2-letter codes — what Walmart's stateCode field actually expects.
// Includes DC + the territories WFS/USPS treat the same way (PR, VI, GU).
const US_STATES = [
    ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"], ["CA", "California"],
    ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"], ["DC", "District of Columbia"],
    ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"], ["ID", "Idaho"], ["IL", "Illinois"],
    ["IN", "Indiana"], ["IA", "Iowa"], ["KS", "Kansas"], ["KY", "Kentucky"], ["LA", "Louisiana"],
    ["ME", "Maine"], ["MD", "Maryland"], ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"],
    ["MS", "Mississippi"], ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"], ["NV", "Nevada"],
    ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"], ["NY", "New York"],
    ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"], ["OK", "Oklahoma"], ["OR", "Oregon"],
    ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"], ["SD", "South Dakota"],
    ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"], ["VT", "Vermont"], ["VA", "Virginia"],
    ["WA", "Washington"], ["WV", "West Virginia"], ["WI", "Wisconsin"], ["WY", "Wyoming"],
    ["PR", "Puerto Rico"], ["VI", "U.S. Virgin Islands"], ["GU", "Guam"],
];

// WFS return/pickup addresses are almost always domestic — keep this short
// and explicit rather than a giant world list, since a wrong ISO code here
// silently breaks shipment creation.
const RETURN_COUNTRIES = [["US", "United States"]];

export const ReturnAddressStep = ({ onNext, onBack }) => {
    const [addr, setAddr] = useState({ address_line1: "", address_line2: "", city: "", state_code: "", country_code: "US", postal_code: "" });
    const [saveDefault, setSaveDefault] = useState(true);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");

    useEffect(() => {
        getReturnAddress().then((d) => { if (d.address) setAddr(d.address); }).finally(() => setLoading(false));
    }, []);

    const set = (field, val) => setAddr((a) => ({ ...a, [field]: val }));
    const complete = addr.address_line1 && addr.city && addr.state_code && addr.postal_code;

    const handleContinue = async () => {
        setSubmitting(true);
        setSubmitError("");
        try {
            if (saveDefault) await saveReturnAddress(addr).catch(() => { });
            // onNext creates the shipment draft on the backend — await it so
            // the button stays in its loading state for the whole operation,
            // not just the (much faster) address save above.
            await onNext(addr);
        } catch (e) {
            setSubmitError(e.message || "Something went wrong — please try again.");
            setSubmitting(false);
        }
    };

    if (loading) return <div className="max-w-lg mx-auto py-10 px-6 text-gray-400 flex items-center gap-2"><Loader size={16} className="animate-spin" /> Loading...</div>;

    return (
        <div className="max-w-lg mx-auto py-10 px-6">
            <button onClick={onBack} className="text-gray-400 hover:text-gray-600 mb-4 flex items-center gap-1 text-sm"><ChevronLeft size={16} /> Back</button>
            <h1 className="text-xl font-bold text-gray-800 mb-1 flex items-center gap-2"><MapPin size={20} className="text-teal-600" /> Return / Pickup Address</h1>
            <p className="text-gray-500 text-sm mb-5">Where should Walmart send returns or pick up freight from?</p>

            <div className="bg-white rounded-2xl shadow-lg p-6 space-y-3">
                <input placeholder="Address line 1" value={addr.address_line1} onChange={(e) => set("address_line1", e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" />
                <input placeholder="Address line 2 (optional)" value={addr.address_line2 || ""} onChange={(e) => set("address_line2", e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" />
                <div className="grid grid-cols-3 gap-2">
                    <input placeholder="City" value={addr.city} onChange={(e) => set("city", e.target.value)}
                        className="col-span-2 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" />
                    <select value={addr.state_code} onChange={(e) => set("state_code", e.target.value)}
                        className="border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-teal-500 bg-white">
                        <option value="">State</option>
                        {US_STATES.map(([code, name]) => <option key={code} value={code}>{code} — {name}</option>)}
                    </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <input placeholder="ZIP / Postal Code" value={addr.postal_code} onChange={(e) => set("postal_code", e.target.value)}
                        className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" />
                    <select value={addr.country_code} onChange={(e) => set("country_code", e.target.value)}
                        className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 bg-white">
                        {RETURN_COUNTRIES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
                    </select>
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-500 pt-1">
                    <input type="checkbox" checked={saveDefault} onChange={(e) => setSaveDefault(e.target.checked)} className="accent-teal-600" />
                    Save as my default return address
                </label>
            </div>

            {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 mt-4 text-xs text-red-700 flex items-start gap-2">
                    <AlertCircle size={14} className="flex-shrink-0 mt-0.5" /> {submitError}
                </div>
            )}

            <button onClick={handleContinue} disabled={!complete || submitting}
                className="w-full mt-5 py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition disabled:opacity-40 flex items-center justify-center gap-2">
                {submitting ? <><Loader size={16} className="animate-spin" /> Setting Up Your Shipment...</> : <>Continue <ArrowRight size={16} /></>}
            </button>
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════
   STEP 4 — Preview: ITS vs Self Split Shipment
   ═══════════════════════════════════════════════════════════ */
const PREVIEW_TYPE_LABELS = {
    inbound: "Inventory Transfer Service (ITS)",
    selfMirroring: "Self Split Shipment",
    custom: "Build Your Own Boxes",
};
const PREVIEW_TYPE_DESCRIPTIONS = {
    inbound: "Ship to one FC — Walmart redistributes inventory across the network for a fee.",
    selfMirroring: "You split and ship directly to exactly which FC(s) Walmart assigns — no transfer fee.",
    custom: "Skip Walmart's auto-generated split entirely — define your own box packing and submit it directly.",
};

const DestinationItemsList = ({ destination, itemsBySku }) => {
    const lines = destination.shipmentItems || [];
    return (
        <div className="divide-y divide-gray-100">
            {lines.map((line, i) => {
                const local = itemsBySku[line.vendorSku] || {};
                return (
                    <div key={i} className="flex items-center gap-3 py-2.5">
                        <ProductImage src={local.image_url} />
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 truncate">{local.title || line.vendorSku}</p>
                            <p className="text-xs text-gray-400">
                                SKU {line.vendorSku}
                                {local.product_id && ` · ${local.product_id_type || "ID"} ${local.product_id}`}
                            </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                            <p className="text-sm font-bold text-teal-700">{line.itemQty} units</p>
                            {line.netTransferCharge != null && (
                                <p className="text-[10px] text-orange-500">${line.netTransferCharge.toFixed(2)} fee</p>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};


const ShipmentComparisonReportModal = ({ options, itemsBySku, initialType, onClose }) => {
    const [activeType, setActiveType] = useState(initialType || options[0]?.previewType);
    const activeOption = options.find((o) => o.previewType === activeType) || options[0];
    const destinations = activeOption?.preview || [];

    const flatRows = destinations.flatMap((dest) =>
        (dest.shipmentItems || []).map((line) => ({
            destinationLabel: dest.shipToAddress?.fcName || dest.shipNode,
            destinationCity: formatShipToAddress(dest.shipToAddress),
            isNonSortable: dest.isNonSortable,
            ...line,
            local: itemsBySku[line.vendorSku] || {},
        }))
    );

    const exportToCSV = () => {
        const headers = ["Destination", "City", "Item Name", "SKU", "Product ID", "Qty", "Transfer Fee"];
        const rows = flatRows.map((r) => [
            r.destinationLabel, r.destinationCity, (r.local.title || "").replace(/"/g, '""'),
            r.vendorSku, r.productId || r.local.product_id || "", r.itemQty, r.netTransferCharge ?? "",
        ].map((v) => `"${v ?? ""}"`).join(","));
        const csv = [headers.join(","), ...rows].join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `wfs_shipment_${activeType}_report.csv`; a.click();
        URL.revokeObjectURL(url);
    };

    const printPDF = () => {
        const win = window.open("", "_blank");
        const html = `
        <!DOCTYPE html><html><head><title>WFS Shipment Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; color: #1f2937; }
          h1 { color: #0071ce; margin-bottom: 4px; }
          h2 { color: #374151; margin-top: 24px; margin-bottom: 8px; border-bottom: 2px solid #e5e7eb; padding-bottom: 4px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
          th { background: #0071ce; color: white; padding: 8px 12px; text-align: left; font-size: 13px; }
          td { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; font-size: 12px; vertical-align: middle; }
          tr:nth-child(even) { background: #f9fafb; }
          img { width: 60px; height: 60px; object-fit: contain; }
          .no-img { width: 40px; height: 40px; background: #f3f4f6; display: inline-block; border-radius: 4px; }
          .meta { color: #6b7280; font-size: 12px; margin-bottom: 24px; }
        </style></head><body>
        <h1>📦 WFS Shipment Report — ${PREVIEW_TYPE_LABELS[activeType] || activeType}</h1>
        <p class="meta">Generated: ${new Date().toLocaleString()} &nbsp;|&nbsp; ${destinations.length} destination(s) &nbsp;|&nbsp; ${flatRows.length} item lines</p>
        ${destinations.map((dest) => `
          <h2>${dest.shipToAddress?.fcName || dest.shipNode} — ${formatShipToAddress(dest.shipToAddress)}${dest.totalNetTransferCharge != null ? ` (Fee: $${dest.totalNetTransferCharge.toFixed(2)})` : ""}</h2>
          <table>
            <thead><tr><th style="width:70px;">Image</th><th>Item Name</th><th style="width:150px;">SKU</th><th style="width:140px;">Product ID</th><th style="width:60px;">Qty</th><th style="width:80px;">Fee</th></tr></thead>
            <tbody>
              ${(dest.shipmentItems || []).map((line) => {
            const local = itemsBySku[line.vendorSku] || {};
            return `<tr>
                  <td>${local.image_url ? `<img src="${local.image_url}" />` : '<span class="no-img"></span>'}<div style="font-weight:bold;font-size:16px;color:#0071ce;text-align:center;margin-top:2px;">QTY ${line.itemQty}</div></td>
                  <td>${local.title || line.vendorSku}</td>
                  <td style="font-family:monospace;">${line.vendorSku}</td>
                  <td style="font-family:monospace;">${line.productId || local.product_id || "—"}</td>
                  <td style="font-weight:bold;font-size:15px;">${line.itemQty}</td>
                  <td>${line.netTransferCharge != null ? `$${line.netTransferCharge.toFixed(2)}` : "—"}</td>
                </tr>`;
        }).join("")}
            </tbody>
          </table>
        `).join("")}
        </body></html>`;
        win.document.write(html);
        win.document.close();
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-4 flex items-center justify-between flex-shrink-0" style={{ background: "#0071ce" }}>
                    <div>
                        <h2 className="text-white font-bold text-lg">📦 Shipment Comparison Report</h2>
                        <p className="text-blue-100 text-xs mt-0.5">{destinations.length} destination(s) · {flatRows.length} item line(s)</p>
                    </div>
                    <button onClick={onClose} className="text-white/70 hover:text-white"><X size={22} /></button>
                </div>

                {options.length > 1 && (
                    <div className="flex gap-1 px-6 pt-3 flex-shrink-0 border-b border-gray-100">
                        {options.map((o) => (
                            <button key={o.previewType} onClick={() => setActiveType(o.previewType)}
                                className={`px-4 py-2 text-sm font-bold border-b-2 transition ${activeType === o.previewType ? "border-teal-500 text-teal-700" : "border-transparent text-gray-400 hover:text-gray-600"}`}>
                                {PREVIEW_TYPE_LABELS[o.previewType] || o.previewType}
                            </button>
                        ))}
                    </div>
                )}

                <div className="px-6 py-3 flex items-center gap-3 flex-shrink-0 bg-gray-50 border-b border-gray-100">
                    <button onClick={exportToCSV} className="px-4 py-2 rounded-lg text-xs font-bold text-white" style={{ background: "#0071ce" }}>
                        ⬇ Export to CSV
                    </button>
                    <button onClick={printPDF} className="px-4 py-2 rounded-lg text-xs font-bold text-white" style={{ background: "#7c3aed" }}>
                        🖨 Print / Save PDF
                    </button>
                </div>

                <div className="overflow-y-auto flex-1">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0" style={{ background: "#1e3a5f" }}>
                            <tr>
                                <th className="p-2.5 text-left text-white text-xs w-16">Image</th>
                                <th className="p-2.5 text-left text-white text-xs">Destination</th>
                                <th className="p-2.5 text-left text-white text-xs">Item</th>
                                <th className="p-2.5 text-left text-white text-xs">SKU</th>
                                <th className="p-2.5 text-left text-white text-xs">Product ID</th>
                                <th className="p-2.5 text-left text-white text-xs w-16">Qty</th>
                                <th className="p-2.5 text-left text-white text-xs w-20">Fee</th>
                            </tr>
                        </thead>
                        <tbody>
                            {flatRows.map((r, i) => (
                                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                    <td className="p-2.5"><ProductImage src={r.local.image_url} /></td>
                                    <td className="p-2.5 text-xs text-gray-500 whitespace-nowrap">
                                        {r.destinationLabel}{r.isNonSortable && <span className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded bg-amber-100 text-amber-700">NS</span>}
                                        <br /><span className="text-gray-400">{r.destinationCity}</span>
                                    </td>
                                    <td className="p-2.5 text-xs text-gray-800">{r.local.title || r.vendorSku}</td>
                                    <td className="p-2.5 text-xs font-mono text-gray-600">{r.vendorSku}</td>
                                    <td className="p-2.5 text-xs font-mono text-purple-600">{r.productId || r.local.product_id || "—"}</td>
                                    <td className="p-2.5 text-xs font-bold text-teal-700">{r.itemQty}</td>
                                    <td className="p-2.5 text-xs text-orange-500">{r.netTransferCharge != null ? `$${r.netTransferCharge.toFixed(2)}` : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const CustomSplitUnlockModal = ({ onConfirm, onCancel }) => (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onCancel}>
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-50 text-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <AlertCircle size={20} />
                </div>
                <div>
                    <h2 className="font-bold text-gray-800 mb-1">Before you enable Build Your Own Boxes</h2>
                    <p className="text-xs text-gray-500 leading-relaxed">
                        Only works if your Seller Center has Walmart's <strong>new</strong> shipping experience —
                        where walmart.com lets <strong>you</strong> decide what goes in each box.
                    </p>
                    <p className="text-xs text-gray-500 leading-relaxed mt-2">
                        On the older version, Walmart silently ignores your box plan and falls back to its own
                        default split. The shipment still goes through — your boxes just won't be respected.
                    </p>
                </div>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 mb-5 text-xs text-purple-700">
                <strong>Only continue if you've confirmed</strong> (by starting a shipment directly on walmart.com)
                that your account shows the new "build your own boxes" experience.
            </div>
            <div className="flex gap-2">
                <button onClick={onCancel}
                    className="flex-1 py-2.5 border-2 border-gray-200 text-gray-500 rounded-lg text-sm font-bold hover:bg-gray-50 transition">
                    Cancel
                </button>
                <button onClick={onConfirm}
                    className="flex-1 py-2.5 bg-purple-600 text-white rounded-lg text-sm font-bold hover:bg-purple-700 transition">
                    Yes, I Have the New Version
                </button>
            </div>
        </div>
    </div>
);

export const PreviewStep = ({ shipmentId, onNext, onBack }) => {
    const [preview, setPreview] = useState(null);
    const [itemsBySku, setItemsBySku] = useState({});
    const [error, setError] = useState("");
    const [itemErrors, setItemErrors] = useState(null); // Walmart-rejected SKUs, or null
    const [loading, setLoading] = useState(true);
    const [choice, setChoice] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [removingSku, setRemovingSku] = useState(null);
    const [expandedKeys, setExpandedKeys] = useState(new Set()); // set of `${previewType}-${idx}`, each independent
    const [reportOpen, setReportOpen] = useState(false);
    const [customSplitUnlocked, setCustomSplitUnlocked] = useState(
        () => localStorage.getItem(CUSTOM_SPLIT_UNLOCKED_KEY) === "true"
    );
    const [showUnlockModal, setShowUnlockModal] = useState(false);

    const loadPreview = async () => {
        setLoading(true);
        setError("");
        setItemErrors(null);
        try {
            // Fetch the shipment's own items FIRST and independently of the
            // preview call — we need itemsBySku (for matching Walmart's
            // rejected SKUs back to a removable row) regardless of whether
            // the preview call below succeeds or fails.
            const shipmentData = await getWfsShipment(shipmentId);
            const map = {};
            (shipmentData.shipment?.wfs_shipment_items || []).forEach((it) => { map[it.sku] = it; });
            setItemsBySku(map);

            const previewData = await fetchShipmentPreview(shipmentId);
            setPreview(previewData);
        } catch (e) {
            if (e.itemErrors) {
                setItemErrors(e.itemErrors);
            } else {
                setError(e.message || "Failed to fetch shipment preview");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadPreview(); }, [shipmentId]);

    const handleRemoveItem = async (sku, itemId) => {
        setRemovingSku(sku);
        try {
            await removeShipmentItem(shipmentId, itemId);
            await loadPreview();
        } catch (e) {
            setError(e.message || "Failed to remove item");
        } finally {
            setRemovingSku(null);
        }
    };

    const options = (preview?.payload?.[0]?.previews) || [];
    // Custom Split isn't something Walmart's preview call returns — it's a
    // third path we offer alongside whatever ITS/selfMirroring options came
    // back. Only actually usable once the seller has confirmed (one time,
    // remembered via localStorage) that their account has Walmart's new
    // seller-defined box-split experience — otherwise Walmart silently
    // ignores the box plan and falls back to its own default split.
    const hasSelfMirroring = options.some((o) => o.previewType === 'selfMirroring');
    const allOptions = (customSplitUnlocked && hasSelfMirroring)
        ? [...options, { previewType: 'custom', preview: [] }]
        : [...options];

    const handleSelect = async (previewType) => {
        if (previewType === 'custom') {
            onNext('custom');
            return;
        }
        setSubmitting(true);
        try {
            await selectShipmentPreview(shipmentId, previewType);
            onNext(previewType);
        } catch (e) {
            setError(e.message || "Failed to save selection");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="max-w-4xl mx-auto py-10 px-6 flex items-center gap-2 text-gray-400"><Loader size={18} className="animate-spin" /> Getting shipment preview from Walmart...</div>;

    if (itemErrors) return (
        <div className="max-w-lg mx-auto py-10 px-6">
            <button onClick={onBack} className="text-gray-400 hover:text-gray-600 mb-4 flex items-center gap-1 text-sm"><ChevronLeft size={16} /> Back</button>
            <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-start gap-2 mb-4">
                    <AlertCircle size={20} className="text-orange-500 flex-shrink-0 mt-0.5" />
                    <div>
                        <h2 className="font-bold text-gray-800">Walmart flagged {itemErrors.length} item{itemErrors.length === 1 ? "" : "s"}</h2>
                        <p className="text-xs text-gray-500 mt-0.5">These SKUs aren't currently recognized in your WFS catalog. Remove them from this shipment, or go fix and resubmit them, then retry.</p>
                    </div>
                </div>

                <div className="space-y-2 mb-5">
                    {itemErrors.map((ie) => {
                        return (
                            <div key={ie.sku} className="border border-orange-200 bg-orange-50 rounded-xl p-3 text-xs">
                                <p className="font-bold text-gray-800 mb-0.5">{ie.item_desc || ie.sku}</p>
                                <p className="text-gray-500 mb-1">SKU {ie.sku}</p>
                                {ie.messages.map((m, i) => <p key={i} className="text-orange-600">{m}</p>)}
                            </div>
                        );
                    })}
                </div>

                <p className="text-xs text-gray-400 mb-3">
                    To fix an item: go to <span className="font-semibold">Batches</span>, find its WFS submission, and resubmit it — then come back and retry.
                </p>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-3 text-xs text-blue-700 flex items-start gap-2">
                    <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                    <span>
                        <strong>Hint:</strong> Sometimes this fixes itself on Walmart's side. Before changing anything, try hitting Retry once or twice.
                    </span>
                </div>

                <div className="flex flex-col gap-2">
                    <button onClick={loadPreview} className="w-full py-2.5 bg-teal-600 text-white rounded-lg text-sm font-bold hover:bg-teal-700 transition">
                        Retry Preview
                    </button>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                    <p className="text-xs font-semibold text-gray-500">Or remove the problem item(s) and continue without them:</p>
                    {itemErrors.map((ie) => {
                        const localItem = Object.values(itemsBySku).find((v) => v.sku === ie.sku);
                        return (
                            <button key={ie.sku} onClick={() => localItem && handleRemoveItem(ie.sku, localItem.id)}
                                disabled={!localItem || removingSku === ie.sku}
                                className="w-full flex items-center justify-between px-3 py-2 border border-gray-200 rounded-lg text-xs hover:bg-gray-50 transition disabled:opacity-50">
                                <span className="text-gray-600">Remove {ie.sku}</span>
                                <span className="text-red-500 font-bold">{removingSku === ie.sku ? "Removing..." : "Remove & Retry"}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    if (error) return (
        <div className="max-w-lg mx-auto py-10 px-6">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm flex items-start gap-2">
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5" /> {error}
            </div>
            <button onClick={onBack} className="mt-4 text-teal-600 text-sm underline">Go back</button>
        </div>
    );

    return (
        <div className="max-w-4xl mx-auto py-10 px-6">
            <div className="flex items-center justify-between mb-4">
                <button onClick={onBack} className="text-gray-400 hover:text-gray-600 flex items-center gap-1 text-sm"><ChevronLeft size={16} /> Back</button>
                <button onClick={() => setReportOpen(true)} disabled={options.length === 0}
                    className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white border-2 border-teal-600 rounded-xl text-sm font-bold shadow-md hover:bg-teal-700 hover:shadow-lg transition disabled:opacity-40 disabled:shadow-none">
                    <Printer size={16} /> View Full Report
                </button>
            </div>
            <h1 className="text-xl font-bold text-gray-800 mb-1">Choose How to Ship</h1>
            <p className="text-gray-500 text-sm mb-6 print:hidden">Compare destinations and fees, and see exactly what's shipping where, before committing.</p>

            {options.some((opt) => (opt.preview || []).some((p) => p.isNonSortable)) && (
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mb-4 flex items-start gap-1.5">
                    <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
                    Some items are oversized/non-sortable and always ship to a separate destination — this applies under both shipping methods.
                </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 print:grid-cols-1">
                {allOptions.map((opt) => {
                    if (opt.previewType === 'custom') {
                        return (
                            <button key="custom" onClick={() => setChoice('custom')}
                                className={`bg-white rounded-2xl shadow-lg p-5 border-2 transition text-left print:hidden ${choice === 'custom' ? "border-teal-500" : "border-transparent"}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-bold text-gray-800">{PREVIEW_TYPE_LABELS.custom}</h3>
                                    {choice === 'custom' && <Check size={18} className="text-teal-600" />}
                                </div>
                                <p className="text-xs text-gray-500">{PREVIEW_TYPE_DESCRIPTIONS.custom}</p>
                            </button>
                        );
                    }
                    const isIts = opt.previewType === "inbound";
                    const totalCharge = (opt.preview || []).reduce((sum, p) => sum + (p.totalNetTransferCharge || 0), 0);
                    return (
                        <div key={opt.previewType}
                            className={`bg-white rounded-2xl shadow-lg p-5 border-2 transition print:shadow-none print:border ${choice === opt.previewType ? "border-teal-500" : "border-transparent"}`}>
                            <button onClick={() => setChoice(opt.previewType)} className="w-full text-left print:cursor-default">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-bold text-gray-800">{PREVIEW_TYPE_LABELS[opt.previewType] || opt.previewType}</h3>
                                    {choice === opt.previewType && <Check size={18} className="text-teal-600 print:hidden" />}
                                </div>
                                <p className="text-xs text-gray-500 mb-4">{PREVIEW_TYPE_DESCRIPTIONS[opt.previewType]}</p>
                            </button>

                            <div className="space-y-2">
                                {(opt.preview || []).map((p, i) => {
                                    const key = `${opt.previewType}-${i}`;
                                    const expanded = expandedKeys.has(key);
                                    const lineCount = (p.shipmentItems || []).length;
                                    return (
                                        <div key={i} className="bg-gray-50 rounded-xl overflow-hidden print:bg-white print:border print:border-gray-200">
                                            <button
                                                onClick={() => setExpandedKeys((prev) => {
                                                    const next = new Set(prev);
                                                    next.has(key) ? next.delete(key) : next.add(key);
                                                    return next;
                                                })}
                                                className="w-full p-3 text-xs text-left print:cursor-default"
                                            >
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="font-semibold text-gray-700 flex items-center gap-1.5">
                                                        <Warehouse size={12} className="text-gray-400" />
                                                        {p.shipToAddress?.fcName || p.shipNode}
                                                        {p.isNonSortable && (
                                                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700" title="This destination handles oversized/non-sortable items — routed here regardless of ITS vs Self Split.">
                                                                Non-Sortable Items
                                                            </span>
                                                        )}
                                                    </span>
                                                    <span className="text-gray-400">{formatShipToAddress(p.shipToAddress)}</span>
                                                </div>
                                                <div className="flex items-center justify-between text-gray-400">
                                                    <span className="text-teal-600 font-semibold underline decoration-dotted print:no-underline print:text-gray-500">
                                                        {lineCount} SKU line{lineCount === 1 ? "" : "s"} {expanded ? "▲" : "▼"}
                                                    </span>
                                                    {p.totalNetTransferCharge != null && <span className="font-semibold text-gray-600">${p.totalNetTransferCharge.toFixed(2)} {p.currencyUnit}</span>}
                                                </div>
                                            </button>
                                            <div className={`px-3 ${expanded ? "block" : "hidden"} print:block print:px-3 print:pb-2`}>
                                                <DestinationItemsList destination={p} itemsBySku={itemsBySku} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {isIts && totalCharge > 0 && (
                                <p className="text-xs font-bold text-orange-600 mt-3">Total ITS fee: ${totalCharge.toFixed(2)}</p>
                            )}
                        </div>
                    );
                })}
            </div>

            {options.length === 0 && <p className="text-gray-400 text-sm">No preview options returned.</p>}

            {!customSplitUnlocked && (
                <button onClick={() => setShowUnlockModal(true)}
                    className="w-full mt-4 py-3 border-2 border-dashed border-purple-300 hover:border-purple-400 rounded-xl text-purple-500 hover:text-purple-600 text-sm font-semibold transition print:hidden">
                    🔓 Have Walmart's new "build your own boxes" experience? Unlock it here
                </button>
            )}

            {showUnlockModal && (
                <CustomSplitUnlockModal
                    onCancel={() => setShowUnlockModal(false)}
                    onConfirm={() => {
                        localStorage.setItem(CUSTOM_SPLIT_UNLOCKED_KEY, "true");
                        setCustomSplitUnlocked(true);
                        setShowUnlockModal(false);
                    }}
                />
            )}

            <button onClick={() => handleSelect(choice)} disabled={!choice || submitting}
                className="w-full mt-6 py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition disabled:opacity-40 flex items-center justify-center gap-2">
                {submitting ? "Saving..." : <>Confirm & Continue <ArrowRight size={16} /></>}
            </button>

            {reportOpen && (
                <ShipmentComparisonReportModal
                    options={options}
                    itemsBySku={itemsBySku}
                    initialType={choice || options[0]?.previewType}
                    onClose={() => setReportOpen(false)}
                />
            )}
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════
   STEP 5 — Confirm & Create on Walmart
   ═══════════════════════════════════════════════════════════ */
const ConfirmCreateStep = ({ shipmentId, onCreated, onBack }) => {
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState(null); // { message, detail? }

    const handleCreate = async () => {
        setCreating(true);
        setError(null);
        try {
            const { shipment } = await createShipmentOnWalmart(shipmentId);
            onCreated(shipment);
        } catch (e) {
            setError({ message: e.message, detail: e.detail });
        } finally {
            setCreating(false);
        }
    };

    return (
        <div className="max-w-lg mx-auto py-10 px-6 text-center">
            <button onClick={onBack} className="text-gray-400 hover:text-gray-600 mb-6 flex items-center gap-1 text-sm"><ChevronLeft size={16} /> Back</button>
            <div className="bg-white rounded-2xl shadow-lg p-8">
                <Truck size={40} className="text-teal-600 mx-auto mb-4" />
                <h1 className="text-xl font-bold text-gray-800 mb-2">Ready to Create Your Shipment</h1>
                <p className="text-gray-500 text-sm mb-6">This sends your Inbound Order to Walmart. You'll get shipment IDs, expected delivery dates, and a full prep report next.</p>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-left text-xs text-red-700">
                        <p className="font-bold mb-1 flex items-center gap-1"><AlertCircle size={14} /> {error.message}</p>
                        {error.detail && <pre className="whitespace-pre-wrap text-[10px] text-red-500 max-h-32 overflow-y-auto">{JSON.stringify(error.detail, null, 2)}</pre>}
                    </div>
                )}

                <button onClick={handleCreate} disabled={creating}
                    className="w-full py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2">
                    {creating ? <><Loader size={16} className="animate-spin" /> Creating on Walmart...</> : <><Send size={16} /> Create Shipment</>}
                </button>
            </div>
        </div>
    );
};

// Same "pick a saved template or go custom" pattern as the SF label
// package-type picker (OrdersPage.jsx BuyLabelPanel), scoped to just the
// physical box specs (length/width/height/weight) since none of the WFS
// box-entry forms carry a carrier package-type shortname or unit selector.
// One shared localStorage key — a preset saved from ANY box entry point
// (Build Your Own Boxes, packing-time box dims, or Box Configuration) is
// immediately available in all the others.
const WFS_BOX_PRESETS_KEY = "wfs_box_size_presets";

const loadWfsBoxPresets = () => {
    try {
        const raw = localStorage.getItem(WFS_BOX_PRESETS_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
};

const saveWfsBoxPresetToStorage = (preset) => {
    try {
        const list = loadWfsBoxPresets();
        list.push(preset);
        localStorage.setItem(WFS_BOX_PRESETS_KEY, JSON.stringify(list));
        return list;
    } catch {
        return loadWfsBoxPresets();
    }
};

const deleteWfsBoxPresetFromStorage = (id) => {
    try {
        const list = loadWfsBoxPresets().filter((p) => p.id !== id);
        localStorage.setItem(WFS_BOX_PRESETS_KEY, JSON.stringify(list));
        return list;
    } catch {
        return loadWfsBoxPresets();
    }
};

// `box` = the box row's current values (used for "save current as preset").
// `onApply(patch)` fires with { length, width, height, weight } the moment
// a preset is picked — nothing changes when "Custom Box" is selected, so
// switching back to Custom never wipes what's already typed.
const BoxPresetPicker = ({ box, onApply }) => {
    const [presets, setPresets] = useState(() => loadWfsBoxPresets());
    const [selected, setSelected] = useState("");

    const applyPreset = (id) => {
        setSelected(id);
        if (!id) return;
        const p = presets.find((pr) => pr.id === id);
        if (p) onApply({ length: p.length, width: p.width, height: p.height, weight: p.weight });
    };

    const saveCurrentAsPreset = () => {
        const name = window.prompt("Name this box preset (e.g. \"Small Poly Mailer\"):");
        if (!name || !name.trim()) return;
        const preset = {
            id: `${Date.now()}`, name: name.trim(),
            length: box.length, width: box.width, height: box.height, weight: box.weight,
        };
        setPresets(saveWfsBoxPresetToStorage(preset));
        setSelected(preset.id);
    };

    const removeSelectedPreset = () => {
        if (!selected) return;
        setPresets(deleteWfsBoxPresetFromStorage(selected));
        setSelected("");
    };

    const dimsComplete = box.length && box.width && box.height && box.weight;

    return (
        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
            <select value={selected} onChange={(e) => applyPreset(e.target.value)}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-[11px] focus:outline-none focus:border-teal-500 bg-white">
                <option value="">Custom Box (enter dimensions)</option>
                {presets.length > 0 && (
                    <optgroup label="Saved Presets">
                        {presets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </optgroup>
                )}
            </select>
            {selected && (
                <button type="button" onClick={removeSelectedPreset} className="text-[10px] text-gray-300 hover:text-red-500 font-bold">
                    Delete Preset
                </button>
            )}
            <button type="button" onClick={saveCurrentAsPreset} disabled={!dimsComplete}
                className="text-[10px] font-bold text-teal-600 hover:text-teal-700 disabled:opacity-40 disabled:hover:text-teal-600 whitespace-nowrap">
                + Save current size as preset
            </button>
        </div>
    );
};

const emptyCustomBox = (shipNode) => ({ ship_node: shipNode, label: "", count: "1", weight: "", length: "", width: "", height: "", items: [], is_mixed_sku: false, mixed_manual: false });

// Auto-sets "mixed" from how many SKUs are actually in the box, unless the
// seller has manually toggled the checkbox for this box.
const withAutoMixed = (box) => (box.mixed_manual ? box : { ...box, is_mixed_sku: box.items.length > 1 });

const CustomSplitStep = ({ shipmentId, onCreated, onBack }) => {
    const [shipmentItems, setShipmentItems] = useState(null);
    const [destinations, setDestinations] = useState(null); // null = loading, [] = none/error
    const [destError, setDestError] = useState("");
    const [boxes, setBoxes] = useState([]);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState(null);
    const [mismatches, setMismatches] = useState(null);
    const [showEqualSplit, setShowEqualSplit] = useState(false);
    const [equalSplitCount, setEqualSplitCount] = useState("2");
    const [preview, setPreview] = useState(null); // { destinations: [...] }
    const [previewSig, setPreviewSig] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [previewError, setPreviewError] = useState("");

    useEffect(() => {
        getWfsShipment(shipmentId).then((d) => setShipmentItems(d.shipment?.wfs_shipment_items || []));
        getSelfMirroringDestinations(shipmentId)
            .then((d) => setDestinations(d.destinations || []))
            .catch((e) => { setDestinations([]); setDestError(e.message || "Couldn't load the destination split."); });
    }, [shipmentId]);

    // Seed one box on the first available node once destinations arrive.
    useEffect(() => {
        if (destinations && destinations.length > 0 && boxes.length === 0) {
            setBoxes([emptyCustomBox(destinations[0].ship_node)]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [destinations]);

    const destByNode = useMemo(
        () => Object.fromEntries((destinations || []).map((d) => [d.ship_node, d])),
        [destinations]
    );

    const updateBox = (idx, field, val) => setBoxes((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx], [field]: val };
        return next;
    });

    const addBox = () => setBoxes((prev) => [...prev, emptyCustomBox(destinations?.[0]?.ship_node)]);
    const removeBox = (idx) => setBoxes((prev) => prev.filter((_, i) => i !== idx));
    const duplicateBox = (idx) => setBoxes((prev) => {
        const copy = { ...prev[idx], items: prev[idx].items.map((i) => ({ ...i })) };
        const next = [...prev];
        next.splice(idx + 1, 0, copy);
        return next;
    });

    const setBoxItemQty = (boxIdx, sku, qty) => setBoxes((prev) => {
        const next = [...prev];
        const box = { ...next[boxIdx] };
        const items = box.items.filter((i) => i.sku !== sku);
        if (qty && parseInt(qty, 10) > 0) items.push({ sku, qty_per_box: qty });
        box.items = items;
        next[boxIdx] = withAutoMixed(box);
        return next;
    });

    // Running totals per SKU across EVERY box, regardless of which node
    // it's assigned to — which node gets how much is entirely up to the
    // seller now; the only thing that must add up is the shipment total.
    const totalsBySku = useMemo(() => {
        const totals = {};
        for (const b of boxes) {
            const count = parseInt(b.count, 10) || 0;
            for (const i of b.items) {
                totals[i.sku] = (totals[i.sku] || 0) + (parseInt(i.qty_per_box, 10) || 0) * count;
            }
        }
        return totals;
    }, [boxes]);

    // Fills in whatever's left unaccounted for this SKU (across every OTHER
    // box, on any node) into THIS box.
    const addRemainingToBox = (boxIdx, sku, requestedQty) => setBoxes((prev) => {
        const box = prev[boxIdx];
        const count = parseInt(box.count, 10) || 1;
        const currentContribution = (parseInt(box.items.find((i) => i.sku === sku)?.qty_per_box, 10) || 0) * count;
        const accountedElsewhere = (totalsBySku[sku] || 0) - currentContribution;
        const remaining = requestedQty - accountedElsewhere;
        if (remaining <= 0) return prev;
        const perBoxQty = Math.max(1, Math.floor(remaining / count));
        return prev.map((b, i) => {
            if (i !== boxIdx) return b;
            const items = b.items.filter((it) => it.sku !== sku);
            items.push({ sku, qty_per_box: String(perBoxQty) });
            return withAutoMixed({ ...b, items });
        });
    });

    // Divides every SKU's total quantity evenly across N boxes, round-
    // robining a real destination node onto each box so the result is a
    // usable multi-node starting point — the seller can still reassign
    // any box to a different node afterward via its Ship To dropdown.
    const applyEqualSplit = () => {
        if (!destinations || destinations.length === 0) return;
        const n = Math.max(1, parseInt(equalSplitCount, 10) || 1);
        const newBoxes = Array.from({ length: n }, (_, i) => ({
            ...emptyCustomBox(destinations[i % destinations.length].ship_node), items: [],
        }));
        shipmentItems.forEach((it) => {
            // Same amount in every box this product goes into (Walmart rule):
            // use the largest k <= n that divides the quantity evenly.
            let k = Math.min(n, it.item_qty);
            while (k > 1 && it.item_qty % k !== 0) k--;
            const perBox = it.item_qty / k;
            for (let i = 0; i < k; i++) newBoxes[i].items.push({ sku: it.sku, qty_per_box: String(perBox) });
        });
        setBoxes(newBoxes.map(withAutoMixed));
        setShowEqualSplit(false);
    };

    const boxInvalid = (b) => !b.ship_node || !b.count || parseInt(b.count, 10) <= 0
        || !b.weight || parseFloat(b.weight) <= 0
        || !b.length || parseFloat(b.length) <= 0
        || !b.width || parseFloat(b.width) <= 0
        || !b.height || parseFloat(b.height) <= 0
        || b.items.length === 0;

    const allSkusMatched = shipmentItems?.every((it) => (totalsBySku[it.sku] || 0) === it.item_qty);
    // Walmart allows ONE units-per-box amount per product per shipment.
    const unevenSkus = (shipmentItems || []).filter((it) => {
        const sizes = new Set();
        boxes.forEach((b) => b.items.forEach((i) => {
            const q = parseInt(i.qty_per_box, 10);
            if (i.sku === it.sku && q > 0) sizes.add(q);
        }));
        return sizes.size > 1;
    });
    const canSubmit = shipmentItems && destinations && destinations.length > 0
        && boxes.length > 0 && !boxes.some(boxInvalid) && allSkusMatched;

    const buildBoxPayload = () => boxes.map((b) => ({
        ship_node: b.ship_node, label: b.label, count: parseInt(b.count, 10),
        weight: parseFloat(b.weight), length: parseFloat(b.length),
        width: parseFloat(b.width), height: parseFloat(b.height),
        is_mixed_sku: b.is_mixed_sku !== false,
        items: b.items.map((i) => ({ sku: i.sku, qty_per_box: parseInt(i.qty_per_box, 10) })),
    }));
    const boxesSig = JSON.stringify(boxes);
    const previewStale = !!preview && previewSig !== boxesSig;
    const titleBySku = Object.fromEntries((shipmentItems || []).map((it) => [it.sku, truncateTitle(it.title || it.sku, 30)]));

    const runPreview = async () => {
        setPreviewing(true);
        setPreviewError("");
        try {
            const res = await previewCustomSplit(shipmentId, buildBoxPayload());
            setPreview(res);
            setPreviewSig(boxesSig);
        } catch (e) {
            setPreviewError(e.message || "Walmart preview failed");
        } finally {
            setPreviewing(false);
        }
    };

    const handleCreate = async () => {
        setCreating(true);
        setError(null);
        setMismatches(null);
        try {
            const payload = boxes.map((b) => ({
                ship_node: b.ship_node,
                label: b.label, count: parseInt(b.count, 10),
                weight: parseFloat(b.weight), length: parseFloat(b.length),
                width: parseFloat(b.width), height: parseFloat(b.height),
                is_mixed_sku: b.is_mixed_sku !== false,
                items: b.items.map((i) => ({ sku: i.sku, qty_per_box: parseInt(i.qty_per_box, 10) })),
            }));
            const { shipment } = await createCustomSplitShipment(shipmentId, payload);
            onCreated(shipment);
        } catch (e) {
            if (e.mismatches) setMismatches(e.mismatches);
            setError({ message: e.message, detail: e.detail });
        } finally {
            setCreating(false);
        }
    };

    if (!shipmentItems || destinations === null) {
        return <div className="max-w-4xl mx-auto py-10 px-6 flex items-center gap-2 text-gray-400"><Loader size={18} className="animate-spin" /> Loading items...</div>;
    }

    if (destinations.length === 0) {
        return (
            <div className="max-w-lg mx-auto py-10 px-6">
                <button onClick={onBack} className="text-gray-400 hover:text-gray-600 mb-4 flex items-center gap-1 text-sm"><ChevronLeft size={16} /> Back</button>
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
                    {destError || "No multi-destination split is available for this shipment. Go back and re-run the preview."}
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto py-10 px-6">
            <button onClick={onBack} className="text-gray-400 hover:text-gray-600 mb-4 flex items-center gap-1 text-sm"><ChevronLeft size={16} /> Back</button>
            <h1 className="text-xl font-bold text-gray-800 mb-1">Build Your Own Boxes</h1>
            <p className="text-gray-500 text-sm mb-2">
                Design your boxes, then click "See Walmart Preview" to see exactly how Walmart will split them before anything is created. Change boxes and preview again as often as you like.
            </p>
            <p className="text-xs text-gray-400 mb-5">
                Tips: use the same amount of a product in every box it's in so Walmart keeps your boxes exactly. Products in their own boxes can go to different warehouses, while mixed boxes ship together. Some products can only go to one warehouse, and the preview will show that.
            </p>

            <div className="relative mb-5">
                <button onClick={() => setShowEqualSplit((o) => !o)}
                    className="px-4 py-2 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg text-xs font-bold hover:bg-purple-100 transition">
                    Equal Split - divide everything evenly across N boxes
                </button>
                {showEqualSplit && (
                    <div className="absolute z-20 top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-3 w-64">
                        <p className="text-[10px] font-bold text-gray-500 uppercase mb-1.5">How many boxes?</p>
                        <input type="number" min="1" autoFocus value={equalSplitCount}
                            onChange={(e) => setEqualSplitCount(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && applyEqualSplit()}
                            onWheel={preventWheelChange}
                            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500 mb-2" />
                        <p className="text-[10px] text-gray-400 mb-2">This replaces your current box plan. Boxes are assigned round-robin across your available destinations — you can reassign any box afterward.</p>
                        <div className="flex gap-1.5">
                            <button onClick={applyEqualSplit} className="flex-1 py-1.5 bg-purple-600 text-white rounded-lg text-[11px] font-bold hover:bg-purple-700 transition">
                                Apply
                            </button>
                            <button onClick={() => setShowEqualSplit(false)} className="px-3 py-1.5 text-gray-400 hover:text-gray-600 text-[11px] font-bold">
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 mb-5">
                <p className="text-xs font-bold text-gray-500 uppercase mb-2">Units Accounted For (shipment total)</p>
                <div className="space-y-1">
                    {shipmentItems.map((it) => {
                        const boxed = totalsBySku[it.sku] || 0;
                        const ok = boxed === it.item_qty;
                        return (
                            <div key={it.sku} className="flex items-center justify-between text-xs">
                                <span className="text-gray-600 truncate max-w-[240px]">{it.title || it.sku} <span className="text-gray-400 font-mono">({it.sku})</span></span>
                                <span className={`font-bold ${ok ? "text-green-600" : "text-orange-600"}`}>{boxed} / {it.item_qty}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {mismatches && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-5 text-xs text-red-700">
                    <p className="font-bold mb-1 flex items-center gap-1"><AlertCircle size={12} /> Box plan doesn't match requested quantities</p>
                    {mismatches.map((m) => <p key={m.sku}>{m.sku}: requested {m.requested}, boxed {m.boxed}</p>)}
                </div>
            )}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-5 text-xs text-red-700">
                    <p className="font-bold flex items-center gap-1"><AlertCircle size={12} /> {error.message}</p>
                    {error.detail && <pre className="whitespace-pre-wrap text-[10px] text-red-500 max-h-32 overflow-y-auto mt-1">{JSON.stringify(error.detail, null, 2)}</pre>}
                </div>
            )}

            <div className="space-y-3 mb-5">
                {boxes.map((b, idx) => (
                    <div key={idx} className="bg-white rounded-2xl shadow p-4">
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                            <input value={b.label} onChange={(e) => updateBox(idx, "label", e.target.value)}
                                placeholder={`Box type ${idx + 1} (optional label)`}
                                className="text-sm font-semibold border-b border-dashed border-gray-200 focus:outline-none focus:border-teal-500 flex-1 mr-2 min-w-[120px]" />
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="text-[10px] font-bold text-gray-400 uppercase">Box type {idx + 1}</span>
                                <button onClick={() => duplicateBox(idx)} title="Duplicate this box type"
                                    className="text-[10px] font-bold text-teal-600 hover:text-teal-700 underline decoration-dotted">
                                    Duplicate
                                </button>
                                {boxes.length > 1 && (
                                    <button onClick={() => removeBox(idx)} className="text-gray-300 hover:text-red-500"><Trash2 size={14} /></button>
                                )}
                            </div>
                        </div>

                        <BoxPresetPicker box={b} onApply={(patch) => setBoxes((prev) => {
                            const next = [...prev];
                            next[idx] = { ...next[idx], ...patch };
                            return next;
                        })} />

                        <div className="grid grid-cols-5 gap-2 mb-3">
                            <div>
                                <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Boxes</p>
                                <input type="number" min="1" value={b.count} onChange={(e) => updateBox(idx, "count", e.target.value)}
                                    onWheel={preventWheelChange}
                                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                            </div>
                            <div>
                                <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Length</p>
                                <input type="number" step="0.1" value={b.length} onChange={(e) => updateBox(idx, "length", e.target.value)}
                                    onWheel={preventWheelChange}
                                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                            </div>
                            <div>
                                <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Width</p>
                                <input type="number" step="0.1" value={b.width} onChange={(e) => updateBox(idx, "width", e.target.value)}
                                    onWheel={preventWheelChange}
                                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                            </div>
                            <div>
                                <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Height</p>
                                <input type="number" step="0.1" value={b.height} onChange={(e) => updateBox(idx, "height", e.target.value)}
                                    onWheel={preventWheelChange}
                                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                            </div>
                            <div>
                                <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Weight (lb)</p>
                                <input type="number" step="0.1" value={b.weight} onChange={(e) => updateBox(idx, "weight", e.target.value)}
                                    onWheel={preventWheelChange}
                                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                            </div>
                        </div>

                        <label className="flex items-center gap-1.5 text-[14px] text-black mb-3">
                            <input type="checkbox" checked={b.is_mixed_sku === true}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setBoxes((prev) => prev.map((x, i) => (i === idx ? { ...x, is_mixed_sku: checked, mixed_manual: true } : x)));
                                }}
                                className="accent-teal-600 h-4 w-4" />
                            This box contains more than one SKU (mixed)
                            {!b.mixed_manual && <span className="text-[10px] text-gray-400">(auto)</span>}
                        </label>

                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-1.5">Units per box (for each SKU going in this box type)</p>
                        <div className="space-y-1.5">
                            {shipmentItems.map((it) => {
                                const existing = b.items.find((i) => i.sku === it.sku);
                                return (
                                    <div key={it.sku} className="flex items-center gap-2">
                                        <ProductImage src={it.image_url} />
                                        <span className="text-xs text-gray-600 flex-1 truncate">{it.title || it.sku} <span className="text-gray-400 font-mono">({it.sku})</span></span>
                                        <input type="number" min="0" placeholder="0" value={existing?.qty_per_box || ""}
                                            onChange={(e) => setBoxItemQty(idx, it.sku, e.target.value)}
                                            onWheel={preventWheelChange}
                                            className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-teal-500" />
                                        <button onClick={() => addRemainingToBox(idx, it.sku, it.item_qty)}
                                            className="text-[10px] font-bold text-teal-600 hover:text-teal-700 underline decoration-dotted whitespace-nowrap">
                                            Add remaining
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            <button onClick={addBox} className="w-full mb-5 py-2.5 border-2 border-dashed border-gray-300 hover:border-teal-400 rounded-xl text-gray-500 hover:text-teal-600 text-sm font-semibold transition">
                + Add another box type
            </button>

            <div className="bg-white rounded-2xl shadow p-4 mb-5">
                <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold text-gray-600 uppercase">Walmart Preview</p>
                    <button onClick={runPreview} disabled={!canSubmit || previewing}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 transition disabled:opacity-40">
                        {previewing ? "Asking Walmart..." : preview ? "Refresh Preview" : "See Walmart Preview"}
                    </button>
                </div>
                {!canSubmit && <p className="text-[11px] text-gray-400">Finish your boxes (every unit assigned, dimensions and weight filled) to preview.</p>}
                {unevenSkus.length > 0 && (
                    <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mb-2">
                        Some products have different amounts in different boxes. Walmart allows only one box size per product, so it will repack those into equal smaller boxes. Check the preview.
                    </p>
                )}
                {previewError && <p className="text-xs text-red-500">{previewError}</p>}
                {previewStale && <p className="text-[11px] text-amber-600">You changed your boxes since the last preview. Refresh it to see the new result.</p>}
                {preview && !previewStale && (
                    <div className="space-y-2">
                        <p className="text-[11px] text-gray-500">
                            Walmart will create <strong>{preview.destinations.length}</strong> shipment{preview.destinations.length === 1 ? "" : "s"}. Creating now uses this exact plan.
                        </p>
                        {preview.destinations.map((d, i) => (
                            <div key={i} className="border border-gray-100 rounded-lg p-2.5 text-xs">
                                <p className="font-semibold text-gray-700 mb-1">
                                    {d.ship_to_address?.fcName || d.ship_node} <span className="text-gray-400 font-normal">— {formatShipToAddress(d.ship_to_address)}</span>
                                </p>
                                {(d.boxes || []).map((bx, j) => (
                                    <p key={j} className="text-gray-600">
                                        <span className="font-bold text-teal-700">{bx.noOfBoxes} box{bx.noOfBoxes === 1 ? "" : "es"}</span>, each: {(bx.items || []).map((it) => `${it.unitsPerBox} × ${titleBySku[it.sku] || it.sku}`).join(" + ")}
                                    </p>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <TotalBoxesBar
                total={boxes.reduce((sum, b) => sum + (parseInt(b.count, 10) || 0), 0)}
                shipments={preview && !previewStale ? preview.destinations.length : boxes.length}
                note={preview && !previewStale ? "per Walmart preview" : "run the preview to see real shipments"}
            />

            <button onClick={handleCreate} disabled={!canSubmit || creating || !preview || previewStale}
                className="w-full py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition disabled:opacity-40 flex items-center justify-center gap-2">
                {creating ? <><Loader size={16} className="animate-spin" /> Creating on Walmart...</> : <><Send size={16} /> Create Shipment with This Box Plan</>}
            </button>
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════
   STEP 6 — Prep Report + Checklist
   ═══════════════════════════════════════════════════════════ */

const PrepReportModal = ({ report, onClose }) => {
    const flatRows = report.splits.flatMap((split) =>
        split.lines.map((l) => ({ ...l, destinationLabel: split.ship_to_address?.fcName || split.shipment_id, destinationCity: formatShipToAddress(split.ship_to_address) }))
    );

    const exportToCSV = () => {
        const headers = ["Destination", "City", "Item Name", "UPC/GTIN", "SKU", "Qty", "Case Pack", "Boxes", "Weight", "Dims (LxWxH)"];
        const rows = flatRows.map((r) => [
            r.destinationLabel, r.destinationCity, (r.item_desc || "").replace(/"/g, '""'), r.product_id, r.sku,
            r.item_qty, r.vendor_pack_qty, r.boxes_needed, r.weight,
            r.dimensions ? `${r.dimensions.length}x${r.dimensions.width}x${r.dimensions.height}` : "",
        ].map((v) => `"${v ?? ""}"`).join(","));
        const csv = [headers.join(","), ...rows].join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `wfs_prep_report_${report.inbound_order_id}.csv`; a.click();
        URL.revokeObjectURL(url);
    };

    const printPDF = () => {
        const win = window.open("", "_blank");
        const html = `
        <!DOCTYPE html><html><head><title>WFS Prep Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; color: #1f2937; }
          h1 { color: #0071ce; margin-bottom: 4px; }
          h2 { color: #374151; margin-top: 24px; margin-bottom: 8px; border-bottom: 2px solid #e5e7eb; padding-bottom: 4px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
          th { background: #0071ce; color: white; padding: 8px 12px; text-align: left; font-size: 13px; }
          td { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; font-size: 12px; vertical-align: middle; }
          tr:nth-child(even) { background: #f9fafb; }
          img { width: 60px; height: 60px; object-fit: contain; }
          .no-img { width: 40px; height: 40px; background: #f3f4f6; display: inline-block; border-radius: 4px; }
          .meta { color: #6b7280; font-size: 12px; margin-bottom: 24px; }
          .totals { text-align: right; font-weight: bold; color: #0071ce; font-size: 12px; margin-bottom: 20px; }
        </style></head><body>
        <h1>📦 WFS Prep Report</h1>
        <p class="meta">Inbound Order ${report.inbound_order_id} &nbsp;|&nbsp; Generated: ${new Date().toLocaleString()} &nbsp;|&nbsp; ${report.splits.length} destination(s) &nbsp;|&nbsp; ${report.grand_total_units} units</p>
        ${report.splits.map((split) => `
          <h2>${split.ship_to_address?.fcName || split.shipment_id} — ${formatShipToAddress(split.ship_to_address)}</h2>
          <table>
            <thead><tr><th style="width:60px;">Image</th><th>Item Name</th><th style="width:120px;">UPC/GTIN</th><th style="width:120px;">SKU</th><th style="width:50px;">Qty</th><th style="width:60px;">Case Pack</th><th style="width:50px;">Boxes</th><th style="width:70px;">Weight</th><th style="width:110px;">Dims</th></tr></thead>
            <tbody>
              ${split.lines.map((l) => `<tr>
                <td>${l.image_url ? `<img src="${l.image_url}" />` : '<span class="no-img"></span>'}<div style="font-weight:bold;font-size:16px;color:#0071ce;text-align:center;margin-top:2px;">QTY ${l.item_qty}</div></td>
                <td>${l.item_desc}</td>
                <td style="font-family:monospace;">${l.product_id || "—"}</td>
                <td style="font-family:monospace;">${l.sku}</td>
                <td style="font-weight:bold;font-size:15px;">${l.item_qty}</td>
                <td>${l.vendor_pack_qty}</td>
                <td><strong>${l.boxes_needed}</strong></td>
                <td>${l.weight ? `${l.weight} lb` : "—"}</td>
                <td>${l.dimensions ? `${l.dimensions.length}×${l.dimensions.width}×${l.dimensions.height}"` : "—"}</td>
              </tr>`).join("")}
            </tbody>
          </table>
          <p class="totals">${split.total_units} units for this destination</p>
        `).join("")}
        </body></html>`;
        win.document.write(html);
        win.document.close();
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-4 flex items-center justify-between flex-shrink-0" style={{ background: "#0071ce" }}>
                    <div>
                        <h2 className="text-white font-bold text-lg">📦 Prep Report</h2>
                        <p className="text-blue-100 text-xs mt-0.5">{report.inbound_order_id} · {report.splits.length} destination(s) · {report.grand_total_units} units</p>
                    </div>
                    <button onClick={onClose} className="text-white/70 hover:text-white"><X size={22} /></button>
                </div>

                <div className="px-6 py-3 flex items-center gap-3 flex-shrink-0 bg-gray-50 border-b border-gray-100">
                    <button onClick={exportToCSV} className="px-4 py-2 rounded-lg text-xs font-bold text-white" style={{ background: "#0071ce" }}>
                        ⬇ Export to CSV
                    </button>
                    <button onClick={printPDF} className="px-4 py-2 rounded-lg text-xs font-bold text-white" style={{ background: "#7c3aed" }}>
                        🖨 Print / Save PDF
                    </button>
                </div>

                <div className="overflow-y-auto flex-1">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0" style={{ background: "#1e3a5f" }}>
                            <tr>
                                <th className="p-2.5 text-left text-white text-xs w-16">Image</th>
                                <th className="p-2.5 text-left text-white text-xs">Destination</th>
                                <th className="p-2.5 text-left text-white text-xs">Item</th>
                                <th className="p-2.5 text-left text-white text-xs">UPC/GTIN</th>
                                <th className="p-2.5 text-left text-white text-xs">SKU</th>
                                <th className="p-2.5 text-left text-white text-xs w-14">Qty</th>
                                <th className="p-2.5 text-left text-white text-xs w-16">Boxes</th>
                                <th className="p-2.5 text-left text-white text-xs w-20">Weight</th>
                                <th className="p-2.5 text-left text-white text-xs w-28">Dims</th>
                            </tr>
                        </thead>
                        <tbody>
                            {flatRows.map((r, i) => (
                                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                    <td className="p-2.5"><ProductImage src={r.image_url} /></td>
                                    <td className="p-2.5 text-xs text-gray-500 whitespace-nowrap">{r.destinationLabel}<br /><span className="text-gray-400">{r.destinationCity}</span></td>
                                    <td className="p-2.5 text-xs text-gray-800" title={r.item_desc}>{truncateTitle(r.item_desc)}</td>
                                    <td className="p-2.5 text-xs font-mono text-gray-500">{r.product_id || "—"}</td>
                                    <td className="p-2.5 text-xs font-mono text-gray-600">{r.sku}</td>
                                    <td className="p-2.5 text-xs font-bold text-teal-700">{r.item_qty}</td>
                                    <td className="p-2.5 text-xs font-bold text-orange-600">{r.boxes_needed}</td>
                                    <td className="p-2.5 text-xs text-gray-500">{r.weight ? `${r.weight} lb` : "—"}</td>
                                    <td className="p-2.5 text-xs text-gray-500">{r.dimensions ? `${r.dimensions.length}×${r.dimensions.width}×${r.dimensions.height}"` : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

// Makes a distortion curve — the higher `amount`, the more aggressive the overdrive
function makeDistortionCurve(amount) {
    const samples = 44100;
    const curve = new Float32Array(samples);
    for (let i = 0; i < samples; i++) {
        const x = (i * 2) / samples - 1;
        curve[i] = ((3 + amount) * x * 20 * (Math.PI / 180)) / (Math.PI + amount * Math.abs(x));
    }
    return curve;
}

const playErrorBeep = () => {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const shaper = ctx.createWaveShaper();

        osc.type = "square";
        osc.frequency.value = 220;
        shaper.curve = makeDistortionCurve(60); // punch it up
        shaper.oversample = "4x";

        const now = ctx.currentTime;
        gain.gain.setValueAtTime(0.001, now);
        gain.gain.exponentialRampToValueAtTime(0.8, now + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.8, now + 0.2);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

        osc.connect(gain);
        gain.connect(shaper);
        shaper.connect(ctx.destination);

        osc.start(now);
        osc.stop(now + 0.25);
        osc.onended = () => ctx.close();
    } catch { /* audio unavailable — fail silently */ }
};

const playSuccessDing = () => {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const shaper = ctx.createWaveShaper();
        shaper.curve = makeDistortionCurve(15); // gentler, keeps it bright not gritty
        shaper.oversample = "4x";
        shaper.connect(ctx.destination);

        const playTone = (freq, startTime, duration) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.setValueAtTime(freq, startTime);
            gain.gain.setValueAtTime(0.001, startTime);
            gain.gain.exponentialRampToValueAtTime(0.8, startTime + 0.015);
            gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
            osc.connect(gain);
            gain.connect(shaper);
            osc.start(startTime);
            osc.stop(startTime + duration);
        };

        const now = ctx.currentTime;
        playTone(1046.5, now, 0.16);
        playTone(1568, now + 0.1, 0.22);

        setTimeout(() => ctx.close(), 400);
    } catch { /* audio unavailable — fail silently */ }
};
// Barcodes may come in with spaces/dashes from a paste — strip to digits only.
const normalizeCode = (v) => (v || "").replace(/\D/g, "");

// UPC (12 digits) and GTIN (14 digits) for the SAME product often differ
// only by a leading "00" padding — a strict equality check misses these
// entirely. Comparing with "contains either direction" catches both:
// scanned UPC "193052076991" matches stored GTIN "00193052076991", and
// vice versa, without needing to know which one is longer up front.
const codesMatch = (a, b) => {
    if (!a || !b) return false;
    // Guard against short leftover fragments (see processScan/handleScanChange)
    // matching almost anything by pure chance via the substring check below.
    if (a.length < 8 || b.length < 8) return false;
    return a === b || a.includes(b) || b.includes(a);
};

// Packing progress (units scanned/checked per SKU) saved in IndexedDB,
// keyed by "<Inbound Order ID>::<Walmart shipment ID>", so a refresh or
// coming back later restores exactly what was packed. Every call is
// best-effort: if IndexedDB is unavailable, packing just works like before.
const PACKING_DB_NAME = "wfs_packing_progress";
const PACKING_STORE = "progress";

const openPackingDb = () => new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") { reject(new Error("IndexedDB unavailable")); return; }
    const req = indexedDB.open(PACKING_DB_NAME, 1);
    req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(PACKING_STORE)) db.createObjectStore(PACKING_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
});

const loadPackingProgress = async (key) => {
    try {
        const db = await openPackingDb();
        return await new Promise((resolve) => {
            const tx = db.transaction(PACKING_STORE, "readonly");
            const r = tx.objectStore(PACKING_STORE).get(key);
            r.onsuccess = () => resolve(r.result || null);
            r.onerror = () => resolve(null);
            tx.oncomplete = () => db.close();
        });
    } catch {
        return null;
    }
};

const savePackingProgress = async (key, data) => {
    try {
        const db = await openPackingDb();
        const tx = db.transaction(PACKING_STORE, "readwrite");
        tx.objectStore(PACKING_STORE).put(data, key);
        tx.oncomplete = () => db.close();
    } catch { /* non-fatal — progress just isn't remembered */ }
};

const PackingSplitCard = ({ split, inboundOrderId, onBoxesChanged }) => {
    const [packingActive, setPackingActive] = useState(false);
    // sku -> units packed so far for that line (0..item_qty). A line counts
    // as "packed" once its count reaches its own item_qty, not on a single
    // scan — multi-unit lines need one scan per physical unit.
    const [packedCounts, setPackedCounts] = useState({});
    const [scanValue, setScanValue] = useState("");
    const [scanError, setScanError] = useState("");
    const scanInputRef = useRef(null);
    // Debounces processing until a scanner's burst of characters actually
    // finishes, instead of firing the moment digit count crosses 12 (which
    // could still be mid-code for a 13/14-digit barcode).
    const scanDebounceRef = useRef(null);
    // Ignores a repeat of the exact same code scanned again this fast —
    // almost certainly one physical scan firing twice (laser/camera double
    // read), not two real units.
    const lastScanRef = useRef({ code: null, time: 0 });
    // SKU of the most recently successful scan — pinned to the top of the
    // list until the next successful scan replaces it.
    const [lastScannedSku, setLastScannedSku] = useState(null);
    // Only relevant while packingActive — hides fully-packed lines from the
    // table entirely so the seller just sees what's left to do. Off by
    // default; resets to off any time packing is stopped, so reopening
    // this card later always starts showing everything again.
    const [showOnlyRemaining, setShowOnlyRemaining] = useState(false);

    // Box dimensions entered WHILE packing — saved to localStorage per
    // shipment, and read back later by BoxConfigModal as a prefill so the
    // seller never has to re-measure/retype boxes they already entered here.
    // Keyed by split.shipment_id — Walmart's OWN shipment ID, which is
    // already unique per destination (confirmed in Walmart's Get Shipments
    // docs: "shipmentId — Unique ID identifying each shipment"). This is
    // the correct key: it's present both here (report.splits, from the
    // prep-report endpoint) and in BoxConfigModal (a full wfs_shipment_splits
    // row), so entries saved here are reliably found there. An earlier
    // attempt to key this by split.id was wrong — that field only exists
    // on the DB-row shape BoxConfigModal sees, not on the lighter object
    // this component receives from the prep report, so it would have
    // silently broken this entirely (every destination collapsing onto a
    // single "undefined" key) rather than fixing anything.
    const defaultMixed = defaultMixedFromSkus(split.lines);
    const [boxes, setBoxes] = useState(() => getPackedBoxDims(split.shipment_id) || [emptyBox(defaultMixed)]);
    const [showBoxEntry, setShowBoxEntry] = useState(false);

    // Restore saved packing progress for this Inbound Order + shipment.
    const packingProgressKey = `${inboundOrderId || ""}::${split.shipment_id}`;
    const progressLoadedRef = useRef(false);
    useEffect(() => {
        progressLoadedRef.current = false;
        let cancelled = false;
        loadPackingProgress(packingProgressKey).then((saved) => {
            if (cancelled) return;
            if (saved?.packedCounts && typeof saved.packedCounts === "object") {
                // Only keep SKUs still in this destination, capped at each line's qty.
                const clean = {};
                split.lines.forEach((l) => {
                    const n = parseInt(saved.packedCounts[l.sku], 10);
                    if (n > 0) clean[l.sku] = Math.min(n, l.item_qty);
                });
                // Never clobber anything scanned in the moment before the load finished.
                setPackedCounts((prev) => (Object.keys(prev).length > 0 ? prev : clean));
                if (saved.lastScannedSku) setLastScannedSku((prev) => prev || saved.lastScannedSku);
            }
            progressLoadedRef.current = true;
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [packingProgressKey]);

    // Save on every change — but never before the restore above has finished,
    // so the empty initial state can't overwrite real saved progress.
    useEffect(() => {
        if (!progressLoadedRef.current) return;
        savePackingProgress(packingProgressKey, { packedCounts, lastScannedSku, updated_at: Date.now() });
    }, [packedCounts, lastScannedSku, packingProgressKey]);

    // Lets the Prep Report's total box count update live as box dims are typed.
    useEffect(() => { onBoxesChanged?.(); }, [boxes]); // eslint-disable-line react-hooks/exhaustive-deps

    const updateBox = (idx, field, val) => setBoxes((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx], [field]: val };
        savePackedBoxDims(split.shipment_id, next);
        return next;
    });
    const addBox = () => setBoxes((prev) => {
        const next = [...prev, emptyBox(defaultMixed)];
        savePackedBoxDims(split.shipment_id, next);
        return next;
    });
    const removeBox = (idx) => setBoxes((prev) => {
        const next = prev.filter((_, i) => i !== idx);
        savePackedBoxDims(split.shipment_id, next);
        return next;
    });

    useEffect(() => {
        if (packingActive) scanInputRef.current?.focus();
    }, [packingActive]);

    const totalLines = split.lines.length;
    const fullyPackedLines = split.lines.filter((l) => (packedCounts[l.sku] || 0) >= l.item_qty).length;
    const allPacked = totalLines > 0 && fullyPackedLines === totalLines;

    // Three groups, in this order: (1) the line you just scanned; (2)
    // everything else still remaining; (3) everything fully packed.
    const sortedLines = useMemo(() => {
        const rank = (l) => {
            if (l.sku === lastScannedSku) return 0;
            const packed = (packedCounts[l.sku] || 0) >= l.item_qty;
            return packed ? 2 : 1;
        };
        return [...split.lines].sort((a, b) => rank(a) - rank(b));
    }, [split.lines, packedCounts, lastScannedSku]);

    // Same sorted list, optionally with fully-packed lines stripped out —
    // only ever applied when both packing is active AND the toggle is on.
    const displayLines = (packingActive && showOnlyRemaining)
        ? sortedLines.filter((l) => (packedCounts[l.sku] || 0) < l.item_qty)
        : sortedLines;

    // Checkbox stays as a manual full-line toggle: checking it jumps
    // straight to item_qty (fully packed), unchecking resets to 0 — for
    // sellers who'd rather tick a box than scan every single unit.
    const togglePacked = (sku, itemQty) => setPackedCounts((prev) => {
        const current = prev[sku] || 0;
        return { ...prev, [sku]: current >= itemQty ? 0 : itemQty };
    });

    const SCAN_DEBOUNCE_MS = 40; // let a scanner's burst finish before matching, instead of firing on a 12-digit prefix of a 13/14-digit code
    const SCAN_COOLDOWN_MS = 250; // ignore an identical code scanned again this fast

    // Runs the match/count-increment logic against a given raw value —
    // takes the value directly (not from state) since we call this from
    // onChange, before setScanValue's update has actually landed yet.
    const processScan = (raw) => {
        const code = normalizeCode(raw);
        setScanValue("");
        if (!code) return;
        if (code.length < 8) return; // fragment too short to be a real barcode — ignore silently, no beep

        const now = Date.now();
        if (lastScanRef.current.code === code && (now - lastScanRef.current.time) < SCAN_COOLDOWN_MS) {
            return; // same code, too soon — treat as one physical scan double-firing, not a second unit
        }
        lastScanRef.current = { code, time: now };

        const match = split.lines.find((l) => codesMatch(normalizeCode(l.product_id), code));
        if (!match) {
            setScanError(`No item in this shipment matches "${code}".`);
            playErrorBeep();
            return;
        }
        const currentCount = packedCounts[match.sku] || 0;
        if (currentCount >= match.item_qty) {
            setScanError(`${match.item_desc || match.sku} is already fully packed (${match.item_qty}/${match.item_qty}).`);
            playErrorBeep();
            return;
        }
        const newCount = currentCount + 1;
        setPackedCounts((prev) => ({ ...prev, [match.sku]: newCount }));
        setLastScannedSku(match.sku);
        setScanError("");
        playSuccessDing();
    };

    // Fires on every keystroke AND on paste. Rather than processing the
    // instant digit count crosses 12 (which used to fire on a 12-digit
    // PREFIX of a still-incoming 13/14-digit code, then fire again on
    // whatever leftover digit(s)/Enter landed in the now-cleared input
    // right after — producing one good + one bad beep from a single
    // physical scan), this waits for a short pause in typing before
    // matching, so the full code has actually landed first.
    const handleScanChange = (e) => {
        const raw = e.target.value;
        setScanValue(raw);
        if (scanDebounceRef.current) clearTimeout(scanDebounceRef.current);

        const digits = normalizeCode(raw);
        if (digits.length < 8) return; // too short to be a real code yet

        scanDebounceRef.current = setTimeout(() => processScan(raw), SCAN_DEBOUNCE_MS);
    };

    return (
        <div className="bg-white rounded-2xl shadow-lg p-5 mb-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                    <p className="font-bold text-gray-800 text-sm">{split.ship_to_address?.fcName || split.shipment_id}</p>
                    <p className="text-xs text-gray-400">
                        {formatShipToAddress(split.ship_to_address)}
                        {formatShipToAddress(split.ship_to_address) && " · "}
                        Shipment {split.shipment_id}
                    </p>
                    <button
                        type="button"
                        onClick={() => printAllLabels(split.lines.map((l) => ({
                            row: { product_id: l.product_id, title: l.item_desc },
                            qty: l.item_qty, expirationDate: l.expiration_date || getSavedExpirationDate(l.sku),
                        })))}
                        className="mt-1 inline-flex items-center gap-1 px-2 py-1 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg text-[10px] font-bold hover:bg-teal-100 transition"
                    >
                        🖨️ Print All Labels for This Shipment
                    </button>
                </div>
                <div className="flex items-center gap-2">
                    {packingActive && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${allPacked ? "bg-green-50 text-green-600" : "bg-orange-50 text-orange-600"}`}>
                            {fullyPackedLines}/{totalLines} lines packed
                        </span>
                    )}
                    <StatusBadge status={split.status} />
                    <button
                        onClick={() => setPackingActive((a) => {
                            if (a) setShowOnlyRemaining(false); // was active, now stopping — reset the toggle
                            return !a;
                        })}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition ${packingActive ? "bg-gray-100 text-gray-600 border-gray-200" : "bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100"}`}
                    >
                        <ClipboardCheck size={13} /> {packingActive ? "Stop Packing" : "Start Packing"}
                    </button>
                </div>
            </div>

            {packingActive && (
                <div className="mb-4">
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
                        <input
                            ref={scanInputRef}
                            value={scanValue}
                            onChange={handleScanChange}
                            onKeyDown={(e) => {
                                if (e.key !== "Enter") return;
                                if (scanDebounceRef.current) clearTimeout(scanDebounceRef.current);
                                processScan(scanValue);
                            }}
                            placeholder="Scan or paste UPC/GTIN barcode..."
                            className={`w-full border rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none ${scanError ? "border-red-300 bg-red-50" : "border-gray-200 focus:border-teal-500"}`}
                        />
                    </div>
                    {scanError && <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1"><AlertCircle size={12} /> {scanError}</p>}
                    <label className="flex items-center gap-1.5 text-xs text-gray-500 mt-2 cursor-pointer w-fit">
                        <input
                            type="checkbox"
                            checked={showOnlyRemaining}
                            onChange={(e) => setShowOnlyRemaining(e.target.checked)}
                            className="accent-teal-600"
                        />
                        Only show what's left to pack
                    </label>
                </div>
            )}

            <div className="mb-4 border border-gray-100 rounded-xl p-3">
                <button type="button" onClick={() => setShowBoxEntry((o) => !o)}
                    className="w-full flex items-center justify-between text-xs font-bold text-gray-600">
                    <span className="flex items-center gap-1.5"><Box size={13} className="text-teal-500" /> Box Dimensions (optional — fill in while packing)</span>
                    <span className="text-gray-400">{showBoxEntry ? "▲" : "▼"}</span>
                </button>
                {showBoxEntry && (
                    <div className="mt-3 space-y-2">
                        <p className="text-[10px] text-gray-400 mb-1">
                            These are saved automatically and will auto-fill later when you configure boxes for carrier rate quotes — no need to retype them.
                        </p>
                        {boxes.map((b, idx) => (
                            <div key={idx} className="border border-gray-200 rounded-lg p-2.5">
                                <div className="flex items-center justify-between mb-1.5">
                                    <input
                                        value={b.label}
                                        onChange={(e) => updateBox(idx, "label", e.target.value)}
                                        placeholder={`Box type ${idx + 1} (optional label)`}
                                        className="text-xs font-semibold border-b border-dashed border-gray-200 focus:outline-none focus:border-teal-500 flex-1 mr-2"
                                    />
                                    {boxes.length > 1 && (
                                        <button type="button" onClick={() => removeBox(idx)} className="text-gray-300 hover:text-red-500">
                                            <X size={13} />
                                        </button>
                                    )}
                                </div>
                                <BoxPresetPicker box={b} onApply={(patch) => setBoxes((prev) => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], ...patch };
                                    savePackedBoxDims(split.shipment_id, next);
                                    return next;
                                })} />
                                <div className="grid grid-cols-5 gap-1.5 mb-1.5">
                                    <div>
                                        <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Boxes</p>
                                        <input type="number" min="1" value={b.count}
                                            onChange={(e) => updateBox(idx, "count", e.target.value)}
                                            onWheel={preventWheelChange}
                                            className="w-full border border-gray-200 rounded px-1.5 py-1 text-xs focus:outline-none focus:border-teal-500" />
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Length</p>
                                        <input type="number" step="0.1" value={b.length}
                                            onChange={(e) => updateBox(idx, "length", e.target.value)}
                                            onWheel={preventWheelChange}
                                            className="w-full border border-gray-200 rounded px-1.5 py-1 text-xs focus:outline-none focus:border-teal-500" />
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Width</p>
                                        <input type="number" step="0.1" value={b.width}
                                            onChange={(e) => updateBox(idx, "width", e.target.value)}
                                            onWheel={preventWheelChange}
                                            className="w-full border border-gray-200 rounded px-1.5 py-1 text-xs focus:outline-none focus:border-teal-500" />
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Height</p>
                                        <input type="number" step="0.1" value={b.height}
                                            onChange={(e) => updateBox(idx, "height", e.target.value)}
                                            onWheel={preventWheelChange}
                                            className="w-full border border-gray-200 rounded px-1.5 py-1 text-xs focus:outline-none focus:border-teal-500" />
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Weight (lb)</p>
                                        <input type="number" step="0.1" value={b.weight}
                                            onChange={(e) => updateBox(idx, "weight", e.target.value)}
                                            onWheel={preventWheelChange}
                                            className="w-full border border-gray-200 rounded px-1.5 py-1 text-xs focus:outline-none focus:border-teal-500" />
                                    </div>
                                </div>
                                <label className="flex items-center gap-1.5 text-[14px] text-black">
                                    <input type="checkbox" checked={b.is_mixed_sku !== false}
                                        onChange={(e) => updateBox(idx, "is_mixed_sku", e.target.checked)}
                                        className="accent-teal-600 h-4 w-4" />
                                    This box contains more than one SKU (mixed)
                                </label>
                            </div>
                        ))}
                        <button type="button" onClick={addBox} className="text-xs font-bold text-teal-600 hover:text-teal-700">
                            + Add another box type
                        </button>
                    </div>
                )}
            </div>

            <table className="w-full text-xs">
                <thead>
                    <tr className="text-gray-400 text-left border-b border-gray-100">
                        {packingActive && <th className="py-2 w-8"></th>}
                        <th className="py-2 w-56">Product</th><th>UPC/GTIN</th><th>SKU</th><th>Qty&emsp;</th><th>Dims & Weight</th>
                    </tr>
                </thead>
                <tbody>
                    {displayLines.map((l) => {
                        const count = packedCounts[l.sku] || 0;
                        const packed = count >= l.item_qty;
                        return (
                            <tr key={l.sku} className={`border-b border-gray-50 ${packed ? "opacity-40" : ""} ${l.sku === lastScannedSku ? "bg-teal-50/60" : ""}`}>
                                {packingActive && (
                                    <td className="py-2">
                                        <input type="checkbox" checked={packed} onChange={() => togglePacked(l.sku, l.item_qty)} className="w-4 h-4 accent-teal-600" />
                                    </td>
                                )}
                                <td className="py-2">
                                    <div className={`flex items-center gap-2 ${packed ? "line-through" : ""}`}>
                                        <ProductImage src={l.image_url} />
                                        <div>
                                            <p title={l.item_desc}>{truncateTitle(l.item_desc)}</p>
                                            {!packed && (
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => printPackRowLabel({ product_id: l.product_id, title: l.item_desc }, l.item_qty, l.expiration_date || getSavedExpirationDate(l.sku))}
                                                        title="Print label(s) for this item"
                                                        className="inline-flex items-center px-2 text-teal-700 border border-teal-200 rounded text-[9px] font-bold hover:bg-teal-100 transition"
                                                    >
                                                        Print Labels ({l.item_qty})
                                                    </button>
                                                    <ExpirationDateEditor
                                                        sku={l.sku}
                                                        value={l.expiration_date}
                                                        onSaved={() => { /* localStorage already updated; label reads it live at print time */ }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className={`font-mono text-gray-500 ${packed ? "line-through" : ""}`}>{l.product_id || "—"}</td>
                                <td className={`font-mono ${packed ? "line-through" : ""}`}>{l.sku}</td>
                                <td className={packed ? "line-through" : ""}>
                                    {packingActive && l.item_qty > 1 ? (
                                        <span className={`font-bold text-lg ${packed ? "text-green-600" : "text-orange-600"}`}>{count}/{l.item_qty}</span>
                                    ) : (
                                        <span className="font-bold text-base text-gray-700">{l.item_qty}</span>
                                    )}
                                </td>
                                <td className={packed ? "line-through" : ""}>{truncateTitle(l.dimensions ? `${l.dimensions.length}×${l.dimensions.width}×${l.dimensions.height}"${l.weight ? ` ${l.weight} lb` : ""}` : "—")}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <p className="text-right text-xs font-bold text-gray-600 mt-2">{split.total_units} units for this destination</p>
        </div>
    );
};

// Regroups the SAME report.splits data by product instead of by
// destination — each unique SKU appears once, with a row per shipment
// showing how many units go to that destination. Pure client-side
// reshaping; report.splits is the only data source, same as
// PackingSplitCard uses for the per-shipment view.
const PerProductPrepView = ({ splits }) => {
    const productsBySku = useMemo(() => {
        const map = new Map();
        for (const split of splits) {
            for (const line of split.lines) {
                if (!map.has(line.sku)) {
                    map.set(line.sku, {
                        sku: line.sku,
                        item_desc: line.item_desc,
                        image_url: line.image_url,
                        product_id: line.product_id,
                        vendor_pack_qty: line.vendor_pack_qty,
                        weight: line.weight,
                        dimensions: line.dimensions,
                        expiration_date: line.expiration_date || getSavedExpirationDate(line.sku) || null,
                        total_qty: 0,
                        total_boxes: 0,
                        byShipment: [],
                    });
                }
                const entry = map.get(line.sku);
                entry.total_qty += line.item_qty;
                entry.total_boxes += line.boxes_needed;
                entry.byShipment.push({
                    shipment_id: split.shipment_id,
                    ship_to_address: split.ship_to_address,
                    item_qty: line.item_qty,
                    boxes_needed: line.boxes_needed,
                });
            }
        }
        return Array.from(map.values()).sort((a, b) => (a.item_desc || "").localeCompare(b.item_desc || ""));
    }, [splits]);

    return (
        <div>
            <div className="flex justify-end mb-3">
                <button
                    type="button"
                    onClick={() => printAllLabels(productsBySku.map((p) => ({
                        row: { product_id: p.product_id, title: p.item_desc },
                        qty: p.total_qty, expirationDate: p.expiration_date,
                    })))}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg text-xs font-bold hover:bg-teal-100 transition"
                >
                    🖨️ Print All Labels
                </button>
            </div>
            {productsBySku.map((p) => (
                <div key={p.sku} className="bg-white rounded-2xl shadow-lg p-5 mb-4">
                    <div className="flex items-center gap-3 mb-3">
                        <ProductImage src={p.image_url} />
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800" title={p.item_desc}>{truncateTitle(p.item_desc, 60)}</p>
                            <p className="text-xs text-gray-400 font-mono">{p.sku}{p.product_id && ` · ${p.product_id}`}</p>
                            <button
                                type="button"
                                onClick={() => printPackRowLabel({ product_id: p.product_id, title: p.item_desc }, p.total_qty, p.expiration_date)}
                                title="Print label(s) for this product (total units across all destinations)"
                                className="mt-1 inline-flex items-center gap-1 px-2 py-1 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg text-[10px] font-bold hover:bg-teal-100 transition"
                            >
                                🖨️ Print Label ({p.total_qty})
                            </button>
                        </div>
                        <div className="text-right flex-shrink-0">
                            <p className="text-sm font-bold text-teal-700">{p.total_qty} units total</p>
                            <p className="text-xs text-gray-400">{p.total_boxes} box{p.total_boxes === 1 ? "" : "es"} · {p.byShipment.length} destination{p.byShipment.length === 1 ? "" : "s"}</p>
                        </div>
                    </div>
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-gray-400 text-left border-b border-gray-100">
                                <th className="py-1.5">Destination</th>
                                <th className="py-1.5 w-24">Shipment</th>
                                <th className="py-1.5 w-20">Qty</th>
                            </tr>
                        </thead>
                        <tbody>
                            {p.byShipment.map((s) => (
                                <tr key={s.shipment_id} className="border-b border-gray-50">
                                    <td className="py-1.5">{s.ship_to_address?.fcName || s.shipment_id} — <span className="text-gray-400">{formatShipToAddress(s.ship_to_address)}</span></td>
                                    <td className="py-1.5 font-mono text-gray-500">{s.shipment_id}</td>
                                    <td className="py-1.5 font-bold text-teal-700">{s.item_qty}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
        </div>
    );
};

const WFS_SPLIT_ORDER_KEY = "wfs_split_order_by_shipment";

const loadSplitOrderMap = () => {
    try {
        const raw = localStorage.getItem(WFS_SPLIT_ORDER_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

const saveSplitOrderForShipment = (shipmentId, order) => {
    try {
        const map = loadSplitOrderMap();
        map[shipmentId] = order;
        localStorage.setItem(WFS_SPLIT_ORDER_KEY, JSON.stringify(map));
    } catch { /* non-fatal — worst case order just isn't remembered next time */ }
};

// Keeps each destination card in the same on-screen position from the
// moment packing starts (Prep Report) all the way through Labels &
// Shipping — and even after closing the shipment and reopening it later
// — instead of resetting every time this is refetched or the component
// remounts. Persisted per shipment in localStorage, keyed by OUR OWN
// shipment id (the same `shipmentId` passed into both PrepReportStep and
// LabelsStep, and `shipment.id` everywhere else), so one remembered
// order is shared across every step of the flow.
//
// Identity for each split is its Walmart shipment_id when present
// (present on every shape this data takes — report.splits here,
// wfs_shipment_splits elsewhere), falling back to the DB row id if not.
//
// Deliberately simple and non-crashable: any id no longer returned by
// the backend is dropped, any new id is appended at the end, and a
// corrupted/missing localStorage entry just falls back to whatever
// order the backend returned this time — never blocks or throws.
const useStableSplitOrder = (shipmentId, splits) => {
    const identityOf = (s) => s.shipment_id || s.id;
    const orderRef = useRef(null);
    if (orderRef.current === null) {
        orderRef.current = (shipmentId && loadSplitOrderMap()[shipmentId]) || [];
    }

    const byId = new Map(splits.map((s) => [identityOf(s), s]));
    const order = orderRef.current.filter((id) => byId.has(id));
    for (const s of splits) {
        const id = identityOf(s);
        if (!order.includes(id)) order.push(id);
    }
    orderRef.current = order;
    if (shipmentId) saveSplitOrderForShipment(shipmentId, order);

    return order.map((id) => byId.get(id));
};

const PrepReportStep = ({ shipmentId, onNext, onBack, showBack = true }) => {
    const [report, setReport] = useState(null);
    const [reportModalOpen, setReportModalOpen] = useState(false);
    // 'shipment' = existing grouped-by-destination view (default, unchanged
    // behavior). 'product' = same underlying data, regrouped so each
    // product appears once with a per-shipment unit breakdown — pure
    // client-side reshaping of report.splits, no backend change.
    const [viewMode, setViewMode] = useState("shipment");
    const [, setBoxTick] = useState(0); // re-render trigger for the total box count

    const load = () => getPrepReport(shipmentId).then(setReport);
    useEffect(() => { load(); }, [shipmentId]);

    const stableSplits = useStableSplitOrder(shipmentId, report?.splits || []);

    if (!report) return <div className="max-w-3xl mx-auto py-10 px-6 flex items-center gap-2 text-gray-400"><Loader size={16} className="animate-spin" /> Building prep report...</div>;

    return (
        <div className="max-w-3xl mx-auto py-10 px-6">
            {showBack && (
                <button onClick={onBack} className="text-gray-400 hover:text-gray-600 mb-4 flex items-center gap-1 text-sm"><ChevronLeft size={16} /> Back</button>
            )}
            <div className="flex items-center justify-between mb-1">
                <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2"><ClipboardCheck size={20} className="text-teal-600" /> Prep Report</h1>
                <button onClick={() => setReportModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white border-2 border-teal-600 rounded-xl text-sm font-bold shadow-md hover:bg-teal-700 hover:shadow-lg transition">
                    <Printer size={16} /> View Full Report
                </button>
            </div>
            <p className="text-gray-500 text-sm mb-3">Inbound Order <span className="font-mono">{report.inbound_order_id}</span> · {report.grand_total_units} units</p>

            <div className="flex items-center justify-between mb-5">
                <div className="inline-flex bg-gray-100 rounded-lg p-1">
                    <button onClick={() => setViewMode("shipment")}
                        className={`px-3 py-1.5 text-xs font-bold rounded-md transition ${viewMode === "shipment" ? "bg-white text-teal-700 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                        Per Shipment
                    </button>
                    <button onClick={() => setViewMode("product")}
                        className={`px-3 py-1.5 text-xs font-bold rounded-md transition ${viewMode === "product" ? "bg-white text-teal-700 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                        Per Product
                    </button>
                </div>
            </div>

            {report.quantity_mismatches?.filter((m) => m.split_total > 0).length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-5 text-xs text-blue-700">
                    <p className="font-semibold mb-1 flex items-center gap-1"><AlertCircle size={12} /> Worth a quick double-check before shipping</p>
                    {report.quantity_mismatches.filter((m) => m.split_total > 0).map((m) => (
                        <p key={m.sku}>{m.sku}: requested {m.requested}, destinations show {m.split_total} — just verify your unit counts</p>
                    ))}
                </div>
            )}

            {viewMode === "shipment" ? (
                stableSplits.map((split) => (
                    <PackingSplitCard key={split.shipment_id} split={split}
                        inboundOrderId={report.inbound_order_id}
                        onBoxesChanged={() => setBoxTick((t) => t + 1)} />
                ))
            ) : (
                <PerProductPrepView splits={report.splits} />
            )}

            {stableSplits.length > 0 && (() => {
                const total = stableSplits.reduce((sum, s) =>
                    sum + (getPackedBoxDims(s.shipment_id) || []).reduce((acc, b) => acc + (parseInt(b.count, 10) || 0), 0), 0);
                return <TotalBoxesBar total={total} shipments={stableSplits.length} />;
            })()}

            <button onClick={onNext} className="w-full py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition flex items-center justify-center gap-2">
                Continue to Labels & Shipping <ArrowRight size={16} />
            </button>

            {reportModalOpen && <PrepReportModal report={report} onClose={() => setReportModalOpen(false)} />}
        </div>
    );
};

const emptyBox = (isMixed = true) => ({ label: "", count: "1", weight: "", length: "", width: "", height: "", is_mixed_sku: isMixed });

// Default for the "mixed" checkbox: more than one distinct SKU in this
// destination -> mixed. Unknown/empty -> true (previous behavior).
const defaultMixedFromSkus = (rows) => {
    const skus = new Set((rows || []).map((r) => r?.sku).filter(Boolean));
    return skus.size === 0 ? true : skus.size > 1;
};

// Box dims entered while PACKING (PackingSplitCard) are saved here, keyed by
// shipment_id, so the exact same numbers auto-populate later in BoxConfigModal
// (Labels & Carrier step, or reopened later from All Shipments) instead of
// asking the seller to retype what they already measured during packing.
const PACKED_BOX_DIMS_KEY = "wfs_packed_box_dims_by_shipment";

const loadPackedBoxDimsMap = () => {
    try {
        const raw = localStorage.getItem(PACKED_BOX_DIMS_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

const savePackedBoxDims = (shipmentId, boxes) => {
    try {
        const map = loadPackedBoxDimsMap();
        map[shipmentId] = boxes;
        localStorage.setItem(PACKED_BOX_DIMS_KEY, JSON.stringify(map));
    } catch { /* non-fatal */ }
};

const getPackedBoxDims = (shipmentId) => loadPackedBoxDimsMap()[shipmentId] || null;

// Manually-typed "boxes" count for the receiving label, saved per split.id
// so it survives closing the tab/coming back later — separate from
// box_config (a fuller box-by-box breakdown), since the seller may just
// want to remember a simple count without going through full Box Config.
const LABEL_BOX_COUNT_KEY = "wfs_receiving_label_box_count_by_split";

const loadLabelBoxCountMap = () => {
    try {
        const raw = localStorage.getItem(LABEL_BOX_COUNT_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

const saveLabelBoxCountForSplit = (splitId, count) => {
    try {
        const map = loadLabelBoxCountMap();
        map[splitId] = count;
        localStorage.setItem(LABEL_BOX_COUNT_KEY, JSON.stringify(map));
    } catch { /* non-fatal */ }
};

const getSavedLabelBoxCount = (splitId) => loadLabelBoxCountMap()[splitId] ?? null;

// Single resolution order for "what boxes did the seller actually pack" —
// real saved backend box_config if present, else whatever was typed while
// packing (cached locally per Walmart shipment_id).
const getEffectiveBoxConfig = (split) => {
    if (split.box_config && split.box_config.length > 0) return split.box_config;
    return getPackedBoxDims(split.shipment_id) || [];
};

// Groups box entries into Walmart's loadType buckets (mixed vs single SKU),
// each with its own total count. Missing/undefined is_mixed_sku defaults to
// mixed. Receiving labels only take ONE loadType per call, so a shipment
// with both mixed and single-SKU boxes needs one request per group.
const summarizeBoxLoadTypes = (boxes) => {
    const totalCount = (boxes || []).reduce((sum, b) => sum + (parseInt(b.count, 10) || 0), 0);
    const mixedCount = (boxes || []).filter((b) => b.is_mixed_sku !== false)
        .reduce((sum, b) => sum + (parseInt(b.count, 10) || 0), 0);
    const singleCount = (boxes || []).filter((b) => b.is_mixed_sku === false)
        .reduce((sum, b) => sum + (parseInt(b.count, 10) || 0), 0);
    const groups = [];
    if (mixedCount > 0) groups.push({ loadType: "MIXED SKUS", count: mixedCount });
    if (singleCount > 0) groups.push({ loadType: "SINGLE SKU", count: singleCount });
    return { totalCount, groups };
};

// Box count for one destination — same priority BulkLabelActions uses when
// printing receiving labels (manual override > saved Box Config / packing
// dims > remembered label count > 1), so the total matches what prints.
const resolveSplitBoxCount = (split, override) => {
    const o = parseInt(override, 10);
    if (o > 0) return o;
    const summary = summarizeBoxLoadTypes(getEffectiveBoxConfig(split));
    if (summary.totalCount > 0) return summary.totalCount;
    const saved = parseInt(getSavedLabelBoxCount(split.id), 10);
    return saved > 0 ? saved : 1;
};

// Display-only total, shown at the bottom of each box-related step.
const TotalBoxesBar = ({ total, shipments, note }) => (
    <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mb-4 flex items-center justify-between text-xs">
        <span className="font-bold text-gray-600 uppercase flex items-center gap-1.5">
            <Box size={13} className="text-teal-500" /> Total Boxes
        </span>
        <span className="text-gray-500">
            <span className="font-bold text-lg text-teal-700">{total}</span> box{total === 1 ? "" : "es"} configured
            {note ? <span className="text-gray-400"> · {note}</span> : null}
        </span>
    </div>
);

const BoxConfigModal = ({ split, onClose, onSaved }) => {
    const defaultMixed = defaultMixedFromSkus(split.wfs_shipment_split_items);
    const [boxes, setBoxes] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        getBoxConfig(split.id).then((d) => {
            const existing = d.box_config || [];
            if (existing.length > 0) {
                setBoxes(existing.map((b) => ({
                    ...b, count: String(b.count), weight: String(b.weight),
                    length: String(b.length), width: String(b.width), height: String(b.height),
                    is_mixed_sku: b.is_mixed_sku !== false,
                })));
                return;
            }
            // Nothing saved on the backend yet — fall back to whatever the
            // seller entered while PACKING this destination (PackingSplitCard),
            // keyed by split.shipment_id (Walmart's own per-destination ID,
            // present on both this DB-row object and PackingSplitCard's
            // lighter report-derived object — see PackingSplitCard for why).
            const packed = getPackedBoxDims(split.shipment_id);
            setBoxes(packed && packed.length > 0 ? packed : [emptyBox(defaultMixed)]);
        }).catch(() => {
            const packed = getPackedBoxDims(split.shipment_id);
            setBoxes(packed && packed.length > 0 ? packed : [emptyBox(defaultMixed)]);
        });
    }, [split.id, split.shipment_id]);

    const update = (idx, field, val) => setBoxes((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx], [field]: val };
        return next;
    });

    const addBox = () => setBoxes((prev) => [...prev, emptyBox(defaultMixed)]);
    const removeBox = (idx) => setBoxes((prev) => prev.filter((_, i) => i !== idx));

    const totalBoxes = (boxes || []).reduce((sum, b) => sum + (parseInt(b.count, 10) || 0), 0);

    const invalid = (b) => !b.count || parseInt(b.count, 10) <= 0
        || !b.weight || parseFloat(b.weight) <= 0
        || !b.length || parseFloat(b.length) <= 0
        || !b.width || parseFloat(b.width) <= 0
        || !b.height || parseFloat(b.height) <= 0;

    const hasInvalid = (boxes || []).some(invalid);

    const handleSave = async () => {
        setSaving(true);
        setError("");
        try {
            const payload = boxes.map((b) => ({
                label: b.label, count: parseInt(b.count, 10),
                weight: parseFloat(b.weight), length: parseFloat(b.length),
                width: parseFloat(b.width), height: parseFloat(b.height),
                is_mixed_sku: b.is_mixed_sku !== false,
            }));
            await saveBoxConfig(split.id, payload);
            // Keep the packing-time local cache in sync too, so every
            // screen (packing, labels, bulk print) sees the same box plan.
            savePackedBoxDims(split.shipment_id, payload);
            onSaved(payload);
        } catch (e) {
            setError(e.message || "Failed to save box configuration");
        } finally {
            setSaving(false);
        }
    };

    if (boxes === null) {
        return (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl p-6 flex items-center gap-2 text-gray-400">
                    <Loader size={16} className="animate-spin" /> Loading...
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                    <div>
                        <h3 className="font-bold text-gray-800 text-sm">Box Configuration</h3>
                        <p className="text-xs text-gray-400 mt-0.5">
                            Tell us the actual boxes you're shipping — this is what Walmart uses to price the rate quote, same as on walmart.com.
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                </div>

                <div className="p-5 overflow-y-auto flex-1 space-y-3">
                    {boxes.map((b, idx) => (
                        <div key={idx} className="border border-gray-200 rounded-xl p-3">
                            <div className="flex items-center justify-between mb-2">
                                <input
                                    value={b.label}
                                    onChange={(e) => update(idx, "label", e.target.value)}
                                    placeholder={`Box type ${idx + 1} (optional label)`}
                                    className="text-xs font-semibold border-b border-dashed border-gray-200 focus:outline-none focus:border-teal-500 flex-1 mr-2"
                                />
                                {boxes.length > 1 && (
                                    <button onClick={() => removeBox(idx)} className="text-gray-300 hover:text-red-500">
                                        <Trash2 size={14} />
                                    </button>
                                )}
                            </div>
                            <BoxPresetPicker box={b} onApply={(patch) => setBoxes((prev) => {
                                const next = [...prev];
                                next[idx] = { ...next[idx], ...patch };
                                return next;
                            })} />
                            <div className="grid grid-cols-5 gap-2 mb-2">
                                <div>
                                    <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Boxes</p>
                                    <input type="number" min="1" value={b.count}
                                        onChange={(e) => update(idx, "count", e.target.value)}
                                        onWheel={preventWheelChange}
                                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                                </div>
                                <div>
                                    <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Length</p>
                                    <input type="number" step="0.1" value={b.length}
                                        onChange={(e) => update(idx, "length", e.target.value)}
                                        onWheel={preventWheelChange}
                                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                                </div>
                                <div>
                                    <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Width</p>
                                    <input type="number" step="0.1" value={b.width}
                                        onChange={(e) => update(idx, "width", e.target.value)}
                                        onWheel={preventWheelChange}
                                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                                </div>
                                <div>
                                    <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Height</p>
                                    <input type="number" step="0.1" value={b.height}
                                        onChange={(e) => update(idx, "height", e.target.value)}
                                        onWheel={preventWheelChange}
                                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                                </div>
                                <div>
                                    <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Weight (lb)</p>
                                    <input type="number" step="0.1" value={b.weight}
                                        onChange={(e) => update(idx, "weight", e.target.value)}
                                        onWheel={preventWheelChange}
                                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                                </div>
                            </div>
                            <label className="flex items-center gap-1.5 text-[14px] text-black">
                                <input type="checkbox" checked={b.is_mixed_sku !== false}
                                    onChange={(e) => update(idx, "is_mixed_sku", e.target.checked)}
                                    className="accent-teal-600 h-4 w-4" />
                                This box contains more than one SKU (mixed)
                            </label>
                        </div>
                    ))}

                    <button onClick={addBox} className="text-xs font-bold text-teal-600 hover:text-teal-700">
                        + Add another box type
                    </button>
                </div>

                <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0">
                    <p className="text-xs text-gray-400 mb-2">Total boxes: <span className="font-bold text-gray-600">{totalBoxes}</span></p>
                    {error && <p className="text-xs text-red-500 mb-2">{error}</p>}
                    <button onClick={handleSave} disabled={saving || hasInvalid}
                        className="w-full py-2.5 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-lg text-sm font-bold hover:opacity-90 transition disabled:opacity-40">
                        {saving ? "Saving..." : "Save & Get Rate Quote"}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════
   STEP 7 — Labels: WFS receiving label + carrier (self or Walmart-arranged)
   ═══════════════════════════════════════════════════════════ */

// Shared bulk-print toolbar — used both in the wizard's Labels & Shipping
// step and in All Shipments' Labels & Carrier tab. Everything is fetched
// as individual PDF blobs (same per-split endpoints as the single-label
// buttons) then merged into ONE PDF client-side. "Get Quotes for All" /
// "Confirm All" are gone on purpose: every destination now needs its own
// box dimensions entered via Configure Boxes before it can be quoted, so
// there's no safe way to bulk that part anymore.
const BulkLabelActions = ({ splits, onChanged, labelCountOverrides = {} }) => {
    const [bulkStatus, setBulkStatus] = useState("");
    // Confirmed WPC bookings are the sure thing. Imported shipments with
    // tracking already saved MIGHT also have a real WPC label on Walmart's
    // side (we just don't know locally) — include them too and let each
    // individual fetch fail quietly if there's nothing there, same as the
    // single-split "try downloading anyway" fallback.
    const confirmedSplits = splits.filter((s) => s.carrier_quote_status === "CONFIRMED");
    const maybeDownloadableSplits = splits.filter((s) =>
        s.carrier_quote_status === "CONFIRMED" || (s.tracking_no?.length > 0)
    );

    const runMergedPrint = async (labelKind, count, getOne) => {
        setBulkStatus(`Fetching ${labelKind} 0/${count}...`);
        const blobs = [];
        for (let i = 0; i < count; i++) {
            try {
                const blob = await getOne(i);
                if (blob) blobs.push(blob); // getOne may itself return null for a skipped item
            } catch { /* skip failed one, keep going */ }
            setBulkStatus(`Fetching ${labelKind} ${i + 1}/${count}...`);
        }
        setBulkStatus("Merging into one PDF...");
        const merged = await mergePdfBlobs(blobs);
        if (merged) openBlobInNewTab(merged);
        else alert(`Couldn't fetch any ${labelKind}.`);
        setBulkStatus("");
    };

    // Same fix as the single-destination button — one copy per PHYSICAL
    // BOX for this destination. Prefers whatever the seller manually typed
    // in that destination's own "boxes" input (labelCountOverrides) over
    // the saved box_config total, since a manual override is a more recent,
    // more deliberate signal than whatever's saved from Box Configuration.
    const resolveBoxGroups = (split) => {
        const override = parseInt(labelCountOverrides[split.id], 10);
        if (override > 0) return [{ loadType: "MIXED SKUS", count: override }];
        const summary = summarizeBoxLoadTypes(getEffectiveBoxConfig(split));
        if (summary.totalCount > 0) return summary.groups;
        const saved = getSavedLabelBoxCount(split.id);
        return [{ loadType: "MIXED SKUS", count: saved > 0 ? saved : 1 }];
    };

    const handleBulkReceivingLabels = () => runMergedPrint(
        "receiving labels", splits.length,
        (i) => {
            const split = splits[i];
            // ONE call per destination carrying every group for that split —
            // keeps Walmart's box numbering continuous per shipment, only
            // merging PDFs across DIFFERENT destinations (which is correct,
            // since each destination's numbering legitimately restarts).
            return fetchReceivingLabelBlob(split.id, { labelSize: "X4_6", labelFormat: "PDF", loadTypes: resolveBoxGroups(split) });
        }
    );

    const handleBulkCarrierLabels = async () => {
        if (maybeDownloadableSplits.length === 0) return;
        const shipDate = window.prompt("Ship date for all carrier labels (YYYY-MM-DD):", new Date().toISOString().slice(0, 10));
        if (!shipDate) return;
        let skipped = 0;
        await runMergedPrint(
            "carrier labels",
            maybeDownloadableSplits.length,
            async (i) => {
                try {
                    return await fetchCarrierLabelBlob(maybeDownloadableSplits[i].id, shipDate);
                } catch (e) {
                    skipped += 1;
                    return null; // no WPC label for this one — just leave it out of the merged PDF
                }
            }
        );
        if (skipped > 0) {
            alert(`${skipped} of ${maybeDownloadableSplits.length} destination(s) had no carrier label available on Walmart's side and were skipped.`);
        }
        await onChanged?.();
    };

    const handleBulkPaired = async () => {
        if (maybeDownloadableSplits.length === 0) return;
        const shipDate = window.prompt("Ship date for all carrier labels (YYYY-MM-DD):", new Date().toISOString().slice(0, 10));
        if (!shipDate) return;
        setBulkStatus(`Building combined PDF 0/${maybeDownloadableSplits.length}...`);
        const blobs = [];
        for (let i = 0; i < maybeDownloadableSplits.length; i++) {
            const split = maybeDownloadableSplits[i];
            try { blobs.push(await fetchReceivingLabelBlob(split.id, { labelSize: "X4_6", labelFormat: "PDF", loadTypes: resolveBoxGroups(split) })); } catch { /* skip */ }
            try { blobs.push(await fetchCarrierLabelBlob(split.id, shipDate)); } catch { /* no WPC label for this one — skip */ }
            setBulkStatus(`Building combined PDF ${i + 1}/${maybeDownloadableSplits.length}...`);
        }
        setBulkStatus("Merging into one PDF...");
        const merged = await mergePdfBlobs(blobs);
        if (merged) openBlobInNewTab(merged);
        setBulkStatus("");
        await onChanged?.();
    };

    return (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
            <h3 className="text-xs font-bold text-gray-500 uppercase mb-3">Bulk Print — All {splits.length} Destinations</h3>
            {bulkStatus && (
                <p className="text-xs text-teal-600 font-semibold mb-3 flex items-center gap-1.5"><Loader size={12} className="animate-spin" /> {bulkStatus}</p>
            )}
            <div className="flex flex-wrap gap-2">
                <button onClick={handleBulkReceivingLabels} disabled={!!bulkStatus || splits.length === 0}
                    className="px-3 py-2 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg text-xs font-bold hover:bg-teal-100 transition disabled:opacity-40 flex items-center gap-1.5">
                    <Printer size={12} /> Print All Receiving Labels (1 PDF)
                </button>
                <button onClick={handleBulkCarrierLabels} disabled={!!bulkStatus || maybeDownloadableSplits.length === 0}
                    className="px-3 py-2 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg text-xs font-bold hover:bg-purple-100 transition disabled:opacity-40 flex items-center gap-1.5">
                    <Printer size={12} /> Print All Carrier Labels (1 PDF) — {maybeDownloadableSplits.length} eligible
                </button>
                <button onClick={handleBulkPaired} disabled={!!bulkStatus || maybeDownloadableSplits.length === 0}
                    className="px-3 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-xs font-bold hover:bg-blue-100 transition disabled:opacity-40 flex items-center gap-1.5">
                    <Printer size={12} /> Print Receiving + Carrier, Paired (1 PDF)
                </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
                "Eligible" means a confirmed Walmart booking, or an imported/self-arranged shipment we haven't checked yet —
                we'll try Walmart for each and skip any with no carrier label on their side. Bulk quoting/confirming a NEW booking
                still isn't offered, since box dimensions must be entered per destination first.
            </p>
        </div>
    );
};

const SplitLabelsCard = ({ split, onDone, labelCountOverride, onLabelCountChange }) => {
    const [labelLoading, setLabelLoading] = useState(false);
    // Walmart's receiving label endpoint happily prints any number of
    // copies in ONE call (loadTypes[].count) — the UI just never exposed
    // more than a hardcoded count:1, which is why a multi-box destination
    // only ever produced a single label. Default guesses from whatever
    // box_config total is already saved for this split, but stays fully
    // editable since box_config isn't always set yet at this point.
    // Whatever the seller types here is also reported UP to the parent
    // (via onLabelCountChange) so the bulk "Print All" buttons use the
    // SAME number instead of silently recomputing from box_config alone.
    //
    // Priority for the initial value: an override already passed down
    // from the parent this session > real saved box_config data from the
    // DB (the most authoritative source, since it's what Carrier Rate
    // Quote also uses) > whatever was locally remembered from a past
    // session in this browser > a bare default of 1.
    const [boxLoadSummary, setBoxLoadSummary] = useState(() => summarizeBoxLoadTypes(getEffectiveBoxConfig(split)));
    const defaultBoxCount = boxLoadSummary.totalCount || getSavedLabelBoxCount(split.id) || 1;
    const [labelCount, setLabelCount] = useState(labelCountOverride ?? defaultBoxCount);
    const [carrierMode, setCarrierMode] = useState(() => {
        if (split.carrier_quote_id) return "walmart";
        if (split.tracking_no?.length) return "self";
        return null;
    });

    // self-arranged
    const [carrierName, setCarrierName] = useState("");
    const [trackingNumbers, setTrackingNumbers] = useState([""]);
    const [savingTracking, setSavingTracking] = useState(false);
    const [trackingSaved, setTrackingSaved] = useState(!!split.tracking_no?.length);

    // For imported shipments, tracking came from Walmart's own data at
    // import time — we genuinely don't know if it was booked through WPC
    // (label fetchable) or shipped fully outside Walmart (nothing to
    // fetch). This lets the seller just ask Walmart directly rather than
    // us guessing from local state.
    const [tryingWalmartLabel, setTryingWalmartLabel] = useState(false);
    const [walmartLabelDownloaded, setWalmartLabelDownloaded] = useState(false);
    const [walmartLabelError, setWalmartLabelError] = useState("");

    const tryDownloadWalmartLabel = async () => {
        setTryingWalmartLabel(true);
        setWalmartLabelError("");
        try {
            const today = new Date().toISOString().slice(0, 10);
            const blob = await fetchCarrierLabelBlob(split.id, today);
            openBlobInNewTab(blob);
            setWalmartLabelDownloaded(true);
        } catch (e) {
            setWalmartLabelError(e.message || "No carrier label found on Walmart's side for this shipment.");
        } finally {
            setTryingWalmartLabel(false);
        }
    };

    // walmart-arranged
    const [quote, setQuote] = useState(split.carrier_quote_id ? { netCharge: split.carrier_quote_net_charge, currency: split.carrier_quote_currency } : null);
    const [quoting, setQuoting] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [confirmed, setConfirmed] = useState(split.carrier_quote_status === "CONFIRMED");
    const [shipDate, setShipDate] = useState("");
    const [printingLabel, setPrintingLabel] = useState(false);
    const [carrierError, setCarrierError] = useState("");
    const [violations, setViolations] = useState(null);
    const [showBoxConfig, setShowBoxConfig] = useState(false);
    const [editingItemId, setEditingItemId] = useState(null);
    const [editValues, setEditValues] = useState({ weight: "", length: "", width: "", height: "" });
    const [savingFix, setSavingFix] = useState(false);

    const printReceivingLabel = async () => {
        setLabelLoading(true);
        try {
            const groups = boxLoadSummary.totalCount > 0
                ? boxLoadSummary.groups
                : [{ loadType: "MIXED SKUS", count: Math.max(1, parseInt(labelCount, 10) || 1) }];
            // ONE call with every group — Walmart numbers all boxes for this
            // destination continuously (1 of N ... N of N) instead of
            // restarting per group.
            const blob = await fetchReceivingLabelBlob(split.id, { labelSize: "X4_6", labelFormat: "PDF", loadTypes: groups });
            openBlobInNewTab(blob);
        } catch (e) {
            alert(e.message || "Failed to print receiving label");
        } finally {
            setLabelLoading(false);
        }
    };

    const saveTracking = async () => {
        setSavingTracking(true);
        try {
            await updateSplitTracking(split.id, carrierName, trackingNumbers.filter(Boolean));
            setTrackingSaved(true);
            onDone();
        } catch (e) {
            alert(e.message || "Failed to save tracking");
        } finally {
            setSavingTracking(false);
        }
    };

    const getQuote = async () => {
        setQuoting(true);
        setCarrierError("");
        setViolations(null);
        try {
            const data = await createCarrierQuote(split.id);
            // The new WPC endpoint wraps everything in a "payload" object —
            // rateQuotes lives at data.payload.rateQuotes, not data.rateQuotes.
            const best = ((data.payload || {}).rateQuotes || [])[0];
            setQuote(best || null);
            if (!best) setCarrierError("No rate quotes returned for this shipment's dimensions/weight.");
        } catch (e) {
            if (e.code === "box_config_required") {
                setShowBoxConfig(true);
            } else if (e.violations) {
                setViolations(e.violations);
            } else if (e.isWalmartInternalError) {
                setCarrierError(`Walmart's FedEx quote service is currently failing on their end (not a data issue on ours). Try again later, or use "I'm Shipping This Myself" instead.${e.correlationId ? ` Reference: ${e.correlationId}` : ""}`);
            } else {
                setCarrierError(e.message || "Failed to get a carrier rate quote");
            }
        } finally {
            setQuoting(false);
        }
    };

    const startEditing = (v) => {
        setEditingItemId(v.item_id);
        setEditValues({
            weight: v.weight ?? "", length: v.dimensions?.length ?? "",
            width: v.dimensions?.width ?? "", height: v.dimensions?.height ?? "",
        });
    };

    const saveFix = async (itemId) => {
        setSavingFix(true);
        try {
            await updateItemPackDetails(itemId, {
                weight: parseFloat(editValues.weight) || null,
                dimensions: {
                    length: parseFloat(editValues.length) || null,
                    width: parseFloat(editValues.width) || null,
                    height: parseFloat(editValues.height) || null,
                },
            });
            setEditingItemId(null);
            setViolations((prev) => prev?.filter((v) => v.item_id !== itemId) || null);
        } catch (e) {
            alert(e.message || "Failed to save");
        } finally {
            setSavingFix(false);
        }
    };

    const confirmQuote = async () => {
        setConfirming(true);
        try {
            await confirmCarrierQuote(split.id);
            setConfirmed(true);

            // Real carrier cost just confirmed — automatically update whatever's
            // stored as this item's WFS inbound_shipping estimate to the real
            // per-unit cost, no confirmation prompt.
            try {
                const splitItems = split.wfs_shipment_split_items || [];
                const totalUnits = splitItems.reduce((s, it) => s + (it.item_qty || 0), 0);
                const netCharge = quote?.netCharge || 0;
                if (totalUnits > 0 && netCharge > 0) {
                    const perUnitCost = netCharge / totalUnits;
                    const uniqueCatalogIds = [...new Set(splitItems.map((it) => it.catalog_item_id).filter(Boolean))];
                    if (uniqueCatalogIds.length > 0) {
                        await Promise.all(uniqueCatalogIds.map((cid) => mergeItemFeeKey(cid, "inbound_shipping", perUnitCost, "label")));
                    }
                }
            } catch { /* non-fatal */ }
        } catch (e) {
            setCarrierError(e.message || "Failed to confirm quote");
        } finally {
            setConfirming(false);
        }
    };

    const printCarrierLabel = async () => {
        if (!shipDate) { setCarrierError("Pick a ship date first"); return; }
        setPrintingLabel(true);
        try {
            const blob = await fetchCarrierLabelBlob(split.id, shipDate);
            openBlobInNewTab(blob);
            onDone();
        } catch (e) {
            // walmart_stuck_label carries a longer, more useful explanation
            // than the generic message — show it in full rather than
            // truncating to e.message.
            setCarrierError(e.code === "walmart_stuck_label" ? e.message : (e.message || "Failed to print carrier label"));
        } finally {
            setPrintingLabel(false);
        }
    };

    const maxShipDate = new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

    return (
        <div className="bg-white rounded-2xl shadow-lg p-5 mb-4">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <p className="font-bold text-gray-800 text-sm">{split.ship_to_address?.fcName || split.shipment_id}</p>
                    <p className="text-xs text-gray-400">
                        {formatShipToAddress(split.ship_to_address)}
                        {formatShipToAddress(split.ship_to_address) && " · "}
                        Shipment {split.shipment_id}
                    </p>
                </div>
                <StatusBadge status={split.status} />
            </div>

            {/* WFS receiving label */}
            <div className="border border-gray-100 rounded-xl p-4 mb-4">
                <h4 className="text-xs font-bold text-gray-600 uppercase mb-2 flex items-center gap-1.5"><Box size={14} /> WFS Receiving Label</h4>
                <p className="text-xs text-gray-400 mb-3">Affix this to every physical box — it tells the FC which Inbound Order/Shipment they're receiving. Print one copy per box.</p>
                <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={printReceivingLabel} disabled={labelLoading}
                        className="px-4 py-2 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg text-xs font-bold hover:bg-teal-100 transition disabled:opacity-50 flex items-center gap-1.5">
                        {labelLoading ? <Loader size={12} className="animate-spin" /> : <Printer size={12} />} Print Receiving Label{(boxLoadSummary.totalCount || parseInt(labelCount, 10) || 1) > 1 ? "s" : ""}
                    </button>
                    {boxLoadSummary.totalCount > 0 ? (
                        <span className="text-xs text-gray-500">
                            <span className="font-bold text-gray-700">{boxLoadSummary.totalCount}</span> box{boxLoadSummary.totalCount === 1 ? "" : "es"} — auto from Box Configuration
                            {boxLoadSummary.groups.length > 1 && (
                                <> ({boxLoadSummary.groups.map((g) => `${g.count} ${g.loadType === "MIXED SKUS" ? "mixed" : "single"}`).join(", ")})</>
                            )}
                            <button type="button" onClick={() => setShowBoxConfig(true)}
                                className="ml-2 text-teal-600 hover:text-teal-700 underline decoration-dotted font-bold">
                                Edit / Add Boxes
                            </button>
                        </span>
                    ) : (
                        <label className="flex items-center gap-1.5 text-xs text-gray-500">
                            <span className="font-semibold text-gray-400">×</span>
                            <input type="number" min="1" value={labelCount}
                                onChange={(e) => {
                                    setLabelCount(e.target.value);
                                    onLabelCountChange?.(e.target.value);
                                    saveLabelBoxCountForSplit(split.id, e.target.value);
                                }}
                                onWheel={preventWheelChange}
                                className="w-14 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                            <span className="text-gray-400">boxes (no Box Config saved yet)</span>
                        </label>
                    )}
                </div>
            </div>

            {/* Carrier */}
            <div className="border border-gray-100 rounded-xl p-4">
                <h4 className="text-xs font-bold text-gray-600 uppercase mb-2 flex items-center gap-1.5"><Truck size={14} /> Get It There</h4>

                {!carrierMode && (
                    <div className="grid grid-cols-2 gap-2">
                        <button onClick={() => setCarrierMode("walmart")} className="p-3 border-2 border-gray-200 hover:border-teal-400 rounded-xl text-left text-xs transition">
                            <p className="font-bold text-gray-700 mb-0.5">Let Walmart Arrange It</p>
                            <p className="text-gray-400">FedEx parcel via WFS Preferred Carrier</p>
                        </button>
                        <button onClick={() => setCarrierMode("self")} className="p-3 border-2 border-gray-200 hover:border-teal-400 rounded-xl text-left text-xs transition">
                            <p className="font-bold text-gray-700 mb-0.5">I'm Shipping This Myself</p>
                            <p className="text-gray-400">Just log your tracking number</p>
                        </button>
                    </div>
                )}

                {carrierMode === "self" && (
                    <div>
                        {!trackingSaved && (
                            <button type="button" onClick={() => setCarrierMode(null)}
                                className="flex items-center gap-1 text-[11px] font-bold text-gray-400 hover:text-gray-600 mb-3">
                                <ChevronLeft size={12} /> Back
                            </button>
                        )}
                        {trackingSaved ? (
                            <div>
                                <p className="text-xs text-green-600 flex items-center gap-1.5 mb-2"><Check size={14} /> Tracking saved: {split.carrier_name} — {(split.tracking_no || trackingNumbers).join(", ")}</p>
                                {!walmartLabelDownloaded ? (
                                    <button onClick={tryDownloadWalmartLabel} disabled={tryingWalmartLabel}
                                        className="text-[11px] font-bold text-teal-600 hover:text-teal-700 underline decoration-dotted disabled:opacity-50">
                                        {tryingWalmartLabel ? "Checking Walmart..." : "This was booked through Walmart — try downloading the carrier label"}
                                    </button>
                                ) : (
                                    <p className="text-[11px] text-green-600 flex items-center gap-1"><Check size={12} /> Carrier label downloaded from Walmart</p>
                                )}
                                {walmartLabelError && <p className="text-[11px] text-gray-400 mt-1">{walmartLabelError}</p>}
                            </div>
                        ) : (
                            <>
                                <input placeholder="Carrier name (e.g. UPS, FedEx)" value={carrierName} onChange={(e) => setCarrierName(e.target.value)}
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs mb-2 focus:outline-none focus:border-teal-500" />
                                {trackingNumbers.map((t, i) => (
                                    <div key={i} className="flex gap-2 mb-2">
                                        <input placeholder="Tracking number" value={t}
                                            onChange={(e) => setTrackingNumbers((arr) => arr.map((x, xi) => xi === i ? e.target.value : x))}
                                            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-teal-500" />
                                        {trackingNumbers.length > 1 && (
                                            <button onClick={() => setTrackingNumbers((arr) => arr.filter((_, xi) => xi !== i))} className="text-gray-300 hover:text-red-500"><X size={14} /></button>
                                        )}
                                    </div>
                                ))}
                                <button onClick={() => setTrackingNumbers((arr) => [...arr, ""])} className="text-xs text-teal-600 font-semibold mb-3">+ Add another tracking number</button>
                                <button onClick={saveTracking} disabled={savingTracking || !carrierName || !trackingNumbers.some(Boolean)}
                                    className="w-full py-2 bg-teal-600 text-white rounded-lg text-xs font-bold hover:bg-teal-700 transition disabled:opacity-40">
                                    {savingTracking ? "Saving..." : "Save Tracking"}
                                </button>
                            </>
                        )}
                    </div>
                )}

                {carrierMode === "walmart" && (
                    <div className="text-xs">
                        {carrierError && <p className="text-red-500 mb-2 flex items-center gap-1"><AlertCircle size={12} /> {carrierError}</p>}

                        {violations && violations.length > 0 && (
                            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-3 space-y-2">
                                <p className="font-bold text-orange-700 flex items-center gap-1"><AlertCircle size={12} /> {violations.length} item(s) exceed FedEx parcel limits</p>
                                {violations.map((v) => (
                                    <div key={v.sku} className="bg-white rounded-lg p-2 border border-orange-100">
                                        <p className="font-semibold text-gray-800">{v.title}</p>
                                        <p className="text-gray-400 mb-1">SKU {v.sku}</p>
                                        {v.reasons.map((r, i) => <p key={i} className="text-orange-600">{r}</p>)}
                                        {editingItemId === v.item_id ? (
                                            <div className="grid grid-cols-4 gap-1.5 mt-2">
                                                <input type="number" step="0.01" placeholder="lb" value={editValues.weight}
                                                    onChange={(e) => setEditValues((s) => ({ ...s, weight: e.target.value }))}
                                                    onWheel={preventWheelChange}
                                                    className="border border-gray-200 rounded px-1.5 py-1 text-[11px]" />
                                                <input type="number" step="0.1" placeholder="L" value={editValues.length}
                                                    onChange={(e) => setEditValues((s) => ({ ...s, length: e.target.value }))}
                                                    onWheel={preventWheelChange}
                                                    className="border border-gray-200 rounded px-1.5 py-1 text-[11px]" />
                                                <input type="number" step="0.1" placeholder="W" value={editValues.width}
                                                    onChange={(e) => setEditValues((s) => ({ ...s, width: e.target.value }))}
                                                    onWheel={preventWheelChange}
                                                    className="border border-gray-200 rounded px-1.5 py-1 text-[11px]" />
                                                <input type="number" step="0.1" placeholder="H" value={editValues.height}
                                                    onChange={(e) => setEditValues((s) => ({ ...s, height: e.target.value }))}
                                                    onWheel={preventWheelChange}
                                                    className="border border-gray-200 rounded px-1.5 py-1 text-[11px]" />
                                                <button onClick={() => saveFix(v.item_id)} disabled={savingFix}
                                                    className="col-span-2 py-1 bg-teal-600 text-white rounded text-[10px] font-bold mt-1">
                                                    {savingFix ? "Saving..." : "Save"}
                                                </button>
                                                <button onClick={() => setEditingItemId(null)}
                                                    className="col-span-2 py-1 bg-gray-100 text-gray-500 rounded text-[10px] font-bold mt-1">
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <button onClick={() => startEditing(v)} className="text-teal-600 font-bold underline decoration-dotted mt-1">
                                                Fix Weight/Dimensions
                                            </button>
                                        )}
                                    </div>
                                ))}
                                {violations.length === 0 ? null : (
                                    <button onClick={getQuote} disabled={quoting}
                                        className="w-full py-1.5 bg-teal-600 text-white rounded-lg text-[11px] font-bold hover:bg-teal-700 transition disabled:opacity-50">
                                        {quoting ? "Retrying..." : "Retry Quote"}
                                    </button>
                                )}
                            </div>
                        )}

                        {!quote && (!violations || violations.length === 0) && (
                            <button onClick={() => setShowBoxConfig(true)} disabled={quoting} className="px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg font-bold hover:bg-blue-100 transition disabled:opacity-50">
                                {quoting ? "Getting quote..." : "Configure Boxes & Get Rate Quote"}
                            </button>
                        )}
                        {quote && !confirmed && (
                            <div>
                                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                                    <p className="font-bold text-gray-700">Estimated cost: ${quote.netCharge?.toFixed(2)} {quote.currency}</p>
                                </div>
                                <button onClick={confirmQuote} disabled={confirming} className="w-full py-2 bg-teal-600 text-white rounded-lg font-bold hover:bg-teal-700 transition disabled:opacity-50">
                                    {confirming ? "Confirming..." : "Confirm & Accept Rate"}
                                </button>
                                <button onClick={() => setShowBoxConfig(true)} disabled={confirming}
                                    className="w-full mt-1.5 py-1 text-[10px] text-gray-400 hover:text-teal-600 font-semibold underline decoration-dotted disabled:opacity-50">
                                    Edit box configuration & re-quote
                                </button>
                            </div>
                        )}
                        {confirmed && (
                            <div>
                                <p className="text-green-600 flex items-center gap-1 mb-2"><Check size={14} /> Booking confirmed</p>
                                <label className="block text-gray-500 mb-1">Ship Date (up to 10 days out)</label>
                                <input type="date" value={shipDate} max={maxShipDate} onChange={(e) => setShipDate(e.target.value)}
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 mb-2 focus:outline-none focus:border-teal-500" />
                                <button onClick={printCarrierLabel} disabled={printingLabel || !shipDate}
                                    className="w-full py-2 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-lg text-[11px] font-bold hover:opacity-90 transition disabled:opacity-40 flex items-center justify-center gap-1.5 mb-2">
                                    {printingLabel ? <Loader size={12} className="animate-spin" /> : <Printer size={12} />} Print Carrier Label
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {showBoxConfig && (
                <BoxConfigModal
                    split={split}
                    onClose={() => setShowBoxConfig(false)}
                    onSaved={async (savedBoxes) => {
                        setShowBoxConfig(false);
                        if (savedBoxes?.length) {
                            // Only update THIS card's own summary for immediate display.
                            // Deliberately NOT forwarding to onLabelCountChange/
                            // labelCountOverrides — that path exists solely for the
                            // manual "no box config yet" fallback and always assumes
                            // a single MIXED SKUS group. Forwarding a real box_config
                            // total here would collapse a mixed+single box plan into
                            // one MIXED SKUS group for Bulk Print and lose the split.
                            // Bulk actions correctly re-derive the per-type breakdown
                            // from split.box_config once onDone() refreshes the parent.
                            setBoxLoadSummary(summarizeBoxLoadTypes(savedBoxes));
                        }
                        await getQuote();
                        onDone(); // refresh the parent's copy of this split so bulk actions & re-opens stay in sync
                    }}
                />
            )}
        </div>
    );
};

const LabelsStep = ({ shipmentId, onFinish, onBack }) => {
    const [shipment, setShipment] = useState(null);
    // splitId -> manually-typed "boxes" count, so Bulk Print uses the exact
    // same number the seller sees/edited in that destination's own card.
    const [labelCountOverrides, setLabelCountOverrides] = useState({});
    const load = () => getWfsShipment(shipmentId).then((d) => setShipment(d.shipment));
    useEffect(() => { load(); }, [shipmentId]);

    // Auto-fill missing product images/titles the moment the popup opens —
    // same live lookup used elsewhere in the app, just applied here for
    // whatever the import (or a partial catalog match) left blank.
    useEffect(() => {
        if (!shipment) return;
        const missing = (shipment.wfs_shipment_items || []).filter((it) => !it.image_url);
        if (missing.length === 0) return;
        let cancelled = false;
        (async () => {
            for (const it of missing) {
                if (cancelled) return;
                await enrichShipmentItemImage(it.id).catch(() => { });
                await new Promise((r) => setTimeout(r, 250)); // gentle stagger, same idea as your other enrichment loops
            }
            if (!cancelled) load(); // one reload once all enrichment attempts finish, to pick up whatever got saved
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shipment?.id]);

    const stableSplits = useStableSplitOrder(shipmentId, shipment?.wfs_shipment_splits || []);

    if (!shipment) return <div className="max-w-3xl mx-auto py-10 px-6 flex items-center gap-2 text-gray-400"><Loader size={16} className="animate-spin" /> Loading...</div>;

    return (
        <div className="max-w-3xl mx-auto py-10 px-6">
            <button onClick={onBack} className="text-gray-400 hover:text-gray-600 mb-4 flex items-center gap-1 text-sm"><ChevronLeft size={16} /> Back</button>
            <h1 className="text-xl font-bold text-gray-800 mb-1">Labels & Shipping</h1>
            <p className="text-gray-500 text-sm mb-6">Print your WFS receiving labels, then arrange how the boxes get to Walmart.</p>

            <BulkLabelActions splits={stableSplits} onChanged={load} labelCountOverrides={labelCountOverrides} />

            {stableSplits.map((split) => (
                <SplitLabelsCard key={split.id} split={split} onDone={load}
                    labelCountOverride={labelCountOverrides[split.id]}
                    onLabelCountChange={(val) => setLabelCountOverrides((prev) => ({ ...prev, [split.id]: val }))} />
            ))}

            {stableSplits.length > 0 && (
                <TotalBoxesBar
                    total={stableSplits.reduce((sum, s) => sum + resolveSplitBoxCount(s, labelCountOverrides[s.id]), 0)}
                    shipments={stableSplits.length}
                />
            )}

            <button onClick={onFinish} className="w-full py-3 bg-gray-800 text-white rounded-xl font-bold hover:bg-gray-900 transition flex items-center justify-center gap-2">
                Done — View All Shipments <ArrowRight size={16} />
            </button>
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════
   Wizard orchestrator
   ═══════════════════════════════════════════════════════════ */
const NewShipmentWizard = ({ onDone, resumeShipmentId, onGoToAllShipments }) => {
    const [step, setStep] = useState(resumeShipmentId ? null : "source"); // null = "figuring out where to resume"
    const [sourceItems, setSourceItems] = useState(null);
    const [sourceBatchId, setSourceBatchId] = useState(null);
    const [packRows, setPackRows] = useState(null); // preserves manual edits (typed GTIN/UPC, weights, etc.) across Back/Forward within this session
    const [, setReturnAddress] = useState(null);
    const [shipmentId, setShipmentId] = useState(resumeShipmentId || null);
    // Once true, the wizard can never navigate back to source/pack/address/
    // preview/confirm/custom_split — those steps only make sense for a
    // shipment that doesn't exist on Walmart yet. Set the moment a shipment
    // is actually created (fresh flow) OR when resuming one that's already
    // status='created'.
    const [shipmentAlreadyCreated, setShipmentAlreadyCreated] = useState(false);

    // Whenever resuming an existing shipment, load ITS saved wizard_step
    // instead of always restarting at Preview — this is what makes closing
    // out of e.g. the Prep Report and reopening the shipment land you back
    // exactly there.
    useEffect(() => {
        if (!resumeShipmentId) return;
        getWfsShipment(resumeShipmentId).then((d) => {
            const shipment = d.shipment;
            setShipmentId(shipment.id);

            if (shipment?.status === 'created') {
                // Already real on Walmart's side — never route back through
                // Preview/Confirm regardless of what wizard_step says. Land
                // on the saved step if it's a valid post-creation one,
                // otherwise default straight to Prep Report.
                setShipmentAlreadyCreated(true);
                const saved = shipment.wizard_step;
                setStep(saved === 'prep' || saved === 'labels' ? saved : 'prep');
                return;
            }

            // A draft with NO return address yet can never legitimately be
            // on preview/confirm/custom_split — Walmart's preview call needs
            // a real address. This can happen because shipments are now
            // created right after item selection (before an address is ever
            // entered), so a saved wizard_step of "preview" just means "the
            // seller got that far in an earlier attempt," not "the address
            // is actually set." Send them back to Pack Details (not straight
            // to Address) so they land somewhere with full context — their
            // items, quantities, and dims all visible — rather than a bare
            // address form with no reminder of what's being shipped.
            const hasAddress = shipment?.return_address && Object.keys(shipment.return_address).length > 0
                && shipment.return_address.address_line1;
            if (!hasAddress) {
                setStep('pack');
                return;
            }

            const saved = shipment?.wizard_step;
            setStep(saved && saved !== 'confirm' && saved !== 'custom_split' ? saved : 'preview');
        }).catch(() => setStep("preview"));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resumeShipmentId]);

    // Persist the step any time it changes, for any shipment that already
    // exists (source/pack/address happen before shipmentId exists at all,
    // so there's nothing to persist yet during those).
    useEffect(() => {
        if (shipmentId && step) {
            updateWizardStep(shipmentId, step).catch(() => { /* non-fatal — worst case resume falls back to a nearby step */ });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shipmentId, step]);

    if (step === null) {
        return <div className="max-w-3xl mx-auto py-10 px-6 flex items-center gap-2 text-gray-400"><Loader size={18} className="animate-spin" /> Resuming your shipment...</div>;
    }

    if (step === "source") return (
        <SourceStep
            onGoToAllShipments={onGoToAllShipments}
            onNext={({ sourceBatchId: bId, items, shipmentId: newId }) => {
                setSourceBatchId(bId); setSourceItems(items); setPackRows(null); setShipmentId(newId); setStep("pack");
            }}
        />
    );

    if (step === "pack") return (
        <PackDetailsStep shipmentId={shipmentId} sourceItems={sourceItems} initialRows={packRows}
            onBack={(currentRows) => { setPackRows(currentRows); setStep("source"); }}
            onNext={(items, currentRows) => { setSourceItems(items); setPackRows(currentRows); setStep("address"); }} />
    );

    if (step === "address") return (
        <ReturnAddressStep onBack={() => setStep("pack")}
            onNext={async (addr) => {
                setReturnAddress(addr);
                await updateShipmentReturnAddress(shipmentId, addr);
                setStep("preview");
            }} />
    );

    if (step === "preview") return <PreviewStep shipmentId={shipmentId} onBack={() => setStep("address")}
        onNext={(previewType) => setStep(previewType === 'custom' ? "custom_split" : "confirm")} />;

    if (step === "custom_split") return <CustomSplitStep shipmentId={shipmentId} onBack={() => setStep("preview")} onCreated={() => { setShipmentAlreadyCreated(true); setStep("prep"); }} />;

    if (step === "confirm") return <ConfirmCreateStep shipmentId={shipmentId} onBack={() => setStep("preview")} onCreated={() => { setShipmentAlreadyCreated(true); setStep("prep"); }} />;

    if (step === "prep") return <PrepReportStep shipmentId={shipmentId} showBack={!shipmentAlreadyCreated} onBack={() => setStep("confirm")} onNext={() => setStep("labels")} />;

    if (step === "labels") return <LabelsStep shipmentId={shipmentId} onBack={() => setStep("prep")} onFinish={onDone} />;

    return null;
};

/* ═══════════════════════════════════════════════════════════
   All Shipments tab
   ═══════════════════════════════════════════════════════════ */
const ShipmentDetailPanel = ({ shipmentId, onClose, onChanged, onResume }) => {
    const [shipment, setShipment] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshError, setRefreshError] = useState("");
    const [errors, setErrors] = useState(null);
    const [cancelling, setCancelling] = useState(false);
    const [removingFromList, setRemovingFromList] = useState(false);
    const [innerTab, setInnerTab] = useState("receiving"); // 'receiving' | 'labels'
    // Set the moment `load()` resolves, read by the auto-refresh effect —
    // a ref (not state) so it's available synchronously right after load()
    // without waiting for a re-render.
    const shipmentIsCreated = useRef(false);

    const [duplicating, setDuplicating] = useState(false);

    const handleDuplicate = async () => {
        setDuplicating(true);
        try {
            const items = shipment.wfs_shipment_items || [];
            const draftItems = items.map((it) => ({
                catalog_item_id: it.catalog_item_id, sku: it.sku, title: it.title, image_url: it.image_url,
                product_id: it.product_id, product_id_type: it.product_id_type,
                item_desc: it.item_desc || it.title,
                item_qty: it.item_qty, vendor_pack_qty: it.vendor_pack_qty, inner_pack_qty: it.inner_pack_qty,
                weight: it.weight, dimensions: it.dimensions,
                non_sort_item: it.non_sort_item, expiration_date: it.expiration_date,
            }));
            const { shipment: newShipment } = await createShipmentDraft({ items: draftItems });
            onClose();
            onResume(newShipment.id);
        } catch (e) {
            alert(e.message || "Failed to duplicate shipment");
        } finally {
            setDuplicating(false);
        }
    };

    const load = () => getWfsShipment(shipmentId).then((d) => {
        setShipment(d.shipment);
        shipmentIsCreated.current = d.shipment?.status === "created";
        return d.shipment;
    });

    useEffect(() => {
        // Load whatever we have locally first (instant), then immediately
        // pull fresh status from Walmart in the background — so opening any
        // created shipment always shows current reality without a manual click.
        load().then(() => {
            if (shipmentIsCreated.current) refresh();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shipmentId]);

    const refresh = async () => {
        setRefreshing(true);
        setRefreshError("");
        try {
            await refreshWfsShipmentStatus(shipmentId);
            await load();
            onChanged();
        } catch (e) {
            setRefreshError(e.message || "Failed to refresh status from Walmart");
        } finally {
            setRefreshing(false);
        }
    };

    // Auto-fill missing product images/titles the moment the popup opens —
    // same live lookup used in LabelsStep, applied here too since this is
    // the actual popup used for All Shipments (imported shipments in
    // particular often have no image at all).
    useEffect(() => {
        if (!shipment) return;
        const missing = (shipment.wfs_shipment_items || []).filter((it) => !it.image_url);
        if (missing.length === 0) return;
        let cancelled = false;
        (async () => {
            for (const it of missing) {
                if (cancelled) return;
                const result = await enrichShipmentItemImage(it.id).catch(() => null);
                if (cancelled) return;
                // Update just this one item locally the moment its result
                // comes back, instead of waiting for the whole batch — the
                // thumbnail pops in immediately rather than everything
                // appearing at once at the end.
                if (result?.image_url) {
                    setShipment((prev) => {
                        if (!prev) return prev;
                        return {
                            ...prev,
                            wfs_shipment_items: (prev.wfs_shipment_items || []).map((row) =>
                                row.id === it.id ? { ...row, image_url: result.image_url, title: result.title || row.title } : row
                            ),
                            wfs_shipment_splits: (prev.wfs_shipment_splits || []).map((split) => ({
                                ...split,
                                wfs_shipment_split_items: (split.wfs_shipment_split_items || []).map((row) =>
                                    row.sku === it.sku ? { ...row, image_url: result.image_url, title: result.title || row.title } : row
                                ),
                            })),
                        };
                    });
                }
                await new Promise((r) => setTimeout(r, 250));
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shipment?.id]);

    const loadErrors = async () => {
        try { setErrors((await getWfsShipmentErrors(shipmentId)).payload || []); } catch { setErrors([]); }
    };

    const handleCancel = async () => {
        const isDraft = shipment.status !== "created";
        const msg = isDraft
            ? "Delete this draft? It was never sent to Walmart, so this is just cleaning up locally."
            : "Cancel this entire inbound shipment on WALMART'S SIDE? This actually cancels the real inbound order — Walmart will no longer expect this inventory to arrive. This can't be undone.";
        if (!window.confirm(msg)) return;
        setCancelling(true);
        try { await cancelWfsShipment(shipmentId); onChanged(); onClose(); } catch (e) { alert(e.message); } finally { setCancelling(false); }
    };

    const handleRemoveFromList = async () => {
        const msg = "Remove this shipment from your list? This will NOT cancel it on Walmart — the real inbound order keeps existing exactly as it is. To cancel the inbound order on Walmart, use the Cancel on Walmart button instead.";
        if (!window.confirm(msg)) return;
        setRemovingFromList(true);
        try { await removeShipmentFromList(shipmentId); onChanged(); onClose(); } catch (e) { alert(e.message); } finally { setRemovingFromList(false); }
    };

    const openFullPrepReport = async () => {
        await updateWizardStep(shipmentId, "prep").catch(() => { });
        onClose();
        onResume(shipmentId);
    };

    if (!shipment) return null;

    const notYetCreated = shipment.status === "draft" || shipment.status === "previewed";

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
                    <div>
                        <p className="font-bold text-gray-800">{shipment.inbound_order_id}</p>
                        <StatusBadge status={shipment.status} />
                        {refreshError && <p className="text-[10px] text-red-500 mt-1">{refreshError}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handleDuplicate} disabled={duplicating}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-xs font-bold hover:bg-blue-100 transition disabled:opacity-50">
                            <Package size={14} /> {duplicating ? "Duplicating..." : "Duplicate Shipment"}
                        </button>
                        {!notYetCreated && (
                            <button onClick={refresh} disabled={refreshing}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg text-xs font-bold hover:bg-teal-100 transition disabled:opacity-50">
                                <RefreshCcw size={14} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Refreshing..." : "Refresh Status"}
                            </button>
                        )}
                        <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600"><X size={18} /></button>
                    </div>
                </div>

                {notYetCreated ? (
                    <div className="p-6">
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                            <p className="text-sm text-blue-700 font-semibold mb-1 flex items-center gap-1.5">
                                <AlertCircle size={14} /> Not sent to Walmart yet
                            </p>
                            <p className="text-xs text-blue-600 mb-3">
                                This shipment only exists in your account so far — nothing has been created or committed on Walmart's side. Pick up where you left off, or delete it.
                            </p>
                            <div className="flex gap-2">
                                <button onClick={() => { onClose(); onResume(shipmentId); }}
                                    className="flex-1 py-2 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-lg text-xs font-bold hover:opacity-90 transition">
                                    Continue Setup <ArrowRight size={12} className="inline ml-1" />
                                </button>
                                <button onClick={handleCancel} disabled={cancelling}
                                    className="px-4 py-2 bg-white border border-red-200 text-red-500 rounded-lg text-xs font-bold hover:bg-red-50 transition disabled:opacity-50">
                                    {cancelling ? "Deleting..." : "Delete Draft"}
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center gap-1 px-6 pt-2 border-b border-gray-100 flex-shrink-0">
                            <button onClick={() => setInnerTab("receiving")}
                                className={`px-4 py-2.5 text-sm font-bold border-b-2 transition ${innerTab === "receiving" ? "border-teal-500 text-teal-700" : "border-transparent text-gray-400 hover:text-gray-600"}`}>
                                Receiving Progress
                            </button>
                            <button onClick={() => setInnerTab("labels")}
                                className={`px-4 py-2.5 text-sm font-bold border-b-2 transition ${innerTab === "labels" ? "border-teal-500 text-teal-700" : "border-transparent text-gray-400 hover:text-gray-600"}`}>
                                Labels & Carrier
                            </button>
                            <button onClick={openFullPrepReport}
                                className="ml-auto mb-2 flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white border-2 border-teal-600 rounded-xl text-sm font-bold shadow-md hover:bg-teal-700 hover:shadow-lg transition">
                                <ClipboardCheck size={16} /> Full Prep Report
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto flex-1">
                            {innerTab === "receiving" && <ReceivingProgressTab shipment={shipment} />}
                            {innerTab === "labels" && <LabelsCarrierTab shipment={shipment} onChanged={load} />}

                            <div className="mt-4 pt-4 border-t border-gray-100">
                                <button onClick={loadErrors} className="text-xs text-orange-600 font-semibold underline mb-2">
                                    {errors === null ? "Check for shipment errors" : errors.length === 0 ? "No errors found ✓" : `${errors.length} error(s) found`}
                                </button>
                                {errors && errors.length > 0 && (
                                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3 text-xs text-red-700 space-y-1">
                                        {errors.map((e, i) => <p key={i}>{e.description || e.code}</p>)}
                                    </div>
                                )}
                                <div className="flex items-center gap-4">
                                    <button onClick={handleRemoveFromList} disabled={removingFromList}
                                        className="text-xs text-gray-400 hover:text-gray-600 font-semibold flex items-center gap-1.5">
                                        <X size={12} /> {removingFromList ? "Removing..." : "Remove from List"}
                                    </button>
                                    <button onClick={handleCancel} disabled={cancelling}
                                        className="text-xs text-red-500 hover:text-red-600 font-semibold flex items-center gap-1.5">
                                        <Trash2 size={12} /> {cancelling ? "Cancelling..." : "Cancel on Walmart"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const truncateTitle = (title, max = 28) => {
    if (!title) return "";
    return title.length > max ? title.slice(0, max).trimEnd() + "…" : title;
};

const RECEIVING_STATUS_ORDER = { ARRIVED: 0, AWAITING_TRANSIT: 1, COMPLETED: 2, CLOSED: 2, CANCELLED: 3 };

const ReceivingProgressTab = ({ shipment }) => {
    const sortedSplits = [...(shipment.wfs_shipment_splits || [])].sort((a, b) => {
        const orderA = RECEIVING_STATUS_ORDER[deriveDisplayStatus(a, a.wfs_shipment_split_items || []).key] ?? 99;
        const orderB = RECEIVING_STATUS_ORDER[deriveDisplayStatus(b, b.wfs_shipment_split_items || []).key] ?? 99;
        return orderA - orderB;
    });

    return (
        <div>
            {sortedSplits.map((split) => {
                const splitItems = split.wfs_shipment_split_items || [];
                const display = deriveDisplayStatus(split, splitItems);
                const eta = split.updated_expected_delivery_date?.slice(0, 10) || split.expected_delivery_date?.slice(0, 10);
                const originalEta = split.expected_delivery_date?.slice(0, 10);
                const etaChanged = originalEta && eta && originalEta !== eta;
                return (
                    <div key={split.id} className="border border-gray-100 rounded-xl p-4 mb-3">
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <p className="font-semibold text-gray-800 text-sm">{split.ship_to_address?.fcName || split.shipment_id}</p>
                                <p className="text-xs text-gray-400">
                                    {formatShipToAddress(split.ship_to_address)}
                                    {formatShipToAddress(split.ship_to_address) && " · "}
                                    Shipment {split.shipment_id}
                                </p>
                                <div className="text-xs text-gray-400 mt-1 space-y-0.5">
                                    {split.actual_delivery_date ? (
                                        <p className="text-green-600 font-semibold">Arrived {split.actual_delivery_date.slice(0, 10)}</p>
                                    ) : (
                                        <p>
                                            ETA {eta || "—"}
                                            {etaChanged && <span className="text-orange-500"> (was {originalEta})</span>}
                                        </p>
                                    )}
                                    {split.carrier_name && (
                                        <p>Carrier: <span className="font-semibold text-gray-600">{split.carrier_name}</span></p>
                                    )}
                                    {split.tracking_no?.length > 0 && (
                                        <p className="font-mono">Tracking: {split.tracking_no.join(", ")}</p>
                                    )}
                                </div>
                            </div>
                            <StatusBadge status={display.key} label={display.label} />
                        </div>
                        <table className="w-full text-xs">
                            <thead><tr className="text-gray-400 text-left border-b border-gray-100"><th className="py-1.5 w-48">Product</th><th>SKU</th><th>Qty</th><th>Received</th><th>Damaged</th><th>Fill Rate</th></tr></thead>
                            <tbody>
                                {splitItems.map((it) => (
                                    <tr key={it.id} className="border-b border-gray-50">
                                        <td className="py-1.5">
                                            <div className="flex items-center gap-2" title={it.title || it.sku}>
                                                <ProductImage src={it.image_url} />
                                                <span className="text-gray-700">{truncateTitle(it.title) || it.sku}</span>
                                            </div>
                                        </td>
                                        <td className="font-mono text-gray-500">{it.sku}</td>
                                        <td>{it.item_qty}</td>
                                        <td>{it.received_qty ?? "—"}</td>
                                        <td className={it.damaged_qty > 0 ? "text-red-500 font-semibold" : ""}>{it.damaged_qty ?? "—"}</td>
                                        <td>{it.fill_rate != null ? `${it.fill_rate.toFixed(0)}%` : "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            })}
        </div>
    );
};

const LabelsCarrierTab = ({ shipment, onChanged }) => {
    const splits = useStableSplitOrder(shipment.id, shipment.wfs_shipment_splits || []);
    const [labelCountOverrides, setLabelCountOverrides] = useState({});

    return (
        <div>
            <BulkLabelActions splits={splits} onChanged={onChanged} labelCountOverrides={labelCountOverrides} />

            {splits.map((split) => (
                <SplitLabelsCard key={`${split.id}-${split.carrier_quote_status}-${split.tracking_no?.length || 0}`} split={split} onDone={onChanged}
                    labelCountOverride={labelCountOverrides[split.id]}
                    onLabelCountChange={(val) => setLabelCountOverrides((prev) => ({ ...prev, [split.id]: val }))} />
            ))}

            {splits.length > 0 && (
                <TotalBoxesBar
                    total={splits.reduce((sum, s) => sum + resolveSplitBoxCount(s, labelCountOverrides[s.id]), 0)}
                    shipments={splits.length}
                />
            )}
        </div>
    );
};

const SHIPMENTS_PAGE_SIZE = 20;
const SHIPMENT_SEARCH_TYPES = [
    { id: "all", label: "All" },
    { id: "title", label: "Name" },
    { id: "sku", label: "SKU" },
    { id: "product_id", label: "UPC/GTIN" },
];

const ShipmentImageStack = ({ items, max = 4 }) => {
    const withImages = items.filter((it) => it.image_url);
    const shown = withImages.length > 0 ? withImages.slice(0, max) : items.slice(0, max);
    const remaining = items.length - shown.length;
    return (
        <div className="flex items-center -space-x-2 flex-shrink-0">
            {shown.map((it, i) => (
                <div key={it.id || i} className="w-9 h-9 rounded-full border-2 border-white shadow-sm bg-gray-100 overflow-hidden flex items-center justify-center"
                    style={{ zIndex: shown.length - i }}>
                    {it.image_url ? <img src={it.image_url} alt="" className="w-full h-full object-cover" /> : <span className="text-sm">📦</span>}
                </div>
            ))}
            {remaining > 0 && (
                <div className="w-9 h-9 rounded-full border-2 border-white shadow-sm bg-gray-700 text-white flex items-center justify-center text-[10px] font-bold" style={{ zIndex: 0 }}>
                    +{remaining}
                </div>
            )}
        </div>
    );
};

const AllShipmentsTab = ({ onResume }) => {
    const [shipments, setShipments] = useState(null);
    const [total, setTotal] = useState(0);
    const [loadedPages, setLoadedPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [statusFilter, setStatusFilter] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [search, setSearch] = useState("");
    const [searchType, setSearchType] = useState("all");
    const [expandedId, setExpandedId] = useState(null);
    const sentinelRef = useRef(null);
    const liveSync = useLiveShipmentSync();

    const loadFirstPage = () => {
        setLoading(true);
        return listWfsShipments(statusFilter || undefined, 1, SHIPMENTS_PAGE_SIZE, search, searchType)
            .then((d) => { setShipments(d.shipments || []); setTotal(d.total || 0); setLoadedPages(1); })
            .finally(() => setLoading(false));
    };

    // Re-fetches every local page currently on screen, in place — picks up
    // whatever the live sync just found/refreshed without disturbing scroll
    // position or the pages already appended.
    const reloadVisible = useCallback(async () => {
        if (loadedPages === 0) return;
        const results = await Promise.all(
            Array.from({ length: loadedPages }, (_, i) =>
                listWfsShipments(statusFilter || undefined, i + 1, SHIPMENTS_PAGE_SIZE, search, searchType))
        );
        setShipments(results.flatMap((d) => d.shipments || []));
        setTotal(results[results.length - 1]?.total || 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadedPages, statusFilter, search, searchType]);

    // Land on the tab → show local rows instantly, then fetch live Walmart
    // data for the first couple of pages (covers everything currently
    // visible) and refresh in place once it lands.
    useEffect(() => {
        loadFirstPage().then(() => { liveSync.sync(2, reloadVisible); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const loadMore = useCallback(() => {
        if (loadingMore || shipments === null || shipments.length >= total) return;
        setLoadingMore(true);
        const nextPage = loadedPages + 1;
        listWfsShipments(statusFilter || undefined, nextPage, SHIPMENTS_PAGE_SIZE, search, searchType)
            .then((d) => {
                setShipments((prev) => [...(prev || []), ...(d.shipments || [])]);
                setTotal(d.total || 0);
                setLoadedPages(nextPage);
            })
            .finally(() => setLoadingMore(false));
        // Scrolling further is also the cue to fetch one more live page, so
        // older shipments get covered too, not just the first screenful.
        if (!liveSync.done) liveSync.sync(1, reloadVisible);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingMore, shipments, total, loadedPages, statusFilter, search, searchType, liveSync.done]);

    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) loadMore();
        }, { rootMargin: "200px" });
        observer.observe(el);
        return () => observer.disconnect();
    }, [loadMore]);

    const runSearch = async () => {
        await loadFirstPage();
        await liveSync.sync(2, reloadVisible);
    };

    // If a search comes back with nothing locally, the normal scroll-to-load-
    // more trick has no results list to scroll into — so give it an explicit
    // way to keep digging into Walmart's history instead of dead-ending on
    // "No shipments found" when the real shipment just hasn't synced yet.
    const searchDeeper = async () => {
        await liveSync.sync(3, reloadVisible);
    };

    return (
        <div className="max-w-5xl mx-auto py-10 px-6">
            <div className="flex items-center justify-between mb-5">
                <h1 className="text-xl font-bold text-gray-800">All Shipments</h1>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500">
                    <option value="">All statuses</option>
                    <option value="draft">Draft</option>
                    <option value="previewed">Previewed</option>
                    <option value="created">Created</option>
                    <option value="cancelled">Cancelled</option>
                </select>
            </div>

            <div className="bg-white rounded-xl shadow p-3 mb-4">
                <div className="flex gap-2 mb-2">
                    <input value={search} onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && runSearch()}
                        placeholder="Search by product name, SKU, or UPC/GTIN..."
                        className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" />
                    <button onClick={runSearch} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-bold hover:bg-teal-700 transition flex items-center gap-1.5">
                        <Search size={14} /> Search
                    </button>
                    {search && (
                        <button onClick={() => { setSearch(""); setSearchType("all"); loadFirstPage(); }}
                            className="px-3 py-2 text-gray-400 hover:text-gray-600 text-sm">
                            Clear
                        </button>
                    )}
                </div>
                <div className="flex gap-1.5">
                    {SHIPMENT_SEARCH_TYPES.map((t) => (
                        <button key={t.id} onClick={() => setSearchType(t.id)}
                            className={`px-3 py-1 text-xs font-bold rounded-full border-2 transition ${searchType === t.id ? "border-teal-500 bg-teal-50 text-teal-700" : "border-gray-200 text-gray-400 hover:border-gray-300"}`}>
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className={`space-y-2 transition-opacity ${loading ? "opacity-50 pointer-events-none" : ""}`}>
                {shipments === null && (
                    <div className="flex items-center gap-2 text-gray-400 text-sm py-10 justify-center"><Loader size={16} className="animate-spin" /> Loading...</div>
                )}
                {shipments?.length === 0 && (
                    <div className="text-center py-10">
                        <p className="text-gray-400 text-sm mb-2">No shipments found{search ? ` matching "${search}"` : ""}.</p>
                        {search && !liveSync.done && (
                            <button onClick={searchDeeper} disabled={liveSync.syncing}
                                className="text-xs font-bold text-teal-600 hover:text-teal-700 underline decoration-dotted disabled:opacity-50">
                                {liveSync.syncing ? "Checking further back…" : "Check further back in your shipment history"}
                            </button>
                        )}
                        {search && liveSync.done && (
                            <p className="text-xs text-gray-300">Checked your entire shipment history — nothing matched.</p>
                        )}
                    </div>
                )}
                {shipments?.map((s) => {
                    const items = s.wfs_shipment_items || [];
                    const totalUnits = items.reduce((sum, it) => sum + (it.item_qty || 0), 0);
                    const isExpanded = expandedId === s.id;
                    const stillUpdating = !liveSync.done && !liveSync.touchedIds.has(s.inbound_order_id);
                    return (
                        <div key={s.id} className="bg-white rounded-xl shadow overflow-hidden">
                            <div className="w-full p-4 flex items-center gap-3">
                                <ShipmentImageStack items={items} />
                                <button onClick={() => setSelectedId(s.id)} className="flex-1 min-w-0 text-left">
                                    <p className="font-mono text-sm font-semibold text-gray-800 flex items-center gap-2">
                                        {s.inbound_order_id}
                                        {s.source === "imported" && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-600">Imported</span>}
                                    </p>
                                    <p className="text-xs text-gray-400">
                                        {(s.wfs_shipment_splits || []).length} destination(s) · {items.length} SKU{items.length === 1 ? "" : "s"} · {totalUnits} units · {new Date(s.walmart_created_date || s.created_at).toLocaleDateString()}
                                    </p>
                                </button>
                                {stillUpdating && (
                                    <span className="flex items-center gap-1 text-[10px] font-bold text-gray-400 whitespace-nowrap">
                                        <Loader size={10} className="animate-spin" /> Updating…
                                    </span>
                                )}
                                <StatusBadge status={s.status} />
                                <button onClick={() => setExpandedId(isExpanded ? null : s.id)} className="text-gray-300 hover:text-gray-500 p-1">
                                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                </button>
                            </div>
                            {isExpanded && (
                                <div className="px-4 pb-4 pt-1 border-t border-gray-50">
                                    <div className="grid grid-cols-2 gap-2">
                                        {items.map((it) => (
                                            <div key={it.id} className="flex items-center gap-2 text-xs" title={it.title}>
                                                <ProductImage src={it.image_url} />
                                                <div className="min-w-0">
                                                    <p className="text-gray-700 truncate">{truncateTitle(it.title, 32)}</p>
                                                    <p className="text-gray-400 font-mono">{it.sku}{it.product_id && ` · ${it.product_id}`}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {shipments?.length > 0 && (
                <div ref={sentinelRef} className="py-6 flex items-center justify-center">
                    {loadingMore && (
                        <span className="text-xs text-gray-400 flex items-center gap-1.5">
                            <Loader size={12} className="animate-spin" /> Loading more…
                        </span>
                    )}
                    {shipments.length >= total && !loadingMore && (
                        <span className="text-xs text-gray-300">All {total} shipments loaded.</span>
                    )}
                </div>
            )}

            {selectedId && <ShipmentDetailPanel shipmentId={selectedId} onClose={() => setSelectedId(null)} onChanged={reloadVisible} onResume={onResume} />}
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════
   Main page
   ═══════════════════════════════════════════════════════════ */
const ShipmentsPage = () => {
    const [tab, setTab] = useState("new"); // 'new' | 'all'
    const [wizardKey, setWizardKey] = useState(0); // remount wizard on "start new" / "resume"
    const [resumeShipmentId, setResumeShipmentId] = useState(null);

    const handleResume = (shipmentId) => {
        setResumeShipmentId(shipmentId);
        setWizardKey((k) => k + 1);
        setTab("new");
    };

    const handleStartNew = () => {
        setResumeShipmentId(null);
        setWizardKey((k) => k + 1);
        setTab("new");
    };

    return (
        <div>
            <div className="border-b border-gray-100 bg-white px-6 flex items-center gap-1 sticky top-0 z-10">
                <button onClick={handleStartNew}
                    className={`px-4 py-3 text-sm font-bold border-b-2 transition ${tab === "new" ? "border-teal-500 text-teal-700" : "border-transparent text-gray-400 hover:text-gray-600"}`}>
                    New Shipment
                </button>
                <button onClick={() => setTab("all")}
                    className={`px-4 py-3 text-sm font-bold border-b-2 transition ${tab === "all" ? "border-teal-500 text-teal-700" : "border-transparent text-gray-400 hover:text-gray-600"}`}>
                    All Shipments
                </button>
            </div>

            {tab === "new" && <NewShipmentWizard key={wizardKey} resumeShipmentId={resumeShipmentId} onGoToAllShipments={() => setTab("all")} onDone={() => { setTab("all"); setResumeShipmentId(null); setWizardKey((k) => k + 1); }} />}
            {tab === "all" && <AllShipmentsTab onResume={handleResume} />}
        </div>
    );
};

export default ShipmentsPage;